import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CatalogPanel from '../components/admin/CatalogPanel';
import DropboxImagePicker from '../components/admin/DropboxImagePicker';
import ManualOrderPanel from '../components/admin/ManualOrderPanel';
import PresalePanel from '../components/admin/PresalePanel';
import ReviewsPanel from '../components/admin/ReviewsPanel';
import SourcesPanel from '../components/admin/SourcesPanel';
import { uploadProductImage } from '../utils/imageUpload';
import { Package, Truck, CheckCircle2, LogOut, RefreshCw, Upload, FileText, Trash2, X, ExternalLink, UploadCloud, Loader2, Image as ImageIcon, Star, ArrowUp, ArrowDown, Boxes, ShoppingCart, Mail, Send, Eye, Download, Plus, Clock, TrendingUp, Search } from 'lucide-react';

var BACKEND = process.env.REACT_APP_BACKEND_URL;

// Concurrency gate for email-status polling so a long order list (50+ rows)
// doesn't fire 50+ parallel fetches and saturate the browser's 6-connection
// HTTP/1.1 cap — which used to starve the order-edit catalog search.
function _makeGate(max) {
  var inflight = 0;
  var queue = [];
  function next() {
    if (inflight >= max || queue.length === 0) return;
    var job = queue.shift();
    inflight++;
    Promise.resolve()
      .then(job.task)
      .catch(function () {})
      .finally(function () {
        inflight--;
        job.resolve();
        next();
      });
  }
  return function gate(task) {
    return new Promise(function (resolve) {
      queue.push({ task: task, resolve: resolve });
      next();
    });
  };
}
var emailStatusGate = _makeGate(3);

// Friendly carrier names — keep in sync with the dropdown in OrderDetailModal
// and the fallback URL map in backend/routes/admin.py.
var CARRIER_LABELS = {
  'usps': 'USPS',
  'fedex': 'FedEx',
  'ups': 'UPS',
  'dhl': 'DHL',
  'correos-mexico': 'Correos de México',
  'other': 'Other',
};
function carrierLabel(code) {
  if (!code) return '';
  return CARRIER_LABELS[String(code).toLowerCase()] || String(code).toUpperCase();
}

// Strip the legacy "for ForScore/any pdf app" suffix from NoteTurner-era
// orders so the admin dashboard reads cleanly even for older orders.
// Books carry their app name (forScore / MobileSheets) as a coloured tag, so
// the title itself stays clean — matches the shop and the order emails.
var BOOK_RE = /real-book|new-real-book|real-christmas|stamp|fake-?book|omnibook|arban|cookbook|gig-book/;

function itemPlatform(it) {
  var slug = ((it && it.slug) || '').toLowerCase();
  var name = ((it && it.name) || '').toLowerCase();
  if (it && (it.platform === 'forscore' || it.platform === 'mobilesheets')) return it.platform;
  if (slug.slice(-13) === '-mobilesheets' || name.indexOf('mobilesheets') === 0) return 'mobilesheets';
  if (slug.slice(-9) === '-forscore') return 'forscore';
  if (!BOOK_RE.test(slug)) return null;
  return (slug + ' ' + name).indexOf('mobilesheet') !== -1 ? 'mobilesheets' : 'forscore';
}

function cleanItemName(name, it) {
  if (!name) return '';
  var s = String(name)
    .replace(/\s*for\s+ForScore\s*\/\s*any\s+pdf\s+app/i, '')
    .replace(/\s*\/\s*any\s+pdf\s+app/i, '')
    .replace(/\s*for\s+any\s+pdf\s+app/i, '')
    .trim();
  if (!itemPlatform(it || { name: name })) return s;
  return s
    .replace(/\([^()]*(?:forscore|for score|mobilesheets|mobile sheets)[^()]*\)/gi, '')
    .replace(/\b(?:indexed\s+)?(?:index\s+)?for\s+(?:forscore|for score|mobilesheets|mobile sheets)\b/gi, '')
    .replace(/\b(?:forscore|mobilesheets|mobile sheets)\b/gi, '')
    .replace(/\bindexed\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/^[\s\-—–·|,:]+/, '')
    .replace(/[\s\-—–·|,:]+$/, '')
    .trim() || s;
}

// Coloured item name + "(forScore)" / "(MobileSheets)" tag for the order views.
function ItemName(props) {
  var it = props.item || {};
  var platform = itemPlatform(it);
  var cls = platform === 'mobilesheets' ? 'text-emerald-400' : platform === 'forscore' ? 'text-sky-400' : '';
  return React.createElement('span', { className: cls, 'data-testid': 'order-item-name-' + (it.slug || '') },
    cleanItemName(it.name, it),
    platform ? React.createElement('span', { className: 'ml-1 text-[10px] font-bold uppercase tracking-wider' },
      '(', platform === 'mobilesheets' ? 'MobileSheets' : 'forScore', ')') : null
  );
}

function authHeaders() {
  var t = localStorage.getItem('admin_token');
  return t ? { 'Authorization': 'Bearer ' + t } : {};
}

function StatusPill(props) {
  var s = props.status;
  var color = {
    pending: 'bg-neutral-500/20 text-neutral-300 border-neutral-500/30',
    paid: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    processed: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    shipped: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    completed: 'bg-green-500/20 text-green-300 border-green-500/30',
    delivered: 'bg-green-500/20 text-green-300 border-green-500/30',  // legacy
    refunded: 'bg-red-500/20 text-red-300 border-red-500/30',
    cancelled: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
  }[s] || 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30';
  return React.createElement('span', { className: 'px-2 py-0.5 rounded-full text-xs font-semibold border ' + color }, s);
}

function OrderRow(props) {
  var o = props.order;
  var onOpen = props.onOpen;
  var onMarkDelivered = props.onMarkDelivered;
  var d = new Date(o.created_at);
  var [emailStatus, setEmailStatus] = useState(null);
  var [emailBusy, setEmailBusy] = useState(false);

  async function pollEmailStatus() {
    if (!o.followup_email_id) return;
    // Run through the module-level concurrency limiter so 50+ rows don't
    // saturate the browser's ~6 HTTP/1.1 connections and starve other admin
    // fetches (the order-edit catalog search was a victim of this).
    await emailStatusGate(async function() {
      try {
        var r = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/email-status', { headers: authHeaders() });
        if (r.ok) setEmailStatus(await r.json());
      } catch (e) { /* silent */ }
    });
  }
  useEffect(function() {
    if (o.followup_email_sent && o.followup_email_id) pollEmailStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.followup_email_sent, o.followup_email_id]);

  async function quickMarkDelivered(e) {
    e.stopPropagation();
    if (o.followup_email_sent) { pollEmailStatus(); return; }
    if (!window.confirm('Mark order ' + o.order_number + ' delivered AND send the follow-up email to ' + o.customer_email + '?')) return;
    setEmailBusy(true);
    try {
      var r = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/mark-delivered', { method: 'POST', headers: authHeaders() });
      if (r.ok && onMarkDelivered) onMarkDelivered();
    } finally { setEmailBusy(false); }
  }

  // Render the email tracking pill
  var emailPill = null;
  if (o.followup_email_sent) {
    var ev = emailStatus && emailStatus.last_event;
    var label = '📨 ' + (ev === 'opened' || ev === 'clicked' ? 'Opened' : (ev === 'delivered' ? 'Delivered' : 'Sent'));
    var color = (ev === 'opened' || ev === 'clicked') ? 'text-green-300 bg-green-500/10 border-green-500/30'
              : (ev === 'delivered') ? 'text-blue-300 bg-blue-500/10 border-blue-500/30'
              : 'text-neutral-400 bg-neutral-800 border-white/10';
    emailPill = React.createElement('span', {
      onClick: function(e) { e.stopPropagation(); pollEmailStatus(); },
      title: 'Click to refresh — last event: ' + (ev || 'sent'),
      'data-testid': 'order-email-status-' + o.order_number,
      className: 'inline-block ml-2 px-2 py-0.5 rounded-full text-xs border cursor-pointer ' + color
    }, label);
  }

  // Prefer romanized (EN) fields when present so admin can read Japanese / Cyrillic / etc.
  // shipping labels — but fall back to the original if no romanization was generated.
  var displayName = o.customer_name_en || o.customer_name;
  var addr = o.shipping_address_en || o.shipping_address || {};
  var origName = (o.customer_name_en && o.customer_name && o.customer_name !== o.customer_name_en) ? o.customer_name : null;
  var locationParts = [addr.locality, addr.region, addr.country_code_alpha2].filter(function(p) { return !!p; });
  var locationStr = locationParts.length ? locationParts.join(', ') : '—';

  return React.createElement('tr', { className: 'border-b border-white/5 hover:bg-white/5 cursor-pointer', onClick: function() { onOpen(o); } },
    React.createElement('td', { className: 'p-3 font-mono text-[#d4af37] text-sm' }, o.order_number),
    React.createElement('td', { className: 'p-3 text-white text-sm' },
      React.createElement('div', null, displayName || o.customer_email),
      origName ? React.createElement('div', { className: 'text-neutral-500 text-xs italic' }, '(', origName, ')') : null,
      displayName && o.customer_email ? React.createElement('div', { className: 'text-neutral-500 text-xs' },
        React.createElement('a', {
          href: 'mailto:' + o.customer_email + '?subject=' + encodeURIComponent('JiMendez.com — Order ' + o.order_number),
          className: 'hover:text-[#d4af37] hover:underline',
          onClick: function(e) { e.stopPropagation(); },
          title: 'Send a private email to this customer',
        }, o.customer_email)
      ) : null,
      o.customer_phone ? React.createElement('div', { className: 'text-neutral-600 text-xs' }, '📞 ', o.customer_phone) : null
    ),
    React.createElement('td', { className: 'p-3 text-neutral-300 text-sm', 'data-testid': 'order-location-' + o.order_number }, locationStr),
    React.createElement('td', { className: 'p-3 text-neutral-400 text-sm' }, d.toLocaleString()),
    React.createElement('td', { 'data-testid': 'order-items-' + o.order_number, className: 'p-3 text-white text-sm' },
      // Show the actual item names so admin sees what to ship at a glance
      o.items.map(function(it, i) {
        return React.createElement('div', { key: i, className: 'text-neutral-300 text-xs leading-tight' },
          React.createElement('span', { className: 'text-white' }, it.quantity, '× '),
          it.variant
            ? React.createElement(React.Fragment, null,
                React.createElement(ItemName, { item: it }),
                React.createElement('span', { className: 'text-[#d4af37] ml-1', title: 'Model / variant' }, '— ', it.variant.split(' / ').join(' · '))
              )
            : React.createElement(ItemName, { item: it }),
          it.is_digital ? React.createElement('span', { className: 'ml-1 text-[#d4af37]', title: 'Digital' }, '📥') : null
        );
      }),
      React.createElement('div', { className: 'mt-1' },
        o.shipping_insurance ? React.createElement('span', { className: 'mr-2 text-[#d4af37] text-xs', title: 'Shipping insurance purchased' }, '🛡 Insurance') : null,
        o.noteturner_warranty ? React.createElement('span', { className: 'text-[#d4af37] text-xs', title: 'NoteTurner 2-Year Warranty' }, '🎵 2-yr Warranty') : null
      )
    ),
    React.createElement('td', { 'data-testid': 'order-payment-' + o.order_number, className: 'p-3 text-sm' },
      // Detect which gateway captured the payment based on which id is set
      (function() {
        if (o.paypal_capture_id || o.paypal_order_id) {
          return React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#003087]/40 text-[#fff] text-xs font-semibold' }, '💳 PayPal');
        }
        if (o.stripe_intent_id) {
          return React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#635bff]/30 text-white text-xs font-semibold' }, '💳 Stripe');
        }
        if (o.stripe_session_id) {
          return React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#635bff]/30 text-white text-xs font-semibold' }, '💳 Stripe Checkout');
        }
        if (o.braintree_transaction_id) {
          return React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-neutral-700 text-white text-xs font-semibold' }, '💳 Card');
        }
        return React.createElement('span', { className: 'text-neutral-600 text-xs italic' }, 'unknown');
      })()
    ),
    React.createElement('td', { className: 'p-3 text-white text-sm font-semibold' }, '$', (o.total || 0).toFixed(2)),
    React.createElement('td', { className: 'p-3' },
      React.createElement(StatusPill, { status: o.status }),
      emailPill,
      o.tracking_number ? React.createElement('div', { className: 'mt-1.5', 'data-testid': 'order-tracking-' + o.order_number },
        o.tracking_url
          ? React.createElement('a', {
              href: o.tracking_url, target: '_blank', rel: 'noopener',
              onClick: function(e) { e.stopPropagation(); },
              className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#d4af37]/15 border border-[#d4af37]/30 text-[#d4af37] text-[11px] font-mono hover:bg-[#d4af37]/25'
            }, '📦 ', carrierLabel(o.tracking_carrier), ' ', o.tracking_number)
          : React.createElement('span', { className: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#d4af37]/15 border border-[#d4af37]/30 text-[#d4af37] text-[11px] font-mono' },
              '📦 ', (o.tracking_carrier ? (carrierLabel(o.tracking_carrier) + ' ') : ''), o.tracking_number
            )
      ) : null
    ),
    React.createElement('td', { className: 'p-3 text-right' },
      o.followup_email_sent ? React.createElement('span', { className: 'text-neutral-600 text-xs' }, '✓ sent')
        : React.createElement('button', {
          onClick: quickMarkDelivered, disabled: emailBusy,
          'data-testid': 'order-mark-delivered-' + o.order_number,
          className: 'bg-[#d4af37] hover:bg-white text-black text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50',
          title: 'Mark this order delivered and send the customer the follow-up email'
        }, emailBusy ? '…' : '📨 Deliver & email')
    )
  );
}

function OrderEditPanel(props) {
  // Inline editor inside the order detail modal. Lets admin correct customer
  // info, shipping address, line items (with Sanity product picker), and
  // money fields. After save, emits onSaved(updatedOrder, didEdit) so the
  // parent can flip on the "📧 Email updated invoice" button.
  var o = props.order;
  var onSaved = props.onSaved;
  var onCancel = props.onCancel;

  // ----- Local form state, seeded from the order -----
  var [customerName, setCustomerName] = useState(o.customer_name || '');
  var [customerEmail, setCustomerEmail] = useState(o.customer_email || '');
  var [customerPhone, setCustomerPhone] = useState(o.customer_phone || '');
  var [language, setLanguage] = useState(o.language || 'en');

  var initialAddr = (o.shipping_address_en || o.shipping_address || {});
  var [addr, setAddr] = useState({
    first_name: initialAddr.first_name || '',
    last_name: initialAddr.last_name || '',
    phone: initialAddr.phone || '',
    street_address: initialAddr.street_address || '',
    extended_address: initialAddr.extended_address || '',
    locality: initialAddr.locality || '',
    region: initialAddr.region || '',
    postal_code: initialAddr.postal_code || '',
    country_code_alpha2: (initialAddr.country_code_alpha2 || '').toUpperCase(),
  });

  var [items, setItems] = useState((o.items || []).map(function(it) { return Object.assign({}, it); }));
  var [shippingCost, setShippingCost] = useState((o.shipping_cost || 0).toFixed(2));
  var [shippingMethodName, setShippingMethodName] = useState(o.shipping_method_name || '');
  var [taxAmount, setTaxAmount] = useState((o.tax_amount || 0).toFixed(2));
  var [discountAmount, setDiscountAmount] = useState((o.discount_amount || 0).toFixed(2));
  var [discountCode, setDiscountCode] = useState(o.discount_code || '');
  var [notes, setNotes] = useState(o.notes || '');

  var [autoTotal, setAutoTotal] = useState(true);
  var [manualTotal, setManualTotal] = useState((o.total || 0).toFixed(2));

  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState('');

  // ----- Server-side product search for the picker -----
  // We hit /api/admin/catalog/search instead of the 200KB+ /api/sanity/products
  // because the admin React fetch context cannot reliably read large payloads
  // through the preview env's fetch-wrapper. This endpoint returns at most
  // 12 matches (~2-3 KB) so it streams through fine.
  var [pickerQuery, setPickerQuery] = useState('');
  var [pickerResultsState, setPickerResultsState] = useState([]);
  var [pickerLoading, setPickerLoading] = useState(false);
  var [pickerError, setPickerError] = useState(false);
  var [pickerTotal, setPickerTotal] = useState(0);

  useEffect(function() {
    var q = (pickerQuery || '').trim();
    if (q.length < 2) {
      setPickerResultsState([]); setPickerLoading(false); setPickerError(false);
      return;
    }
    setPickerLoading(true); setPickerError(false);
    var cancelled = false;
    // Debounce so we don't fire on every keystroke.
    var timer = setTimeout(function() {
      fetch(BACKEND + '/api/admin/catalog/search?q=' + encodeURIComponent(q) + '&limit=100', {
        headers: authHeaders(),
      })
        .then(function(r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
        .then(function(d) {
          if (cancelled) return;
          setPickerResultsState(Array.isArray(d.results) ? d.results : []);
          setPickerTotal(Number(d.total || (d.results || []).length));
          setPickerLoading(false);
        })
        .catch(function() {
          if (cancelled) return;
          setPickerError(true);
          setPickerLoading(false);
        });
    }, 250);
    return function() { cancelled = true; clearTimeout(timer); };
  }, [pickerQuery]);

  // ----- Derived totals -----
  var subtotal = items.reduce(function(s, it) {
    return s + Number(it.unit_price || 0) * Number(it.quantity || 0);
  }, 0);
  var insuranceCost = o.shipping_insurance ? Number(o.shipping_insurance_cost || 0) : 0;
  var warrantyCost = o.noteturner_warranty ? Number(o.noteturner_warranty_cost || 0) : 0;
  var computedTotal = subtotal + Number(shippingCost || 0) + Number(taxAmount || 0)
    + insuranceCost + warrantyCost - Number(discountAmount || 0);

  function updateItem(idx, key, val) {
    setItems(function(prev) {
      var next = prev.slice();
      next[idx] = Object.assign({}, next[idx]);
      if (key === 'quantity') next[idx][key] = Math.max(1, parseInt(val, 10) || 1);
      else if (key === 'unit_price') next[idx][key] = Math.max(0, Number(val) || 0);
      else next[idx][key] = val;
      return next;
    });
  }
  function removeItem(idx) {
    setItems(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  }
  function addProductFromCatalog(p) {
    setItems(function(prev) {
      return prev.concat([{
        slug: p.slug,
        name: p.name,
        quantity: 1,
        unit_price: Number(p.price || 0),
        variant: null,
        // Server returns `is_digital` (snake_case); legacy clients used `isDigital`
        is_digital: !!(p.is_digital || p.isDigital),
        image: p.image || (p.images && p.images[0]) || null,
        category: p.category || null,
      }]);
    });
    setPickerQuery('');
  }

  // Server-side picker results — the useEffect above debounces the query
  // against /api/admin/catalog/search and populates pickerResultsState.
  var pickerResults = pickerResultsState;

  async function handleSave() {
    setBusy(true); setErr('');
    try {
      // Block accidental zero-total when manual override is on with no value typed.
      if (!autoTotal && (manualTotal === '' || Number(manualTotal) < 0)) {
        throw new Error('Manual total must be a number ≥ 0. Re-enable auto-recompute or type a value.');
      }
      var body = {
        customer_email: customerEmail.trim() || null,
        customer_name: customerName,
        customer_phone: customerPhone,
        language: language,
        items: items.map(function(it) {
          return {
            slug: it.slug,
            name: it.name,
            quantity: Number(it.quantity) || 1,
            unit_price: Number(it.unit_price) || 0,
            variant: it.variant || null,
            is_digital: !!it.is_digital,
            image: it.image || null,
            category: it.category || null,
            weight_lb: it.weight_lb != null ? Number(it.weight_lb) : null,
          };
        }),
        shipping_address: {
          first_name: addr.first_name, last_name: addr.last_name,
          phone: addr.phone || null,
          street_address: addr.street_address,
          extended_address: addr.extended_address || null,
          locality: addr.locality, region: addr.region,
          postal_code: addr.postal_code,
          country_code_alpha2: (addr.country_code_alpha2 || '').toUpperCase().slice(0, 2),
        },
        shipping_cost: Number(shippingCost) || 0,
        shipping_method_name: shippingMethodName,
        tax_amount: Number(taxAmount) || 0,
        discount_amount: Number(discountAmount) || 0,
        discount_code: discountCode,
        notes: notes,
        total_override: autoTotal ? null : Number(manualTotal),
      };
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number, {
        method: 'PATCH',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify(body),
      });
      var data = null;
      try { data = await resp.json(); } catch (_e) { /* non-JSON body */ }
      if (!resp.ok) throw new Error((data && data.detail) || ('Failed to save (HTTP ' + resp.status + ')'));
      onSaved((data && data.order) || null);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  // ----- Render helpers -----
  function inp(value, onChange, placeholder, testid, extraClass) {
    return React.createElement('input', {
      value: value, onChange: function(e) { onChange(e.target.value); },
      placeholder: placeholder, 'data-testid': testid,
      className: 'w-full bg-neutral-900 border border-white/10 rounded-md px-2 py-1.5 text-white text-sm focus:outline-none focus:border-[#d4af37]/50 ' + (extraClass || ''),
    });
  }
  function lbl(text) {
    return React.createElement('label', { className: 'block text-[10px] text-neutral-500 uppercase tracking-wider mb-1' }, text);
  }

  return React.createElement('div', { className: 'bg-neutral-950 border border-[#d4af37]/30 rounded-xl p-4 space-y-5', 'data-testid': 'order-edit-panel' },
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('h3', { className: 'text-[#d4af37] font-bold' }, '✏️ Edit order'),
      React.createElement('button', {
        onClick: onCancel, disabled: busy,
        'data-testid': 'order-edit-cancel-btn',
        className: 'text-neutral-500 hover:text-white text-xs',
      }, 'Cancel')
    ),
    // --- Customer info ---
    React.createElement('div', null,
      React.createElement('h4', { className: 'text-white text-sm font-semibold mb-2' }, 'Customer'),
      React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-2 gap-2' },
        React.createElement('div', null, lbl('Name'), inp(customerName, setCustomerName, 'Jane Doe', 'edit-customer-name')),
        React.createElement('div', null, lbl('Email'), inp(customerEmail, setCustomerEmail, 'jane@example.com', 'edit-customer-email')),
        React.createElement('div', null, lbl('Phone'), inp(customerPhone, setCustomerPhone, '+1 555 0100', 'edit-customer-phone')),
        React.createElement('div', null, lbl('Language'),
          React.createElement('select', {
            value: language, onChange: function(e) { setLanguage(e.target.value); },
            'data-testid': 'edit-customer-language',
            className: 'w-full bg-neutral-900 border border-white/10 rounded-md px-2 py-1.5 text-white text-sm',
          },
            ['en','es','fr','de','it','pt','ja'].map(function(l) {
              return React.createElement('option', { key: l, value: l }, l);
            })
          )
        )
      )
    ),
    // --- Shipping address ---
    React.createElement('div', null,
      React.createElement('h4', { className: 'text-white text-sm font-semibold mb-2' }, 'Shipping address'),
      React.createElement('div', { className: 'grid grid-cols-2 gap-2' },
        React.createElement('div', null, lbl('First name'), inp(addr.first_name, function(v) { setAddr(Object.assign({}, addr, { first_name: v })); }, '', 'edit-addr-first-name')),
        React.createElement('div', null, lbl('Last name'), inp(addr.last_name, function(v) { setAddr(Object.assign({}, addr, { last_name: v })); }, '', 'edit-addr-last-name')),
        React.createElement('div', { className: 'col-span-2' }, lbl('Street'), inp(addr.street_address, function(v) { setAddr(Object.assign({}, addr, { street_address: v })); }, '', 'edit-addr-street')),
        React.createElement('div', { className: 'col-span-2' }, lbl('Apt / suite (optional)'), inp(addr.extended_address, function(v) { setAddr(Object.assign({}, addr, { extended_address: v })); }, '', 'edit-addr-ext')),
        React.createElement('div', null, lbl('City'), inp(addr.locality, function(v) { setAddr(Object.assign({}, addr, { locality: v })); }, '', 'edit-addr-city')),
        React.createElement('div', null, lbl('State / region'), inp(addr.region, function(v) { setAddr(Object.assign({}, addr, { region: v })); }, '', 'edit-addr-region')),
        React.createElement('div', null, lbl('Postal code'), inp(addr.postal_code, function(v) { setAddr(Object.assign({}, addr, { postal_code: v })); }, '', 'edit-addr-postal')),
        React.createElement('div', null, lbl('Country (ISO-2)'), inp(addr.country_code_alpha2, function(v) { setAddr(Object.assign({}, addr, { country_code_alpha2: v.toUpperCase().slice(0,2) })); }, 'US', 'edit-addr-country')),
        React.createElement('div', { className: 'col-span-2' }, lbl('Phone (on shipping label)'), inp(addr.phone, function(v) { setAddr(Object.assign({}, addr, { phone: v })); }, '', 'edit-addr-phone'))
      )
    ),
    // --- Line items ---
    React.createElement('div', null,
      React.createElement('h4', { className: 'text-white text-sm font-semibold mb-2 flex items-center justify-between' },
        React.createElement('span', null, 'Items (', items.length, ')'),
        React.createElement('span', { className: 'text-xs text-neutral-500 font-normal' }, 'Subtotal: $', subtotal.toFixed(2))
      ),
      items.length === 0
        ? React.createElement('p', { className: 'text-xs text-neutral-500 italic mb-2' }, 'No items — add one below.')
        : null,
      items.map(function(it, i) {
        return React.createElement('div', {
          key: i,
          className: 'bg-neutral-900 rounded-lg p-2 mb-2 grid grid-cols-12 gap-2 items-center',
          'data-testid': 'edit-item-row-' + i,
        },
          React.createElement('div', { className: 'col-span-12 sm:col-span-5' },
            React.createElement('div', { className: 'text-white text-sm font-semibold truncate' }, React.createElement(ItemName, { item: it })),
            React.createElement('div', { className: 'text-[10px] text-neutral-500 font-mono truncate' }, it.slug, it.is_digital ? ' · digital' : ''),
            React.createElement('input', {
              value: it.variant || '',
              placeholder: 'Variant (optional, e.g. "Color / Red")',
              onChange: function(e) { updateItem(i, 'variant', e.target.value || null); },
              'data-testid': 'edit-item-variant-' + i,
              className: 'mt-1 w-full bg-neutral-950 border border-white/10 rounded-md px-2 py-1 text-white text-xs',
            })
          ),
          React.createElement('div', { className: 'col-span-3 sm:col-span-2' }, lbl('Qty'),
            React.createElement('input', {
              type: 'number', min: 1, value: it.quantity,
              onChange: function(e) { updateItem(i, 'quantity', e.target.value); },
              'data-testid': 'edit-item-qty-' + i,
              className: 'w-full bg-neutral-950 border border-white/10 rounded-md px-2 py-1 text-white text-sm text-right',
            })
          ),
          React.createElement('div', { className: 'col-span-5 sm:col-span-3' }, lbl('Unit price'),
            React.createElement('input', {
              type: 'number', min: 0, step: '0.01', value: it.unit_price,
              onChange: function(e) { updateItem(i, 'unit_price', e.target.value); },
              'data-testid': 'edit-item-price-' + i,
              className: 'w-full bg-neutral-950 border border-white/10 rounded-md px-2 py-1 text-white text-sm text-right',
            })
          ),
          React.createElement('div', { className: 'col-span-4 sm:col-span-2 flex flex-col items-end justify-end' },
            React.createElement('div', { className: 'text-[#d4af37] text-sm font-bold' }, '$', (Number(it.unit_price || 0) * Number(it.quantity || 0)).toFixed(2)),
            React.createElement('button', {
              onClick: function() { removeItem(i); },
              'data-testid': 'edit-item-remove-' + i,
              className: 'text-xs text-red-400 hover:text-red-300 mt-1',
            }, 'Remove')
          )
        );
      }),
      // Picker
      React.createElement('div', { className: 'bg-neutral-900 rounded-lg p-2 border border-dashed border-white/10' },
        React.createElement('label', { className: 'block text-[10px] text-neutral-500 uppercase tracking-wider mb-1' }, '+ Add item from catalog'),
        inp(pickerQuery, setPickerQuery, 'Search by name or slug (e.g. "real book vol 4")', 'edit-item-picker-input'),
        pickerLoading
          ? React.createElement('p', { className: 'text-[10px] text-neutral-500 mt-1', 'data-testid': 'edit-item-picker-loading' }, 'Searching…')
          : null,
        pickerError
          ? React.createElement('p', { className: 'text-[10px] text-amber-400 mt-1', 'data-testid': 'edit-item-picker-error' }, 'Search failed — try again.')
          : null,
        pickerResults.length > 0
          ? React.createElement(React.Fragment, null,
              React.createElement('p', { className: 'text-[10px] text-neutral-500 mt-1', 'data-testid': 'edit-item-picker-count' },
                pickerTotal > pickerResults.length
                  ? 'Showing ' + pickerResults.length + ' of ' + pickerTotal + ' matches — add a key or volume to narrow it down.'
                  : pickerResults.length + (pickerResults.length === 1 ? ' match' : ' matches') + ' — scroll for more.'),
              React.createElement('ul', { className: 'mt-1 bg-neutral-950 rounded-md max-h-80 overflow-y-auto divide-y divide-white/5', 'data-testid': 'edit-item-picker-results' },
              pickerResults.map(function(p, idx) {
                return React.createElement('li', { key: p.slug || idx },
                  React.createElement('button', {
                    onClick: function() { addProductFromCatalog(p); },
                    'data-testid': 'edit-item-picker-add-' + (p.slug || idx),
                    className: 'w-full text-left px-2 py-1.5 hover:bg-neutral-900 flex items-center justify-between gap-2',
                  },
                    React.createElement('span', { className: 'text-white text-xs truncate' }, p.name, ' ', React.createElement('span', { className: 'text-neutral-500 font-mono' }, p.slug)),
                    React.createElement('span', { className: 'text-[#d4af37] text-xs font-bold whitespace-nowrap' }, '$', Number(p.price || 0).toFixed(2))
                  )
                );
              })
            )
          )
          : pickerQuery
            ? React.createElement('p', { className: 'text-xs text-neutral-500 mt-1' }, 'No matches.')
            : null
      )
    ),
    // --- Money fields ---
    React.createElement('div', null,
      React.createElement('h4', { className: 'text-white text-sm font-semibold mb-2' }, 'Totals'),
      React.createElement('div', { className: 'grid grid-cols-2 sm:grid-cols-3 gap-2' },
        React.createElement('div', null, lbl('Shipping cost'), inp(shippingCost, setShippingCost, '0.00', 'edit-shipping-cost')),
        React.createElement('div', { className: 'col-span-2' }, lbl('Shipping method label'), inp(shippingMethodName, setShippingMethodName, 'USPS Priority Mail', 'edit-shipping-method')),
        React.createElement('div', null, lbl('Tax'), inp(taxAmount, setTaxAmount, '0.00', 'edit-tax')),
        React.createElement('div', null, lbl('Discount $'), inp(discountAmount, setDiscountAmount, '0.00', 'edit-discount-amount')),
        React.createElement('div', null, lbl('Discount code'), inp(discountCode, setDiscountCode, '', 'edit-discount-code'))
      ),
      React.createElement('div', { className: 'mt-3 flex items-center gap-3 flex-wrap' },
        React.createElement('label', { className: 'flex items-center gap-2 text-sm text-neutral-300 cursor-pointer select-none' },
          React.createElement('input', {
            type: 'checkbox', checked: autoTotal,
            onChange: function(e) {
              var on = e.target.checked;
              setAutoTotal(on);
              if (on) setManualTotal(computedTotal.toFixed(2));
            },
            'data-testid': 'edit-total-auto-toggle',
            className: 'w-4 h-4 accent-[#d4af37]',
          }),
          React.createElement('span', null, 'Auto-recompute total')
        ),
        autoTotal
          ? React.createElement('span', { className: 'text-sm text-neutral-400' },
              'Total: ', React.createElement('span', { className: 'text-[#d4af37] font-bold', 'data-testid': 'edit-total-computed' }, '$', computedTotal.toFixed(2)))
          : React.createElement('div', { className: 'flex items-center gap-2' },
              React.createElement('span', { className: 'text-sm text-neutral-400' }, 'Override total:'),
              React.createElement('input', {
                type: 'number', min: 0, step: '0.01', value: manualTotal,
                onChange: function(e) { setManualTotal(e.target.value); },
                'data-testid': 'edit-total-manual',
                className: 'w-28 bg-neutral-900 border border-[#d4af37]/50 rounded-md px-2 py-1 text-[#d4af37] text-sm text-right font-bold',
              })
            )
      ),
      React.createElement('p', { className: 'text-[10px] text-neutral-500 mt-2' }, 'Note: For refunds, change the order status to "refunded" — don\u2019t adjust totals here.')
    ),
    // --- Notes ---
    React.createElement('div', null,
      lbl('Internal notes'),
      React.createElement('textarea', {
        value: notes, onChange: function(e) { setNotes(e.target.value); },
        rows: 2, 'data-testid': 'edit-notes',
        className: 'w-full bg-neutral-900 border border-white/10 rounded-md px-2 py-1.5 text-white text-sm',
      })
    ),
    // --- Footer ---
    err ? React.createElement('p', { className: 'text-red-400 text-sm', 'data-testid': 'order-edit-error' }, err) : null,
    React.createElement('div', { className: 'flex items-center gap-3 pt-2 border-t border-white/10' },
      React.createElement('button', {
        onClick: handleSave, disabled: busy,
        'data-testid': 'order-edit-save-btn',
        className: 'bg-[#d4af37] text-black font-semibold rounded-lg px-4 py-2 text-sm disabled:opacity-50',
      }, busy ? 'Saving…' : '💾 Save changes'),
      React.createElement('button', {
        onClick: onCancel, disabled: busy,
        className: 'text-neutral-400 hover:text-white text-sm',
      }, 'Cancel')
    )
  );
}


function OrderDetailModal(props) {
  var o = props.order;
  var onClose = props.onClose;
  var onUpdate = props.onUpdate;
  var [trackingNumber, setTrackingNumber] = useState(o.tracking_number || '');
  var [carrier, setCarrier] = useState(o.tracking_carrier || 'usps');
  var [customTrackingUrl, setCustomTrackingUrl] = useState(o.tracking_url || '');
  var [sendEmail, setSendEmail] = useState(true);
  var [busy, setBusy] = useState(false);
  var [msg, setMsg] = useState('');
  // Edit-mode toggle + "did the admin just save edits?" flag, which surfaces
  // the "📧 Email updated invoice to customer" button after the PATCH succeeds.
  var [editing, setEditing] = useState(false);
  var [editedAt, setEditedAt] = useState(o.last_edited_at || null);

  async function handleResendDownloads() {
    if (!o.customer_email) { setMsg('No customer email on file.'); return; }
    if (!window.confirm('Email fresh download links for every digital item on this order to ' + o.customer_email + '?\n\nOld links stop working.')) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/resend-downloads', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      var notReady = (data.files || []).filter(function(f) { return !f.file_ready; });
      setMsg('Fresh download links sent to ' + (data.sent_to || o.customer_email) + ' ✅ (' + (data.files || []).length + ' file(s))'
        + (notReady.length ? ' — ⚠️ no Dropbox file linked yet for: ' + notReady.map(function(f) { return f.name; }).join(', ') : ''));
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleSendUpdatedInvoice() {    if (!o.customer_email) { setMsg('No customer email on file.'); return; }
    if (!window.confirm('Send the updated invoice (latest totals + items + shipping address) to ' + o.customer_email + '?')) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/resend-confirmation', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ kind: 'customer', to_email: null }),
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Updated invoice sent to ' + (data.sent_to || o.customer_email) + ' ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleSaveTracking() {
    if (!trackingNumber) { setMsg('Tracking number required'); return; }
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/tracking-simple', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({
          tracking_number: trackingNumber,
          carrier: carrier,
          tracking_url: (customTrackingUrl || '').trim() || null,
          send_email: sendEmail,
        })
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to save tracking');
      if (data.email_sent) {
        setMsg('Tracking saved + shipping email sent to ' + (o.customer_email || 'customer') + ' ✅');
      } else if (data.email_skipped_reason === 'already_sent') {
        setMsg('Tracking updated ✅ (shipping email was already sent earlier)');
      } else if (data.email_skipped_reason === 'no_customer_email') {
        setMsg('Tracking saved ✅ (no customer email on file — skipped notification)');
      } else if (data.email_skipped_reason === 'skipped_by_admin') {
        setMsg('Tracking saved ✅ (email notification skipped)');
      } else {
        setMsg('Tracking saved ✅');
      }
      onUpdate();
    } catch (e) {
      setMsg('Error: ' + e.message);
    } finally { setBusy(false); }
  }

  async function handleSetStatus(newStatus) {
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/status', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ status: newStatus })
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Status set to ' + newStatus + ' ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleMarkDelivered() {
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/mark-delivered', {
        method: 'POST', headers: authHeaders()
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg(data.already_sent ? 'Already marked as delivered' : 'Marked delivered + follow-up email sent ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleSendReviewRequest() {
    var prev = o.review_email_sent_at ? new Date(o.review_email_sent_at).toLocaleDateString() : null;
    var confirmMsg = prev
      ? 'A review invitation was already sent on ' + prev + '. Send another one to ' + o.customer_email + '?'
      : 'Send the "leave a review" email to ' + o.customer_email + ' (in ' + (o.language || 'en').toUpperCase() + ')?';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/send-review-request', {
        method: 'POST', headers: authHeaders()
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Review invitation sent ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleSendCheckIn() {
    var prevSent = o.checkin_email_sent_at ? new Date(o.checkin_email_sent_at).toLocaleDateString() : null;
    var confirmMsg = prevSent
      ? 'A check-in email was already sent on ' + prevSent + '. Send another one to ' + o.customer_email + '?'
      : 'Send "Did your package arrive?" email to ' + o.customer_email + '?';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/send-checkin', {
        method: 'POST', headers: authHeaders()
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Check-in email sent ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleSendMobileSheets() {
    var prev = o.mobilesheets_email_sent_at ? new Date(o.mobilesheets_email_sent_at).toLocaleDateString() : null;
    var confirmMsg = prev
      ? 'MobileSheets setup email was already sent on ' + prev + '. Send again to ' + o.customer_email + '?'
      : 'Send the NoteTurner + MobileSheets setup guide email to ' + o.customer_email + '?';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/send-mobilesheets-setup', {
        method: 'POST', headers: authHeaders()
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('MobileSheets setup email sent ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleSendTrumpetCatalog() {
    var prev = o.trumpet_catalog_email_sent_at ? new Date(o.trumpet_catalog_email_sent_at).toLocaleDateString() : null;
    var confirmMsg = prev
      ? 'Trumpet catalog email was already sent on ' + prev + '. Send again to ' + o.customer_email + '?'
      : 'Send "Must-haves for the trumpet player" catalog email to ' + o.customer_email + '?';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/send-trumpet-catalog', {
        method: 'POST', headers: authHeaders()
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Trumpet catalog email sent ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleResendConfirmation(kind) {
    // kind: 'customer' = the multilingual order-received email to the buyer
    //       'admin'    = the 🎺 New Order notification (defaults to ADMIN_EMAIL)
    var k = (kind === 'admin') ? 'admin' : 'customer';
    var defaultTo = (k === 'admin') ? '' : (o.customer_email || '');
    var label = (k === 'admin') ? 'admin notification' : 'customer confirmation';
    var promptMsg = (k === 'admin')
      ? 'Resend the 🎺 New Order admin notification.\n\nLeave blank to send to your ADMIN_EMAIL, or enter an address to forward a copy:'
      : 'Resend the customer order confirmation.\n\nRecipient (leave as-is to send to the customer, or change to forward yourself a copy):';
    var to = window.prompt(promptMsg, defaultTo);
    if (to === null) return;
    to = (to || '').trim();
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number + '/resend-confirmation', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ to_email: to || null, kind: k }),
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Resent ' + label + ' to ' + (data.sent_to || to || 'ADMIN') + ' ✅');
      onUpdate();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  async function handleDeleteOrder() {
    // Double-confirm because this is a permanent, unrecoverable delete.
    if (!window.confirm('Permanently delete order ' + o.order_number + '?\n\nThis CANNOT be undone. The order, its items, and any payment record will be removed from the database.')) return;
    var typed = window.prompt('To confirm, type the order number exactly:\n\n' + o.order_number);
    if (typed !== o.order_number) {
      setMsg('Delete cancelled (order number did not match).');
      return;
    }
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/' + o.order_number, {
        method: 'DELETE', headers: authHeaders()
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed');
      setMsg('Order deleted ✅');
      // Signal the parent to refresh the orders list. The modal will close
      // implicitly because the order no longer exists on next render.
      onUpdate({ deleted: true });
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  return React.createElement('div', { className: 'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4', onClick: onClose, 'data-testid': 'order-detail-modal' },
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto', onClick: function(e) { e.stopPropagation(); } },
      React.createElement('div', { className: 'flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-neutral-900' },
        React.createElement('div', null,
          React.createElement('h2', { className: 'text-2xl font-bold text-[#d4af37] font-mono' }, o.order_number),
          React.createElement('p', { className: 'text-neutral-500 text-sm' },
            o.customer_email
              ? React.createElement('a', {
                  href: 'mailto:' + o.customer_email + '?subject=' + encodeURIComponent('JiMendez.com — Order ' + o.order_number),
                  'data-testid': 'order-email-link',
                  className: 'text-[#d4af37] hover:underline',
                  onClick: function(e) { e.stopPropagation(); },
                  title: 'Send a private email to this customer',
                }, o.customer_email)
              : React.createElement('span', { className: 'text-neutral-600 italic' }, 'no email on file'),
            ' • ',
            new Date(o.created_at).toLocaleString()
          )
        ),
        React.createElement('button', { onClick: onClose, className: 'text-neutral-500 hover:text-white' }, React.createElement(X, { className: 'w-6 h-6' }))
      ),
      React.createElement('div', { className: 'p-6 space-y-6' },
        React.createElement('div', { className: 'flex flex-wrap items-center gap-3' },
          React.createElement(StatusPill, { status: o.status }),
          // Quick status changer — flips between pending/processed/paid/shipped (does NOT send email).
          // The "Deliver & email" button below is what marks completed AND emails the customer.
          React.createElement('select', {
            'data-testid': 'admin-order-status-select',
            value: o.status || '',
            disabled: busy,
            onChange: function(e) { handleSetStatus(e.target.value); },
            className: 'bg-neutral-950 border border-white/10 text-white text-xs rounded-md px-2 py-1 focus:outline-none focus:border-[#d4af37]/50'
          },
            React.createElement('option', { value: 'pending' }, 'pending'),
            React.createElement('option', { value: 'processed' }, 'processed'),
            React.createElement('option', { value: 'paid' }, 'paid'),
            React.createElement('option', { value: 'shipped' }, 'shipped'),
            React.createElement('option', { value: 'completed' }, 'completed'),
            React.createElement('option', { value: 'refunded' }, 'refunded'),
            React.createElement('option', { value: 'cancelled' }, 'cancelled')
          ),
          // Payment method + gateway transaction reference for refunds / disputes
          (function() {
            var gateway = null, ref = null;
            if (o.paypal_capture_id || o.paypal_order_id) { gateway = 'PayPal'; ref = o.paypal_capture_id || o.paypal_order_id; }
            else if (o.stripe_intent_id) { gateway = 'Stripe (Card / Apple Pay / Google Pay)'; ref = o.stripe_intent_id; }
            else if (o.stripe_session_id) { gateway = 'Stripe Checkout'; ref = o.stripe_session_id; }
            else if (o.braintree_transaction_id) { gateway = 'Braintree'; ref = o.braintree_transaction_id; }
            if (!gateway) return null;
            return React.createElement('span', { 'data-testid': 'admin-order-payment-detail', className: 'inline-flex items-center gap-2 text-sm bg-neutral-950 border border-white/10 rounded-lg px-3 py-1.5' },
              React.createElement('span', { className: 'text-[#d4af37] font-semibold' }, '💳 ', gateway),
              ref ? React.createElement('span', { className: 'text-neutral-500 font-mono text-xs' }, ref) : null
            );
          })(),
          React.createElement('span', { className: 'text-neutral-500 text-sm' }, 'Language: ', o.language)
        ),
        // --- Edit toggle + post-save "send updated invoice" CTA ---
        React.createElement('div', { className: 'flex items-center gap-3 flex-wrap', 'data-testid': 'order-edit-controls' },
          editing
            ? null
            : React.createElement('button', {
                onClick: function() { setEditing(true); },
                'data-testid': 'order-edit-toggle-btn',
                className: 'flex items-center gap-1.5 bg-neutral-800 hover:bg-neutral-700 border border-[#d4af37]/40 text-[#d4af37] font-semibold px-3 py-1.5 rounded-lg text-sm',
                title: 'Correct customer info, address, items, or totals',
              }, '✏️ Edit order'),
          // After the admin saves edits, this CTA appears so they can push the
          // updated invoice/confirmation email to the customer in one click.
          editedAt && !editing
            ? React.createElement('button', {
                onClick: handleSendUpdatedInvoice, disabled: busy || !o.customer_email,
                'data-testid': 'send-updated-invoice-btn',
                className: 'flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50 text-sm',
                title: o.customer_email ? 'Email the updated invoice to ' + o.customer_email : 'No customer email on file',
              }, '📧 Email updated invoice to customer')
            : null,
          editedAt && !editing
            ? React.createElement('span', { className: 'text-xs text-neutral-500' }, 'edited: ' + new Date(editedAt).toLocaleString())
            : null
        ),
        editing
          ? React.createElement(OrderEditPanel, {
              order: o,
              onCancel: function() { setEditing(false); },
              onSaved: function(updated) {
                setEditing(false);
                setEditedAt((updated && updated.last_edited_at) || new Date().toISOString());
                setMsg('Order updated ✅');
                onUpdate();
              },
            })
          : null,
        // Items
        React.createElement('div', null,
          React.createElement('h3', { className: 'text-white font-semibold mb-2' }, 'Items'),
          React.createElement('ul', { className: 'space-y-2 text-sm' },
            o.items.map(function(it, i) {
              return React.createElement('li', { key: i, className: 'flex justify-between text-neutral-300 bg-neutral-950 rounded-lg p-3' },
                React.createElement('span', null, React.createElement(ItemName, { item: it }), it.variant ? ' — ' + it.variant.split(' / ').join(' · ') : '', ' × ', it.quantity, it.is_digital ? ' 📥' : ''),
                React.createElement('span', { className: 'text-white' }, '$', (it.unit_price * it.quantity).toFixed(2))
              );
            })
          ),
          // Order breakdown
          React.createElement('div', { className: 'mt-3 space-y-1 text-sm' },
            React.createElement('div', { className: 'flex justify-between text-neutral-400' },
              React.createElement('span', null, 'Subtotal'),
              React.createElement('span', null, '$', (o.subtotal || 0).toFixed(2))
            ),
            (o.shipping_cost || 0) > 0 ? React.createElement('div', { className: 'flex justify-between text-neutral-400' },
              React.createElement('span', null, 'Shipping', o.shipping_method_name ? ' (' + o.shipping_method_name.split(' — ')[0] + ')' : ''),
              React.createElement('span', null, '$', (o.shipping_cost || 0).toFixed(2))
            ) : null,
            (o.tax_amount || 0) > 0 ? React.createElement('div', { className: 'flex justify-between text-neutral-400' },
              React.createElement('span', null, 'Tax'),
              React.createElement('span', null, '$', (o.tax_amount || 0).toFixed(2))
            ) : null,
            o.shipping_insurance ? React.createElement('div', { className: 'flex justify-between text-[#d4af37]', 'data-testid': 'admin-order-insurance' },
              React.createElement('span', null, '🛡 Shipping insurance — replacement covered'),
              React.createElement('span', null, '$', (o.shipping_insurance_cost || 4).toFixed(2))
            ) : null,
            o.noteturner_warranty ? React.createElement('div', { className: 'flex justify-between text-[#d4af37]', 'data-testid': 'admin-order-warranty' },
              React.createElement('span', null, '🎵 NoteTurner 2-Year Warranty — 1 replacement per year (customer pays shipping)'),
              React.createElement('span', null, '$', (o.noteturner_warranty_cost || 15).toFixed(2))
            ) : null,
            // Discount line — was previously hidden which made customers'
            // discounted totals look like undercharges in the admin view.
            (o.discount_amount || 0) > 0 ? React.createElement('div', { className: 'flex justify-between text-green-400', 'data-testid': 'admin-order-discount' },
              React.createElement('span', null, '🏷 Discount', o.discount_code ? ' (' + o.discount_code + ')' : ''),
              React.createElement('span', null, '−$', (o.discount_amount || 0).toFixed(2))
            ) : null
          ),
          React.createElement('div', { className: 'flex justify-between mt-2 pt-2 border-t border-white/10 text-white font-bold' },
            React.createElement('span', null, 'Total'),
            React.createElement('span', { className: 'text-[#d4af37]' }, '$', (o.total || 0).toFixed(2))
          )
        ),
        // Shipping
        o.shipping_address ? React.createElement('div', null,
          React.createElement('h3', { className: 'text-white font-semibold mb-2 flex items-center gap-2' }, React.createElement(Truck, { className: 'w-4 h-4' }), 'Shipping to'),
          React.createElement('p', { 'data-testid': 'admin-shipping-address-en', className: 'text-neutral-300 text-sm whitespace-pre-line bg-neutral-950 rounded-lg p-3' },
            ((o.shipping_address_en && o.shipping_address_en.first_name) || o.shipping_address.first_name) + ' ' + ((o.shipping_address_en && o.shipping_address_en.last_name) || o.shipping_address.last_name) + '\n' +
            ((o.shipping_address_en && o.shipping_address_en.street_address) || o.shipping_address.street_address) +
              (((o.shipping_address_en && o.shipping_address_en.extended_address) || o.shipping_address.extended_address) ? ' ' + ((o.shipping_address_en && o.shipping_address_en.extended_address) || o.shipping_address.extended_address) : '') + '\n' +
            ((o.shipping_address_en && o.shipping_address_en.locality) || o.shipping_address.locality) + ', ' +
              ((o.shipping_address_en && o.shipping_address_en.region) || o.shipping_address.region) + ' ' + o.shipping_address.postal_code + '\n' +
            o.shipping_address.country_code_alpha2 +
            (o.shipping_address.phone ? '\n📞 ' + o.shipping_address.phone : '')
          ),
          // If the address was romanized, also display the original below for reference
          o.shipping_address_en ? React.createElement('details', { className: 'mt-2', 'data-testid': 'admin-shipping-address-original-wrap' },
            React.createElement('summary', { className: 'text-neutral-500 text-xs cursor-pointer hover:text-[#d4af37]' }, 'Show original (as entered by customer)'),
            React.createElement('p', { 'data-testid': 'admin-shipping-address-original', className: 'text-neutral-500 text-xs whitespace-pre-line bg-neutral-950 rounded-lg p-3 mt-1' },
              o.shipping_address.first_name + ' ' + o.shipping_address.last_name + '\n' +
              o.shipping_address.street_address + (o.shipping_address.extended_address ? ' ' + o.shipping_address.extended_address : '') + '\n' +
              o.shipping_address.locality + ', ' + o.shipping_address.region + ' ' + o.shipping_address.postal_code + '\n' +
              o.shipping_address.country_code_alpha2
            )
          ) : null,
          // Copy-to-clipboard button for USPS Click-N-Ship etc.
          React.createElement('button', {
            'data-testid': 'copy-shipping-label-btn',
            onClick: function() {
              var a = o.shipping_address_en || o.shipping_address;
              var fmt = [
                ((a.first_name || '') + ' ' + (a.last_name || '')).trim(),
                (a.street_address || '') + ((a.extended_address) ? ' ' + a.extended_address : ''),
                ((a.locality || '') + ', ' + (a.region || '') + ' ' + (a.postal_code || '')).trim(),
                a.country_code_alpha2 || '',
                a.phone ? 'Phone: ' + a.phone : null,
              ].filter(function(l) { return l && l.trim() !== ','; }).join('\n');
              try {
                navigator.clipboard.writeText(fmt);
                setMsg('Shipping label copied to clipboard 📋');
              } catch (e) {
                setMsg('Could not copy: ' + e.message);
              }
            },
            className: 'mt-2 inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 border border-white/10 text-[#d4af37] text-xs font-semibold px-3 py-2 rounded-lg'
          }, '📋 Copy shipping label')
        ) : null,
        // Tracking entry — hidden for purely digital orders that don't need shipping
        o.shipping_address || o.has_physical ? React.createElement('div', { className: 'bg-neutral-950 rounded-xl p-4 border border-white/10', 'data-testid': 'admin-tracking-block' },
          React.createElement('h3', { className: 'text-white font-semibold mb-3' }, 'Tracking number'),
          o.tracking_number ? React.createElement('p', { className: 'text-sm text-neutral-400 mb-3' },
            React.createElement('span', { className: 'text-neutral-500' }, 'Current: '),
            o.tracking_url
              ? React.createElement('a', { href: o.tracking_url, target: '_blank', rel: 'noopener', className: 'text-[#d4af37] hover:underline inline-flex items-center gap-1' }, carrierLabel(o.tracking_carrier) + ' ' + o.tracking_number, React.createElement(ExternalLink, { className: 'w-3 h-3' }))
              : React.createElement('span', { className: 'text-[#d4af37] font-mono' }, (o.tracking_carrier ? (carrierLabel(o.tracking_carrier) + ' ') : '') + o.tracking_number)
          ) : null,
          React.createElement('div', { className: 'grid grid-cols-1 sm:grid-cols-3 gap-2' },
            React.createElement('select', { value: carrier, onChange: function(e) { setCarrier(e.target.value); }, 'data-testid': 'tracking-carrier', className: 'bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm' },
              [
                { value: 'usps', label: 'USPS' },
                { value: 'fedex', label: 'FedEx' },
                { value: 'ups', label: 'UPS' },
                { value: 'dhl', label: 'DHL' },
                { value: 'correos-mexico', label: 'Correos de México' },
                { value: 'other', label: 'Other' },
              ].map(function(c) { return React.createElement('option', { key: c.value, value: c.value }, c.label); })
            ),
            React.createElement('input', { value: trackingNumber, onChange: function(e) { setTrackingNumber(e.target.value); }, placeholder: 'Tracking number', 'data-testid': 'tracking-number-input', className: 'bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono' }),
            React.createElement('button', { onClick: handleSaveTracking, disabled: busy, 'data-testid': 'tracking-save-btn', className: 'bg-[#d4af37] text-black font-semibold rounded-lg px-3 py-2 text-sm disabled:opacity-50' }, busy ? '…' : 'Save tracking')
          ),
          // Optional custom tracking link — overrides the auto-generated carrier
          // URL. Useful for Correos de México (whose tracking page changes
          // formats) or any carrier with a one-off shipment link.
          React.createElement('div', { className: 'mt-3' },
            React.createElement('label', { className: 'block text-xs text-neutral-500 mb-1 font-medium uppercase tracking-wider' }, 'Custom tracking link ', React.createElement('span', { className: 'text-neutral-600 normal-case font-normal' }, '(optional — overrides carrier default)')),
            React.createElement('input', {
              type: 'url',
              value: customTrackingUrl,
              onChange: function(e) { setCustomTrackingUrl(e.target.value); },
              placeholder: 'https://www.correosdemexico.gob.mx/...',
              'data-testid': 'tracking-url-input',
              className: 'w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono',
            })
          ),
          React.createElement('label', { className: 'flex items-center gap-2 mt-3 text-sm text-neutral-400 cursor-pointer select-none', 'data-testid': 'tracking-send-email-toggle' },
            React.createElement('input', { type: 'checkbox', checked: sendEmail, onChange: function(e) { setSendEmail(e.target.checked); }, className: 'w-4 h-4 accent-[#d4af37]' }),
            React.createElement('span', null,
              'Email customer the shipping notification ',
              o.tracking_email_sent ? React.createElement('span', { className: 'text-emerald-400 text-xs ml-1' }, '(already sent earlier)') : null
            )
          )
        ) : null,
        // Mark completed
        React.createElement('div', { className: 'flex items-center gap-3 flex-wrap' },
          React.createElement('button', { onClick: handleMarkDelivered, disabled: busy || o.followup_email_sent, 'data-testid': 'mark-delivered-btn', className: 'flex items-center gap-2 bg-green-600 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50' },
            React.createElement(CheckCircle2, { className: 'w-4 h-4' }),
            o.followup_email_sent ? 'Follow-up sent ✓' : 'Mark completed & email customer'
          ),
          // Check-in "Did your package arrive?" email — manual send by admin.
          // After send, the button flips to a disabled "Check-in sent ✓"
          // (matching the follow-up button's pattern). A small "Re-send" link
          // appears underneath for the rare case the customer asks for it again.
          o.checkin_email_sent
            ? React.createElement('button', {
                disabled: true,
                'data-testid': 'checkin-sent-badge',
                className: 'flex items-center gap-2 bg-neutral-800 text-neutral-400 font-semibold px-4 py-2 rounded-lg cursor-not-allowed'
              }, '✓ Check-in sent')
            : React.createElement('button', {
                onClick: handleSendCheckIn, disabled: busy,
                'data-testid': 'send-checkin-btn',
                className: 'flex items-center gap-2 bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50',
                title: 'Send a manual "Did your package arrive?" email + thank-you discount code'
              }, '📦 ', 'Send check-in email'),
          o.checkin_email_sent_at ? React.createElement('span', { className: 'text-xs text-neutral-500' }, 'sent: ' + new Date(o.checkin_email_sent_at).toLocaleDateString()) : null,
          o.checkin_email_sent ? React.createElement('button', {
            onClick: handleSendCheckIn, disabled: busy,
            'data-testid': 'checkin-resend-link',
            className: 'text-xs text-neutral-500 hover:text-[#d4af37] underline ml-1'
          }, 'Re-send') : null,
          // Review invitation — links every purchased product straight to its
          // review form (/product/{slug}#reviews) in the customer's language.
          React.createElement('button', {
            onClick: handleSendReviewRequest, disabled: busy,
            'data-testid': 'send-review-request-btn',
            className: 'flex items-center gap-1.5 ' + (o.review_email_sent ? 'bg-neutral-800 text-neutral-400' : 'bg-[#d4af37] text-black') + ' font-semibold px-3 py-2 rounded-lg disabled:opacity-50 text-sm',
            title: 'Send the "leave a review" email for every product in this order'
          }, o.review_email_sent ? '✓ Review invite sent' : '⭐ Ask for review'),
          o.review_email_sent_at ? React.createElement('span', { className: 'text-xs text-neutral-500' }, 'sent: ' + new Date(o.review_email_sent_at).toLocaleDateString()) : null,
          // Resend the 🎺 New Order admin notification (the email Isaías
          // gets for every live sale). Useful for wix-imported orders that
          // never triggered the live alert, or to forward a record copy.
          React.createElement('button', {
            onClick: function() { handleResendConfirmation('admin'); }, disabled: busy,
            'data-testid': 'resend-admin-notification-btn',
            className: 'flex items-center gap-1.5 bg-amber-700 hover:bg-amber-600 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50 text-sm',
            title: 'Re-send the 🎺 New Order admin notification (default: ADMIN_EMAIL)'
          }, '🎺 Resend admin notification'),
          // Fresh digital download links — mints new 7-day / 5-download
          // tokens for every digital item and emails them to the customer.
          React.createElement('button', {
            onClick: handleResendDownloads, disabled: busy,
            'data-testid': 'resend-downloads-btn',
            className: 'flex items-center gap-1.5 bg-indigo-700 hover:bg-indigo-600 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50 text-sm',
            title: 'Email fresh download links (7 days / 5 downloads) for every digital item on this order'
          }, '⬇️ Resend download links'),
          // Resend the customer-facing order confirmation. Honors the order's
          // stored language so wix-imported orders go out in the right one.
          React.createElement('button', {
            onClick: function() { handleResendConfirmation('customer'); }, disabled: busy,
            'data-testid': 'resend-confirmation-btn',
            className: 'flex items-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50 text-sm',
            title: 'Re-send the customer-facing order confirmation email'
          }, '✉️ Resend customer email'),
          o.last_confirmation_resent_at ? React.createElement('span', { className: 'text-xs text-neutral-500' }, 'last resent: ' + new Date(o.last_confirmation_resent_at).toLocaleDateString() + ' (' + (o.last_confirmation_resent_kind || 'customer') + ')') : null,
          // MobileSheets setup email — manual button, only for NoteTurner buyers
          // (admin asks customer first). Flips to "sent ✓" after one click.
          o.mobilesheets_email_sent
            ? React.createElement('button', {
                disabled: true,
                'data-testid': 'mobilesheets-sent-badge',
                className: 'flex items-center gap-2 bg-neutral-800 text-neutral-400 font-semibold px-3 py-2 rounded-lg cursor-not-allowed text-sm'
              }, '✓ MobileSheets setup sent')
            : React.createElement('button', {
                onClick: handleSendMobileSheets, disabled: busy,
                'data-testid': 'send-mobilesheets-btn',
                className: 'flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50 text-sm',
                title: 'Send the NoteTurner + MobileSheets setup video to this customer'
              }, '🎺 Send MobileSheets setup'),
          // Trumpet catalog email — "Must-haves for the trumpet player".
          // Curated 2-column product grid with photo, name, 1-line description,
          // price, and CTA. Same flip-to-sent UX as the other manual emails.
          o.trumpet_catalog_email_sent
            ? React.createElement('button', {
                disabled: true,
                'data-testid': 'trumpet-catalog-sent-badge',
                className: 'flex items-center gap-2 bg-neutral-800 text-neutral-400 font-semibold px-3 py-2 rounded-lg cursor-not-allowed text-sm'
              }, '✓ Trumpet catalog sent')
            : React.createElement('button', {
                onClick: handleSendTrumpetCatalog, disabled: busy,
                'data-testid': 'send-trumpet-catalog-btn',
                className: 'flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white font-semibold px-3 py-2 rounded-lg disabled:opacity-50 text-sm',
                title: 'Send the "Must-haves for the trumpet player" catalog email to this customer'
              }, '🎺 Send trumpet catalog'),
          o.trumpet_catalog_email_sent_at ? React.createElement('span', { className: 'text-xs text-neutral-500' }, 'sent: ' + new Date(o.trumpet_catalog_email_sent_at).toLocaleDateString()) : null,
          // Spacer pushes Delete to the far right so it's hard to mis-click.
          React.createElement('div', { className: 'flex-1' }),
          React.createElement('button', {
            onClick: handleDeleteOrder, disabled: busy,
            'data-testid': 'delete-order-btn',
            title: 'Permanently delete this order',
            className: 'flex items-center gap-1.5 text-red-400 hover:text-white hover:bg-red-600 border border-red-500/40 px-3 py-2 rounded-lg text-sm font-medium transition disabled:opacity-50'
          },
            React.createElement(Trash2, { className: 'w-4 h-4' }),
            'Delete order'
          )
        ),
        msg ? React.createElement('div', { className: 'text-sm text-neutral-400' }, msg) : null
      )
    )
  );
}

function FilesPanel() {
  var [files, setFiles] = useState({});
  var [productsBySlug, setProductsBySlug] = useState({});
  var [storage, setStorage] = useState(null);
  var [digitalProducts, setDigitalProducts] = useState([]);
  var [selectedSlug, setSelectedSlug] = useState('');
  var [pendingFile, setPendingFile] = useState(null);
  var [uploading, setUploading] = useState(false);
  var [dragOver, setDragOver] = useState(false);
  var [msg, setMsg] = useState(null);  // { type: 'success'|'error', text }
  var fileInputRef = useRef(null);

  async function loadFiles() {
    var resp = await fetch(BACKEND + '/api/admin/digital-files', { headers: authHeaders() });
    var data = await resp.json();
    setFiles(data.files || {});
    setProductsBySlug(data.products || {});
    setStorage(data.storage || null);
  }

  async function loadDigitalProducts() {
    try {
      var resp = await fetch(BACKEND + '/api/sanity/products');
      var data = await resp.json();
      var all = data.products || [];
      var digital = all
        .filter(function(p) { return p.category === 'sheet-music'; })
        .map(function(p) { return { slug: p.slug, name: p.name }; })
        .sort(function(a, b) { return a.name.localeCompare(b.name); });
      setDigitalProducts(digital);
    } catch (e) { /* non-fatal */ }
  }

  useEffect(function() { loadFiles(); loadDigitalProducts(); }, []);

  function formatBytes(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1024 / 1024).toFixed(2) + ' MB';
  }

  function pickFile(f) {
    if (!f) return;
    setPendingFile(f);
    setMsg(null);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    var f = e.dataTransfer.files && e.dataTransfer.files[0];
    pickFile(f);
  }

  async function handleUpload() {
    if (!selectedSlug) { setMsg({ type: 'error', text: 'Select a product first' }); return; }
    if (!pendingFile) { setMsg({ type: 'error', text: 'Choose a file to upload' }); return; }
    setUploading(true); setMsg(null);
    var fd = new FormData();
    fd.append('file', pendingFile);
    try {
      var resp = await fetch(BACKEND + '/api/admin/digital-files/' + encodeURIComponent(selectedSlug), {
        method: 'POST', headers: authHeaders(), body: fd
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Upload failed');
      setMsg({ type: 'success', text: 'Uploaded ' + data.filename + ' (' + formatBytes(data.size_bytes) + ')' });
      setPendingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadFiles();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally { setUploading(false); }
  }

  async function handleDelete(slug) {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete ALL files for "' + slug + '"? This cannot be undone.')) return;
    await fetch(BACKEND + '/api/admin/digital-files/' + encodeURIComponent(slug), { method: 'DELETE', headers: authHeaders() });
    loadFiles();
  }

  var existingSlugs = Object.keys(files).sort();

  // Build picker options: all digital products from Sanity + any orphan slugs already uploaded
  var sanitySlugs = digitalProducts.map(function(p) { return p.slug; });
  var orphanSlugs = existingSlugs.filter(function(s) { return sanitySlugs.indexOf(s) === -1; });

  return React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6', 'data-testid': 'files-panel' },
    React.createElement('h2', { className: 'text-xl font-bold text-white mb-2 flex items-center gap-2' },
      React.createElement(FileText, { className: 'w-5 h-5 text-[#d4af37]' }),
      'Digital Files'
    ),
    React.createElement('p', { className: 'text-neutral-500 text-sm mb-5' }, 'Upload PDF or ZIP files that customers will download after purchase. Pick the matching product, then drop your file below.'),

    // ----- Storage usage bar -----
    storage ? (function() {
      var usedMB = (storage.used_bytes / 1024 / 1024).toFixed(1);
      var totalGB = storage.disk_total_bytes ? (storage.disk_total_bytes / 1024 / 1024 / 1024).toFixed(1) : null;
      var freeGB = storage.disk_free_bytes ? (storage.disk_free_bytes / 1024 / 1024 / 1024).toFixed(1) : null;
      // Show usage as % of total disk (uploaded files are tiny vs disk size,
      // so this bar gives you visual confidence you've got tons of room).
      var pct = storage.disk_total_bytes ? Math.min(100, (storage.used_bytes / storage.disk_total_bytes) * 100) : 0;
      return React.createElement('div', { className: 'mb-5 bg-neutral-950 border border-white/5 rounded-lg p-3', 'data-testid': 'storage-bar' },
        React.createElement('div', { className: 'flex items-center justify-between text-xs mb-1' },
          React.createElement('span', { className: 'text-neutral-400' },
            React.createElement('span', { className: 'text-[#d4af37] font-semibold' }, usedMB + ' MB'),
            ' used by digital files'
          ),
          totalGB ? React.createElement('span', { className: 'text-neutral-500' },
            freeGB, ' GB free of ', totalGB, ' GB'
          ) : null
        ),
        React.createElement('div', { className: 'w-full h-1.5 bg-neutral-800 rounded-full overflow-hidden' },
          React.createElement('div', { className: 'h-full bg-[#d4af37] transition-all', style: { width: Math.max(pct, 0.5) + '%' } })
        )
      );
    })() : null,

    // ----- Step 1: Product picker -----
    React.createElement('div', { className: 'mb-4' },
      React.createElement('label', { className: 'block text-neutral-400 text-xs font-semibold uppercase tracking-wide mb-2' }, '1. Product'),
      React.createElement('select', {
        value: selectedSlug,
        onChange: function(e) { setSelectedSlug(e.target.value); setMsg(null); },
        'data-testid': 'file-upload-product-select',
        className: 'w-full bg-neutral-950 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#d4af37] focus:outline-none'
      },
        React.createElement('option', { value: '' }, '— Select a digital product —'),
        digitalProducts.length > 0 ? React.createElement('optgroup', { label: 'Digital products (Sanity)' },
          digitalProducts.map(function(p) {
            return React.createElement('option', { key: p.slug, value: p.slug }, p.name + ' (' + p.slug + ')');
          })
        ) : null,
        orphanSlugs.length > 0 ? React.createElement('optgroup', { label: 'Other (already uploaded)' },
          orphanSlugs.map(function(s) {
            return React.createElement('option', { key: s, value: s }, s);
          })
        ) : null
      )
    ),

    // Selected-product preview card — makes it crystal clear which product
    // the file you're about to upload will attach to.
    selectedSlug ? (function() {
      var picked = digitalProducts.find(function(p) { return p.slug === selectedSlug; });
      var thumb = picked && picked.image;
      var displayName = (picked && picked.name) || selectedSlug;
      return React.createElement('div', { className: 'mb-4 bg-[#d4af37]/5 border border-[#d4af37]/30 rounded-xl p-3 flex items-center gap-3', 'data-testid': 'file-upload-selected-product' },
        thumb ? React.createElement('img', { src: thumb, alt: displayName, className: 'w-14 h-14 rounded-lg object-cover bg-neutral-900' })
              : React.createElement('div', { className: 'w-14 h-14 rounded-lg bg-neutral-900 flex items-center justify-center' }, React.createElement(FileText, { className: 'w-6 h-6 text-neutral-600' })),
        React.createElement('div', { className: 'flex-1 min-w-0' },
          React.createElement('div', { className: 'text-[10px] text-[#d4af37] uppercase tracking-wider font-semibold mb-0.5' }, 'Attaching file to:'),
          React.createElement('div', { className: 'text-white font-semibold text-sm truncate' }, displayName),
          React.createElement('div', { className: 'text-neutral-500 text-xs font-mono truncate' }, selectedSlug)
        )
      );
    })() : null,

    // ----- Step 2: Drag-and-drop zone -----
    React.createElement('div', { className: 'mb-4' },
      React.createElement('label', { className: 'block text-neutral-400 text-xs font-semibold uppercase tracking-wide mb-2' }, '2. File (PDF or ZIP)'),
      React.createElement('div', {
        onDragOver: function(e) { e.preventDefault(); setDragOver(true); },
        onDragLeave: function() { setDragOver(false); },
        onDrop: handleDrop,
        onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); },
        'data-testid': 'file-upload-dropzone',
        className: 'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ' + (dragOver ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-white/15 hover:border-white/30 bg-neutral-950')
      },
        React.createElement('input', {
          ref: fileInputRef,
          type: 'file',
          accept: '.pdf,.zip',
          className: 'hidden',
          'data-testid': 'file-upload-input',
          onChange: function(e) { pickFile(e.target.files[0]); }
        }),
        pendingFile ? React.createElement('div', null,
          React.createElement(FileText, { className: 'w-10 h-10 text-[#d4af37] mx-auto mb-2' }),
          React.createElement('p', { className: 'text-white font-medium text-sm break-all' }, pendingFile.name),
          React.createElement('p', { className: 'text-neutral-500 text-xs mt-1' }, formatBytes(pendingFile.size)),
          React.createElement('button', {
            type: 'button',
            onClick: function(e) { e.stopPropagation(); setPendingFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; },
            className: 'mt-2 text-red-400 hover:text-red-300 text-xs underline'
          }, 'Remove')
        ) : React.createElement('div', null,
          React.createElement(UploadCloud, { className: 'w-10 h-10 text-neutral-500 mx-auto mb-2' }),
          React.createElement('p', { className: 'text-neutral-300 text-sm font-medium' }, 'Drop a file here or click to browse'),
          React.createElement('p', { className: 'text-neutral-600 text-xs mt-1' }, 'PDF or ZIP — max 50 MB recommended')
        )
      )
    ),

    // ----- Upload action -----
    React.createElement('button', {
      onClick: handleUpload,
      disabled: uploading || !selectedSlug || !pendingFile,
      'data-testid': 'file-upload-submit',
      className: 'w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-3 rounded-full hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
    },
      uploading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Upload, { className: 'w-4 h-4' }),
      uploading ? 'Uploading…' : 'Upload File'
    ),

    msg ? React.createElement('div', {
      className: 'mt-3 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300')
    }, (msg.type === 'success' ? '✅ ' : '⚠️ ') + msg.text) : null,

    // ----- Existing files list -----
    React.createElement('div', { className: 'mt-6 pt-6 border-t border-white/10' },
      React.createElement('h3', { className: 'text-neutral-300 text-sm font-semibold mb-3' }, 'Uploaded files (', existingSlugs.length, ' product', existingSlugs.length === 1 ? '' : 's', ')'),
      existingSlugs.length === 0 ? React.createElement('p', { className: 'text-neutral-600 text-sm' }, 'No digital files uploaded yet.')
        : React.createElement('div', { className: 'space-y-2' },
            existingSlugs.map(function(slug) {
              var slugFiles = files[slug] || [];
              var totalSize = slugFiles.reduce(function(a, f) { return a + (f.size_bytes || 0); }, 0);
              return React.createElement('div', { key: slug, className: 'bg-neutral-950 border border-white/5 rounded-lg p-3 flex items-center justify-between gap-3' },
                React.createElement('div', { className: 'flex-1 min-w-0' },
                  React.createElement('p', { className: 'text-white font-mono text-sm truncate' }, slug),
                  React.createElement('p', { className: 'text-neutral-500 text-xs mt-0.5' },
                    slugFiles.length, ' file', slugFiles.length === 1 ? '' : 's',
                    ' • ', formatBytes(totalSize),
                    ' • ', slugFiles.map(function(f) { return f.filename; }).join(', ')
                  )
                ),
                React.createElement('button', {
                  onClick: function() { handleDelete(slug); },
                  'data-testid': 'file-delete-' + slug,
                  title: 'Delete all files for this slug',
                  className: 'p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors'
                }, React.createElement(Trash2, { className: 'w-4 h-4' }))
              );
            })
          )
    )
  );
}

// =====================  Product Images Panel  =====================
function ImagesPanel() {
  var [products, setProducts] = useState([]);
  var [selectedSlug, setSelectedSlug] = useState('');
  // pendingFiles is an ARRAY now — admin can drop/select many images at once
  // and they upload sequentially with a running progress counter.
  var [pendingFiles, setPendingFiles] = useState([]);
  var [uploading, setUploading] = useState(false);
  var [uploadProgress, setUploadProgress] = useState(null); // { current, total, fileName }
  var [setAsPrimary, setSetAsPrimary] = useState(false);
  var [msg, setMsg] = useState(null);
  var [dragOver, setDragOver] = useState(false);
  var [showDropbox, setShowDropbox] = useState(false);
  var fileInputRef = useRef(null);
  var [visibility, setVisibility] = useState({});
  var [visFilter, setVisFilter] = useState('all');
  var [visQuery, setVisQuery] = useState('');
  var [visBusy, setVisBusy] = useState('');

  async function loadVisibility() {
    var r = await fetch(BACKEND + '/api/admin/product-visibility', { headers: authHeaders() });
    var d = await r.json();
    var map = {};
    (d.products || []).forEach(function(p) { map[p.slug] = p; });
    setVisibility(map);
  }

  async function toggleVisibility(slug, hidden) {
    setVisBusy(slug);
    try {
      var r = await fetch(BACKEND + '/api/admin/product-visibility/' + encodeURIComponent(slug), {
        method: 'PUT',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ hidden: hidden }),
      });
      // Never swallow this: a silent 401 made the toggle look like it did nothing.
      if (!r.ok) {
        var d = await r.json().catch(function() { return {}; });
        throw new Error(r.status === 401
          ? 'Your admin session expired — log out and log in again, then try once more.'
          : (d.detail || 'Could not change visibility (HTTP ' + r.status + ')'));
      }
      await loadVisibility();
      setMsg({ type: 'success', text: hidden ? 'Hidden from the shop' : 'Now live in the shop' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || String(e) });
    } finally { setVisBusy(''); }
  }

  async function load() {
    var resp = await fetch(BACKEND + '/api/admin/product-images', { headers: authHeaders() });
    var data = await resp.json();
    setProducts(data.products || []);
    loadVisibility();
  }

  useEffect(function() { load(); }, []);

  var selectedProduct = products.find(function(p) { return p.slug === selectedSlug; });
  var galleryImages = (selectedProduct && selectedProduct.images) || [];
  var primaryImage = selectedProduct && selectedProduct.image;

  function formatBytes(b) {
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1024 / 1024).toFixed(2) + ' MB';
  }

  function addFiles(fileList) {
    if (!fileList || fileList.length === 0) return;
    var ALLOWED = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    var MAX = 25 * 1024 * 1024;
    var accepted = [];
    var rejected = [];
    for (var i = 0; i < fileList.length; i++) {
      var f = fileList[i];
      if (ALLOWED.indexOf(f.type) === -1) { rejected.push(f.name + ' (unsupported type)'); continue; }
      if (f.size > MAX) { rejected.push(f.name + ' (>25 MB)'); continue; }
      accepted.push(f);
    }
    if (accepted.length) setPendingFiles(function(prev) { return prev.concat(accepted); });
    if (rejected.length) {
      setMsg({ type: 'error', text: 'Skipped ' + rejected.length + ' file(s): ' + rejected.slice(0, 3).join(', ') + (rejected.length > 3 ? '…' : '') });
    } else if (accepted.length) {
      setMsg(null);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  function removePending(idx) {
    setPendingFiles(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  }

  async function handleUpload() {
    if (!selectedSlug) { setMsg({ type: 'error', text: 'Select a product first' }); return; }
    if (!pendingFiles.length) { setMsg({ type: 'error', text: 'Choose at least one image' }); return; }
    setUploading(true); setMsg(null);
    var successCount = 0;
    var failures = [];
    // Upload sequentially — keeps memory low and avoids overwhelming the
    // backend / Cloudinary on weak Wi-Fi. The FIRST file gets the
    // setAsPrimary flag (if checked); the rest are added to the gallery.
    for (var i = 0; i < pendingFiles.length; i++) {
      var f = pendingFiles[i];
      setUploadProgress({ current: i + 1, total: pendingFiles.length, fileName: f.name });
      try {
        await uploadProductImage(selectedSlug, f, setAsPrimary && i === 0);
        successCount += 1;
      } catch (e) {
        failures.push(f.name + ': ' + (e.message || e));
      }
    }
    setUploadProgress(null);
    setUploading(false);
    setPendingFiles([]);
    setSetAsPrimary(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await load();
    if (failures.length === 0) {
      setMsg({ type: 'success', text: 'Uploaded ' + successCount + ' image' + (successCount === 1 ? '' : 's') });
    } else {
      setMsg({ type: 'error', text: 'Uploaded ' + successCount + ', failed ' + failures.length + ': ' + failures.slice(0, 2).join(' • ') + (failures.length > 2 ? '…' : '') });
    }
  }

  async function persist(newImages, newPrimary) {
    var resp = await fetch(BACKEND + '/api/admin/product-images/' + encodeURIComponent(selectedSlug) + '/reorder', {
      method: 'POST',
      headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
      body: JSON.stringify({ images: newImages, image: newPrimary })
    });
    if (!resp.ok) {
      var d = await resp.json().catch(function() { return {}; });
      setMsg({ type: 'error', text: d.detail || 'Save failed' });
      return;
    }
    await load();
  }

  function moveImg(idx, dir) {
    var imgs = galleryImages.slice();
    var target = idx + dir;
    if (target < 0 || target >= imgs.length) return;
    var tmp = imgs[idx]; imgs[idx] = imgs[target]; imgs[target] = tmp;
    persist(imgs, primaryImage);
  }

  function deleteImg(idx) {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Remove this image from the product?')) return;
    var imgs = galleryImages.slice();
    var removed = imgs.splice(idx, 1)[0];
    var newPrimary = primaryImage;
    if (removed === primaryImage) {
      newPrimary = imgs[0] || null;
    }
    persist(imgs, newPrimary);
  }

  function setPrimary(url) {
    persist(galleryImages, url);
  }

  // Group products by category for the picker
  var groups = {};
  products.forEach(function(p) {
    var cat = p.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  });
  var cats = Object.keys(groups).sort();

  return React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6', 'data-testid': 'images-panel' },
    React.createElement('h2', { className: 'text-xl font-bold text-white mb-2 flex items-center gap-2' },
      React.createElement(ImageIcon, { className: 'w-5 h-5 text-[#d4af37]' }),
      'Product Images'
    ),
    React.createElement('p', { className: 'text-neutral-500 text-sm mb-5' }, 'Upload, reorder, remove, or change the primary image for any product. Changes go live instantly — no redeploy needed.'),

    // Storefront visibility — hide a product from the shop without deleting it.
    React.createElement('div', { className: 'mb-6 border border-white/10 rounded-xl p-4', 'data-testid': 'visibility-panel' },
      React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-3' },
        React.createElement('h3', { className: 'text-white text-sm font-bold mr-2' }, 'Shop visibility'),
        ['all', 'live', 'hidden'].map(function(v) {
          var count = Object.keys(visibility).filter(function(s) {
            if (v === 'all') return true;
            return v === 'hidden' ? visibility[s].hidden : !visibility[s].hidden;
          }).length;
          return React.createElement('button', {
            key: v,
            onClick: function() { setVisFilter(v); },
            'data-testid': 'visibility-filter-' + v,
            className: 'px-3 py-1.5 rounded-full text-xs font-semibold ' + (visFilter === v ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-300')
          }, (v === 'all' ? 'All' : v === 'live' ? 'Live' : 'Hidden') + ' (' + count + ')');
        }),
        React.createElement('input', {
          value: visQuery,
          onChange: function(e) { setVisQuery(e.target.value); },
          placeholder: 'Search products…',
          'data-testid': 'visibility-search',
          className: 'ml-auto w-full sm:w-64 bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-white text-xs'
        })
      ),
      React.createElement('div', { className: 'max-h-80 overflow-y-auto space-y-1' },
        Object.keys(visibility).sort(function(a, b) {
          return (visibility[a].name || '').toLowerCase() < (visibility[b].name || '').toLowerCase() ? -1 : 1;
        }).filter(function(slug) {
          var p = visibility[slug];
          if (visFilter === 'hidden' && !p.hidden) return false;
          if (visFilter === 'live' && p.hidden) return false;
          var q = visQuery.trim().toLowerCase();
          if (!q) return true;
          return ((p.name || '') + ' ' + slug).toLowerCase().indexOf(q) !== -1;
        }).map(function(slug) {
          var p = visibility[slug];
          return React.createElement('div', {
            key: slug,
            'data-testid': 'visibility-row-' + slug,
            className: 'flex items-center gap-3 px-3 py-2 rounded-lg ' + (p.hidden ? 'bg-neutral-950 border border-white/5' : 'bg-white/5')
          },
            React.createElement('div', { className: 'min-w-0 flex-1' },
              React.createElement('div', { className: 'text-white text-sm truncate' }, p.name || slug),
              React.createElement('div', { className: 'text-neutral-600 text-xs' }, slug, p.price ? ' · $' + p.price : '')
            ),
            React.createElement('span', {
              className: 'text-xs px-2 py-1 rounded-full flex-shrink-0 ' + (p.hidden ? 'bg-neutral-800 text-neutral-400' : 'bg-emerald-500/15 text-emerald-300')
            }, p.hidden ? 'Hidden' : 'Live'),
            React.createElement('button', {
              onClick: function() { toggleVisibility(slug, !p.hidden); },
              disabled: visBusy === slug,
              'data-testid': 'visibility-toggle-' + slug,
              className: 'flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 ' + (p.hidden ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-200')
            }, visBusy === slug ? '…' : (p.hidden ? 'Show in shop' : 'Hide'))
          );
        })
      )
    ),

    // Product picker
    React.createElement('div', { className: 'mb-5' },
      React.createElement('label', { className: 'block text-neutral-400 text-xs font-semibold uppercase tracking-wide mb-2' }, '1. Product'),
      React.createElement('select', {
        value: selectedSlug,
        onChange: function(e) { setSelectedSlug(e.target.value); setMsg(null); },
        'data-testid': 'image-product-select',
        className: 'w-full bg-neutral-950 border border-white/10 rounded-lg px-4 py-3 text-white text-sm focus:border-[#d4af37] focus:outline-none'
      },
        React.createElement('option', { value: '' }, '— Select a product (' + products.length + ' total) —'),
        cats.map(function(cat) {
          return React.createElement('optgroup', { key: cat, label: cat.replace('-', ' ').toUpperCase() },
            groups[cat].map(function(p) {
              var imgCount = (p.images && p.images.length) || (p.image ? 1 : 0);
              return React.createElement('option', { key: p.slug, value: p.slug }, p.name + '  (' + imgCount + ' img)');
            })
          );
        })
      )
    ),

    selectedProduct ? React.createElement('div', null,
      // Existing images grid
      React.createElement('div', { className: 'mb-5' },
        React.createElement('label', { className: 'block text-neutral-400 text-xs font-semibold uppercase tracking-wide mb-2' },
          'Current images (', galleryImages.length, ')'
        ),
        galleryImages.length === 0 ? React.createElement('p', { className: 'text-neutral-600 text-sm bg-neutral-950 rounded-lg p-4 text-center' }, 'No images yet — upload below') :
        React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3' },
          galleryImages.map(function(url, idx) {
            var isPrimary = url === primaryImage;
            return React.createElement('div', {
              key: url + idx,
              className: 'relative group bg-neutral-950 rounded-lg overflow-hidden border ' + (isPrimary ? 'border-[#d4af37] ring-2 ring-[#d4af37]/20' : 'border-white/10')
            },
              React.createElement('img', { src: url, alt: 'Image ' + (idx + 1), className: 'w-full aspect-square object-cover', referrerPolicy: 'no-referrer' }),
              isPrimary ? React.createElement('span', { className: 'absolute top-2 left-2 bg-[#d4af37] text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1' },
                React.createElement(Star, { className: 'w-3 h-3 fill-current' }), 'PRIMARY'
              ) : null,
              React.createElement('div', { className: 'absolute inset-x-0 bottom-0 bg-black/85 backdrop-blur-sm p-2 flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity' },
                !isPrimary ? React.createElement('button', { onClick: function() { setPrimary(url); }, title: 'Set as primary', className: 'p-1.5 bg-neutral-800 hover:bg-[#d4af37] hover:text-black rounded text-white' }, React.createElement(Star, { className: 'w-3.5 h-3.5' })) : null,
                React.createElement('button', { onClick: function() { moveImg(idx, -1); }, disabled: idx === 0, title: 'Move up/left', className: 'p-1.5 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-30 rounded text-white' }, React.createElement(ArrowUp, { className: 'w-3.5 h-3.5' })),
                React.createElement('button', { onClick: function() { moveImg(idx, 1); }, disabled: idx === galleryImages.length - 1, title: 'Move down/right', className: 'p-1.5 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-30 rounded text-white' }, React.createElement(ArrowDown, { className: 'w-3.5 h-3.5' })),
                React.createElement('button', { onClick: function() { deleteImg(idx); }, title: 'Remove', className: 'p-1.5 bg-red-500/20 hover:bg-red-500 rounded text-red-300 hover:text-white' }, React.createElement(Trash2, { className: 'w-3.5 h-3.5' }))
              )
            );
          })
        )
      ),

      // Upload zone
      React.createElement('div', { className: 'mb-3' },
        React.createElement('label', { className: 'block text-neutral-400 text-xs font-semibold uppercase tracking-wide mb-2' }, '2. Add images (drop or pick multiple at once)'),
        React.createElement('div', {
          onDragOver: function(e) { e.preventDefault(); setDragOver(true); },
          onDragLeave: function() { setDragOver(false); },
          onDrop: handleDrop,
          onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); },
          className: 'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ' + (dragOver ? 'border-[#d4af37] bg-[#d4af37]/10' : 'border-white/15 hover:border-white/30 bg-neutral-950')
        },
          React.createElement('input', {
            ref: fileInputRef,
            type: 'file',
            multiple: true,
            accept: 'image/jpeg,image/png,image/webp,image/jpg',
            className: 'hidden',
            'data-testid': 'image-upload-input',
            onChange: function(e) { addFiles(e.target.files); }
          }),
          React.createElement(UploadCloud, { className: 'w-10 h-10 text-neutral-500 mx-auto mb-2' }),
          React.createElement('p', { className: 'text-neutral-300 text-sm font-medium' }, 'Drop images or click to browse (multiple OK)'),
          React.createElement('p', { className: 'text-neutral-600 text-xs mt-1' }, 'JPG, PNG, or WebP — resized in your browser before upload')
        ),
        React.createElement('div', { className: 'mt-2 flex items-center gap-2' },
          React.createElement('button', {
            type: 'button',
            onClick: function() { setShowDropbox(function(v) { return !v; }); },
            'data-testid': 'dropbox-picker-toggle',
            className: 'text-xs text-[#d4af37] hover:text-white border border-[#d4af37]/40 hover:border-white rounded-full px-3 py-1.5 transition-colors'
          }, showDropbox ? 'Hide my Dropbox' : 'Or choose an image already in my Dropbox'),
          React.createElement('span', { className: 'text-neutral-600 text-xs' }, 'no upload needed')
        )
      ),

      showDropbox ? React.createElement(DropboxImagePicker, {
        slug: selectedSlug,
        onClose: function() { setShowDropbox(false); },
        onAttached: async function(d) {
          await load();
          var failed = (d.failed || []).length;
          setMsg(failed
            ? { type: 'error', text: 'Attached ' + d.added + ', failed ' + failed + ': ' + (d.failed[0].error || '') }
            : { type: 'success', text: 'Attached ' + d.added + ' image' + (d.added === 1 ? '' : 's') + ' from Dropbox' });
        }
      }) : null,

      // Pending queue — thumbnails of files waiting to be uploaded.
      pendingFiles.length > 0 ? React.createElement('div', { className: 'mb-3 bg-neutral-950 border border-white/10 rounded-xl p-3' },
        React.createElement('div', { className: 'flex items-center justify-between mb-2' },
          React.createElement('p', { className: 'text-neutral-300 text-xs font-semibold uppercase tracking-wide' }, pendingFiles.length + ' image' + (pendingFiles.length === 1 ? '' : 's') + ' queued'),
          !uploading ? React.createElement('button', {
            type: 'button',
            onClick: function() { setPendingFiles([]); if (fileInputRef.current) fileInputRef.current.value = ''; },
            className: 'text-red-400 hover:text-red-300 text-xs underline'
          }, 'Clear all') : null
        ),
        React.createElement('div', { className: 'grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2' },
          pendingFiles.map(function(f, idx) {
            var url = URL.createObjectURL(f);
            return React.createElement('div', { key: f.name + idx, className: 'relative group bg-black/40 rounded-lg overflow-hidden border border-white/10' },
              React.createElement('img', { src: url, alt: f.name, className: 'w-full aspect-square object-cover' }),
              React.createElement('div', { className: 'absolute inset-x-0 bottom-0 bg-black/85 px-1.5 py-1 text-[10px] text-neutral-300 truncate' }, formatBytes(f.size)),
              !uploading ? React.createElement('button', {
                type: 'button',
                onClick: function(e) { e.stopPropagation(); removePending(idx); },
                title: 'Remove from queue',
                className: 'absolute top-1 right-1 w-6 h-6 bg-red-500/80 hover:bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity'
              }, '×') : null
            );
          })
        )
      ) : null,

      uploadProgress ? React.createElement('div', { className: 'mb-3 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-lg px-3 py-2 text-sm text-[#d4af37]' },
        'Uploading ' + uploadProgress.current + ' / ' + uploadProgress.total + ' — ' + uploadProgress.fileName
      ) : null,

      React.createElement('label', { className: 'flex items-center gap-2 mb-3 text-neutral-400 text-sm cursor-pointer select-none' },
        React.createElement('input', { type: 'checkbox', checked: setAsPrimary, onChange: function(e) { setSetAsPrimary(e.target.checked); }, className: 'w-4 h-4 accent-[#d4af37]' }),
        'Make the FIRST uploaded image the primary (cover) image'
      ),

      React.createElement('button', {
        onClick: handleUpload,
        disabled: uploading || pendingFiles.length === 0,
        'data-testid': 'image-upload-submit',
        className: 'w-full flex items-center justify-center gap-2 bg-[#d4af37] text-black font-semibold py-3 rounded-full hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors'
      },
        uploading ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Upload, { className: 'w-4 h-4' }),
        uploading ? 'Uploading…' : (pendingFiles.length > 1 ? ('Add ' + pendingFiles.length + ' images to product') : 'Add image to product')
      ),

      msg ? React.createElement('div', {
        className: 'mt-3 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300')
      }, (msg.type === 'success' ? '✅ ' : '⚠️ ') + msg.text) : null,

      // Variant → Image mapping panel
      React.createElement(VariantImageMapper, {
        slug: selectedSlug,
        product: selectedProduct,
        galleryImages: galleryImages,
      })
    ) : React.createElement('p', { className: 'text-neutral-600 text-sm bg-neutral-950 rounded-lg p-6 text-center' }, 'Select a product above to manage its images.')
  );
}


function VariantImageMapper(props) {
  var slug = props.slug;
  var product = props.product;
  var galleryImages = props.galleryImages || [];
  var [mapping, setMapping] = useState({});  // { variant_value_lower: image_url }
  var [savingKey, setSavingKey] = useState('');
  var [msg, setMsg] = useState(null);

  // Collect all variant values across all groups
  var variants = [];
  if (product && product.variants && product.variants.groups) {
    product.variants.groups.forEach(function(g) {
      (g.options || []).forEach(function(opt) {
        variants.push({ group: g.name, value: opt });
      });
    });
  }

  async function load() {
    if (!slug) return;
    try {
      var r = await fetch(BACKEND + '/api/admin/products/' + encodeURIComponent(slug) + '/variant-images', { headers: authHeaders() });
      var d = await r.json();
      var m = {};
      (d.variant_images || []).forEach(function(vi) {
        m[(vi.variant_value || '').toLowerCase()] = vi.image_url;
      });
      setMapping(m);
    } catch (e) { /* non-fatal */ }
  }
  useEffect(function() { load(); /* eslint-disable-next-line */ }, [slug]);

  async function saveOne(variant_value, group_name, image_url) {
    var key = variant_value.toLowerCase();
    setSavingKey(key); setMsg(null);
    try {
      if (!image_url) {
        // delete
        await fetch(BACKEND + '/api/admin/products/' + encodeURIComponent(slug) +
          '/variant-images?variant_value=' + encodeURIComponent(variant_value),
          { method: 'DELETE', headers: authHeaders() });
      } else {
        var resp = await fetch(BACKEND + '/api/admin/products/' + encodeURIComponent(slug) + '/variant-images', {
          method: 'POST',
          headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
          body: JSON.stringify({ variant_value: variant_value, image_url: image_url, group_name: group_name })
        });
        if (!resp.ok) {
          var ed = await resp.json();
          throw new Error(ed.detail || 'Save failed');
        }
      }
      setMsg({ type: 'success', text: 'Saved "' + variant_value + '"' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Save failed' });
    }
    setSavingKey('');
  }

  if (variants.length === 0) return null;

  return React.createElement('div', { className: 'mt-8 pt-6 border-t border-white/10', 'data-testid': 'variant-images-mapper' },
    React.createElement('h3', { className: 'text-white text-base font-semibold mb-1 flex items-center gap-2' },
      React.createElement(ImageIcon, { className: 'w-4 h-4 text-[#d4af37]' }),
      'Variant → Image mapping'
    ),
    React.createElement('p', { className: 'text-neutral-500 text-xs mb-4' },
      'Tell the site which gallery image to show when a customer picks each variant ',
      '(color, instrument, type, etc.). Updates live with no redeploy.'
    ),
    galleryImages.length === 0 ?
      React.createElement('p', { className: 'text-neutral-600 text-sm bg-neutral-950 rounded-lg p-4 text-center' }, 'Upload at least one image above before mapping variants.') :
      React.createElement('div', { className: 'space-y-3' },
        variants.map(function(v) {
          var key = v.value.toLowerCase();
          var currentUrl = mapping[key] || '';
          var isSaving = savingKey === key;
          return React.createElement('div', { key: v.group + '|' + v.value, className: 'flex flex-col md:flex-row md:items-center gap-2 bg-neutral-950 border border-white/5 rounded-lg p-3' },
            React.createElement('div', { className: 'md:w-1/3' },
              React.createElement('p', { className: 'text-white text-sm font-medium' }, v.value),
              React.createElement('p', { className: 'text-neutral-500 text-xs' }, v.group)
            ),
            React.createElement('div', { className: 'flex-1 flex items-center gap-2' },
              currentUrl ? React.createElement('img', { src: currentUrl, alt: '', className: 'w-12 h-12 rounded object-cover border border-white/10' }) : null,
              React.createElement('select', {
                value: currentUrl,
                onChange: function(e) { saveOne(v.value, v.group, e.target.value); },
                disabled: isSaving,
                'data-testid': 'variant-image-select-' + key.replace(/\s+/g, '-'),
                className: 'flex-1 bg-neutral-900 border border-white/10 text-white text-sm rounded-lg px-3 py-2'
              },
                React.createElement('option', { value: '' }, '— No mapping (use default gallery) —'),
                galleryImages.map(function(img, idx) {
                  return React.createElement('option', { key: img, value: img }, 'Image #' + (idx + 1));
                })
              ),
              isSaving ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin text-[#d4af37]' }) : null
            )
          );
        })
      ),
    msg ? React.createElement('div', {
      className: 'mt-3 px-3 py-2 rounded-lg text-xs ' + (msg.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300')
    }, (msg.type === 'success' ? '✅ ' : '⚠️ ') + msg.text) : null
  );
}


// Multi-language description editor. Reads + writes Sanity. Lets Isaías
// fix copy in any of the 7 languages himself, plus one-click auto-translate
// the English text into all other languages via the Emergent LLM key.
var DESC_LANGS = [
  { code: 'en', label: '🇺🇸 English' },
  { code: 'es', label: '🇲🇽 Español' },
  { code: 'ja', label: '🇯🇵 日本語' },
  { code: 'de', label: '🇩🇪 Deutsch' },
  { code: 'fr', label: '🇫🇷 Français' },
  { code: 'it', label: '🇮🇹 Italiano' },
  { code: 'uk', label: '🇺🇦 Українська' },
];

function DescriptionsPanel() {
  var [products, setProducts] = useState([]);
  var [selectedSlug, setSelectedSlug] = useState('');
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('');
  var [descsByLang, setDescsByLang] = useState({});
  var [featsByLang, setFeatsByLang] = useState({});
  var [activeLang, setActiveLang] = useState('en');
  var [saving, setSaving] = useState(false);
  var [translating, setTranslating] = useState(false);
  var [msg, setMsg] = useState(null);

  async function load() {
    setLoading(true);
    var r = await fetch(BACKEND + '/api/admin/product-descriptions', { headers: authHeaders() });
    var d = await r.json();
    setProducts(d.products || []);
    setLoading(false);
  }
  useEffect(function() { load(); }, []);

  // When user picks a different product, hydrate the editor.
  useEffect(function() {
    var p = products.find(function(x) { return x.slug === selectedSlug; });
    if (!p) { setDescsByLang({}); setFeatsByLang({}); return; }
    // Seed with all 7 langs so the editor always shows every input.
    var d = {};
    var f = {};
    DESC_LANGS.forEach(function(L) {
      d[L.code] = (p.descriptionsByLanguage && p.descriptionsByLanguage[L.code]) || (L.code === 'en' ? p.description : '') || '';
      f[L.code] = (p.featuresByLanguage && p.featuresByLanguage[L.code]) || (L.code === 'en' ? p.features : []) || [];
    });
    setDescsByLang(d);
    setFeatsByLang(f);
    setMsg(null);
  }, [selectedSlug, products]);

  function updateDesc(lang, v) { setDescsByLang(function(prev) { var n = Object.assign({}, prev); n[lang] = v; return n; }); }
  function updateFeatures(lang, idx, v) {
    setFeatsByLang(function(prev) {
      var n = Object.assign({}, prev);
      var arr = (n[lang] || []).slice();
      arr[idx] = v;
      n[lang] = arr;
      return n;
    });
  }
  function addFeature(lang) {
    setFeatsByLang(function(prev) { var n = Object.assign({}, prev); n[lang] = (n[lang] || []).concat(['']); return n; });
  }
  function removeFeature(lang, idx) {
    setFeatsByLang(function(prev) { var n = Object.assign({}, prev); n[lang] = (n[lang] || []).filter(function(_, i) { return i !== idx; }); return n; });
  }

  async function handleSave() {
    if (!selectedSlug) return;
    setSaving(true); setMsg(null);
    try {
      // Strip blank features and blank language entries before sending.
      var cleanDesc = {};
      Object.keys(descsByLang).forEach(function(k) {
        var s = (descsByLang[k] || '').trim();
        if (s) cleanDesc[k] = s;
      });
      var cleanFeats = {};
      Object.keys(featsByLang).forEach(function(k) {
        var arr = (featsByLang[k] || []).map(function(x) { return (x || '').trim(); }).filter(Boolean);
        if (arr.length) cleanFeats[k] = arr;
      });
      var body = {
        description: cleanDesc.en || '',
        descriptionsByLanguage: cleanDesc,
        features: cleanFeats.en || [],
        featuresByLanguage: cleanFeats,
      };
      var r = await fetch(BACKEND + '/api/admin/product-descriptions/' + encodeURIComponent(selectedSlug), {
        method: 'PUT',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify(body),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      setMsg({ type: 'success', text: 'Saved to Sanity. Storefront cache cleared.' });
      await load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally { setSaving(false); }
  }

  async function handleTranslate() {
    if (!selectedSlug) return;
    var en = (descsByLang.en || '').trim();
    if (!en) { setMsg({ type: 'error', text: 'Write the English description first' }); return; }
    if (!window.confirm('Auto-translate the English description + features to all 6 other languages? This will overwrite the existing translations.')) return;
    setTranslating(true); setMsg(null);
    try {
      var p = products.find(function(x) { return x.slug === selectedSlug; });
      var r = await fetch(BACKEND + '/api/admin/product-descriptions/translate', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({
          description: en,
          features: (featsByLang.en || []).map(function(x) { return (x || '').trim(); }).filter(Boolean),
          product_name: p ? p.name : selectedSlug,
        })
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Translate failed');
      var tr = d.translations || {};
      // Merge into editor state (don't clobber English)
      setDescsByLang(function(prev) {
        var n = Object.assign({}, prev);
        Object.keys(tr).forEach(function(lang) {
          if (lang !== 'en' && tr[lang] && typeof tr[lang].description === 'string') {
            n[lang] = tr[lang].description;
          }
        });
        return n;
      });
      setFeatsByLang(function(prev) {
        var n = Object.assign({}, prev);
        Object.keys(tr).forEach(function(lang) {
          if (lang !== 'en' && tr[lang] && Array.isArray(tr[lang].features)) {
            n[lang] = tr[lang].features;
          }
        });
        return n;
      });
      setMsg({ type: 'success', text: 'Translations populated — review then click Save.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally { setTranslating(false); }
  }

  var filtered = products.filter(function(p) {
    if (!filter.trim()) return true;
    var q = filter.trim().toLowerCase();
    return (p.name || '').toLowerCase().indexOf(q) >= 0 || (p.slug || '').toLowerCase().indexOf(q) >= 0;
  });
  var selectedProduct = products.find(function(p) { return p.slug === selectedSlug; });

  return React.createElement('div', { className: 'bg-neutral-950/40 border border-white/10 rounded-xl p-5', 'data-testid': 'descriptions-panel' },
    React.createElement('div', { className: 'mb-4' },
      React.createElement('h2', { className: 'text-white text-xl font-bold mb-1' }, 'Product copy (all languages)'),
      React.createElement('p', { className: 'text-neutral-500 text-xs' }, 'Edits write directly to Sanity. Storefront picks them up within a minute.')
    ),

    React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-12 gap-4' },
      // Left rail: product picker
      React.createElement('div', { className: 'lg:col-span-4' },
        React.createElement('input', {
          value: filter,
          onChange: function(e) { setFilter(e.target.value); },
          placeholder: 'Search products…',
          'data-testid': 'desc-product-search',
          className: 'w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-2',
        }),
        loading ? React.createElement('div', { className: 'text-neutral-500 text-sm' }, 'Loading…') :
          React.createElement('div', { className: 'border border-white/10 rounded-lg max-h-[600px] overflow-y-auto bg-neutral-950' },
            filtered.length === 0 ? React.createElement('div', { className: 'p-3 text-neutral-500 text-sm' }, 'No products match.') :
              filtered.map(function(p) {
                var isSel = p.slug === selectedSlug;
                var langCount = p.descriptionsByLanguage ? Object.keys(p.descriptionsByLanguage).filter(function(k) { return (p.descriptionsByLanguage[k] || '').trim(); }).length : 0;
                return React.createElement('button', {
                  key: p.slug,
                  onClick: function() { setSelectedSlug(p.slug); },
                  'data-testid': 'desc-product-row-' + p.slug,
                  className: 'w-full text-left flex items-center gap-2 px-3 py-2 border-b border-white/5 transition-colors ' + (isSel ? 'bg-[#d4af37]/10 text-white' : 'text-neutral-300 hover:bg-white/5')
                },
                  p.image ? React.createElement('img', { src: p.image, alt: '', className: 'w-8 h-8 rounded object-cover bg-neutral-800' }) : null,
                  React.createElement('div', { className: 'flex-1 min-w-0' },
                    React.createElement('div', { className: 'text-sm truncate' }, p.name || p.slug),
                    React.createElement('div', { className: 'text-[10px] text-neutral-600' }, langCount + ' / 7 languages')
                  )
                );
              })
          )
      ),

      // Right pane: editor
      React.createElement('div', { className: 'lg:col-span-8' },
        !selectedProduct ? React.createElement('div', { className: 'text-neutral-500 text-sm border border-dashed border-white/10 rounded-xl p-12 text-center' },
          'Pick a product on the left to edit its copy.'
        ) : React.createElement('div', null,
          React.createElement('div', { className: 'flex items-center justify-between mb-3' },
            React.createElement('h3', { className: 'text-white font-semibold' }, selectedProduct.name),
            React.createElement('div', { className: 'flex gap-2' },
              React.createElement('button', {
                onClick: handleTranslate,
                disabled: translating,
                'data-testid': 'desc-translate-btn',
                className: 'text-xs bg-neutral-900 hover:bg-neutral-800 border border-[#d4af37]/30 text-[#d4af37] px-3 py-1.5 rounded-lg disabled:opacity-50',
                title: 'Use AI to translate English → 6 other languages'
              }, translating ? 'Translating…' : '✨ Auto-translate from English'),
              React.createElement('button', {
                onClick: handleSave,
                disabled: saving,
                'data-testid': 'desc-save-btn',
                className: 'text-xs bg-[#d4af37] text-black px-4 py-1.5 rounded-lg font-semibold disabled:opacity-50'
              }, saving ? 'Saving…' : 'Save to Sanity')
            )
          ),
          msg ? React.createElement('div', {
            className: 'mb-3 px-3 py-2 rounded-lg text-sm border ' + (msg.type === 'success' ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300')
          }, msg.text) : null,

          // Language tabs
          React.createElement('div', { className: 'flex flex-wrap gap-1 mb-3 border-b border-white/10' },
            DESC_LANGS.map(function(L) {
              var has = (descsByLang[L.code] || '').trim().length > 0;
              return React.createElement('button', {
                key: L.code,
                onClick: function() { setActiveLang(L.code); },
                'data-testid': 'desc-lang-tab-' + L.code,
                className: 'px-3 py-2 text-sm border-b-2 transition-colors ' + (activeLang === L.code ? 'border-[#d4af37] text-[#d4af37]' : (has ? 'border-transparent text-neutral-300 hover:text-white' : 'border-transparent text-neutral-600 hover:text-neutral-400'))
              }, L.label, has ? '' : ' •');
            })
          ),

          // Editor for active lang
          React.createElement('div', { className: 'space-y-3' },
            React.createElement('div', null,
              React.createElement('label', { className: 'block text-neutral-400 text-xs uppercase tracking-wider mb-1 font-semibold' }, 'Description'),
              React.createElement('textarea', {
                value: descsByLang[activeLang] || '',
                onChange: function(e) { updateDesc(activeLang, e.target.value); },
                placeholder: 'Type the ' + (DESC_LANGS.find(function(x) { return x.code === activeLang; }) || {}).label + ' description…',
                rows: 8,
                'data-testid': 'desc-textarea-' + activeLang,
                className: 'w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono leading-relaxed'
              }),
              React.createElement('div', { className: 'text-[11px] text-neutral-600 mt-1' }, (descsByLang[activeLang] || '').length + ' characters')
            ),
            React.createElement('div', null,
              React.createElement('div', { className: 'flex items-center justify-between mb-1' },
                React.createElement('label', { className: 'text-neutral-400 text-xs uppercase tracking-wider font-semibold' }, 'Features (bullet list)'),
                React.createElement('button', {
                  onClick: function() { addFeature(activeLang); },
                  className: 'text-[#d4af37] hover:underline text-xs',
                  'data-testid': 'desc-feature-add'
                }, '+ Add feature')
              ),
              (featsByLang[activeLang] || []).map(function(f, idx) {
                return React.createElement('div', { key: idx, className: 'flex items-center gap-2 mb-2' },
                  React.createElement('input', {
                    value: f,
                    onChange: function(e) { updateFeatures(activeLang, idx, e.target.value); },
                    placeholder: 'Feature ' + (idx + 1),
                    'data-testid': 'desc-feature-' + activeLang + '-' + idx,
                    className: 'flex-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm'
                  }),
                  React.createElement('button', {
                    onClick: function() { removeFeature(activeLang, idx); },
                    className: 'text-red-400 hover:text-red-300 text-xs w-7 h-7 flex items-center justify-center rounded hover:bg-red-500/10'
                  }, '×')
                );
              })
            )
          )
        )
      )
    )
  );
}



function StockPanel() {
  var [stock, setStock] = useState([]);
  var [products, setProducts] = useState([]);
  var [loading, setLoading] = useState(true);
  var [msg, setMsg] = useState(null);
  // form state
  var [slug, setSlug] = useState('');
  var [variant, setVariant] = useState('');
  var [quantity, setQuantity] = useState('');
  var [threshold, setThreshold] = useState('1');
  var [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      var r = await fetch(BACKEND + '/api/admin/stock', { headers: authHeaders() });
      var d = await r.json();
      setStock(d.stock || []);
    } catch (e) { setMsg({ type: 'error', text: 'Could not load stock' }); }
    try {
      var rp = await fetch(BACKEND + '/api/sanity/products');
      var dp = await rp.json();
      setProducts((dp.products || []).filter(function(p) { return p.category !== 'sheet-music'; }));
    } catch (e) { /* non-fatal */ }
    setLoading(false);
  }
  useEffect(function() { load(); }, []);

  async function saveStock() {
    if (!slug) { setMsg({ type: 'error', text: 'Pick a product' }); return; }
    var q = parseInt(quantity, 10);
    if (isNaN(q) || q < 0) { setMsg({ type: 'error', text: 'Quantity must be a non-negative integer' }); return; }
    setSaving(true); setMsg(null);
    try {
      var product = products.find(function(p) { return p.slug === slug; });
      var resp = await fetch(BACKEND + '/api/admin/stock', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({
          slug: slug,
          variant: variant || null,
          quantity: q,
          low_threshold: parseInt(threshold, 10) || 1,
          product_name: product ? product.name : slug,
        }),
      });
      if (!resp.ok) {
        var ed = await resp.json();
        throw new Error(ed.detail || 'Could not save');
      }
      setMsg({ type: 'success', text: 'Saved stock for ' + slug + (variant ? ' (' + variant + ')' : '') });
      setQuantity(''); setVariant(''); setSlug('');
      load();
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Failed to save' });
    }
    setSaving(false);
  }

  async function removeRow(row) {
    if (!window.confirm('Stop tracking stock for ' + row.slug + (row.variant ? ' (' + row.variant + ')' : '') + '?')) return;
    var qs = new URLSearchParams({ slug: row.slug });
    if (row.variant) qs.set('variant', row.variant);
    await fetch(BACKEND + '/api/admin/stock?' + qs.toString(), { method: 'DELETE', headers: authHeaders() });
    load();
  }

  return React.createElement('div', { 'data-testid': 'admin-stock-panel', className: 'space-y-6' },
    // Form
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6' },
      React.createElement('h2', { className: 'text-white text-lg font-semibold mb-4' }, 'Set stock quantity'),
      React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-5 gap-3' },
        React.createElement('select', {
          value: slug, onChange: function(e) { setSlug(e.target.value); },
          'data-testid': 'stock-product-select',
          className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2 md:col-span-2'
        },
          React.createElement('option', { value: '' }, '— product —'),
          products.map(function(p) {
            return React.createElement('option', { key: p.slug, value: p.slug }, p.name);
          })
        ),
        React.createElement('input', {
          value: variant, onChange: function(e) { setVariant(e.target.value); },
          placeholder: 'variant (optional)',
          'data-testid': 'stock-variant-input',
          className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2'
        }),
        React.createElement('input', {
          value: quantity, onChange: function(e) { setQuantity(e.target.value); },
          type: 'number', min: 0, placeholder: 'qty',
          'data-testid': 'stock-quantity-input',
          className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2'
        }),
        React.createElement('input', {
          value: threshold, onChange: function(e) { setThreshold(e.target.value); },
          type: 'number', min: 0, placeholder: 'low threshold',
          'data-testid': 'stock-threshold-input',
          className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2'
        })
      ),
      React.createElement('p', { className: 'text-neutral-500 text-xs mt-2' },
        'Leave variant blank to track the product as a single SKU. Low threshold = when remaining stock falls to this number (or below), Isaias gets an email.'
      ),
      React.createElement('button', {
        onClick: saveStock, disabled: saving,
        'data-testid': 'stock-save-btn',
        className: 'mt-4 bg-[#d4af37] text-black font-semibold px-5 py-2 rounded-lg hover:bg-white disabled:opacity-50'
      }, saving ? 'Saving…' : 'Save'),
      msg ? React.createElement('div', {
        className: 'mt-3 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-300' : 'bg-red-500/10 border border-red-500/30 text-red-300')
      }, msg.text) : null
    ),
    // Table
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl overflow-hidden' },
      React.createElement('table', { className: 'w-full text-sm' },
        React.createElement('thead', { className: 'bg-neutral-950' },
          React.createElement('tr', { className: 'text-left text-neutral-500 text-xs uppercase tracking-wider' },
            React.createElement('th', { className: 'p-3' }, 'Product'),
            React.createElement('th', { className: 'p-3' }, 'Variant'),
            React.createElement('th', { className: 'p-3' }, 'In stock'),
            React.createElement('th', { className: 'p-3' }, 'Low threshold'),
            React.createElement('th', { className: 'p-3' }, 'Last alerted'),
            React.createElement('th', { className: 'p-3' }, '')
          )
        ),
        React.createElement('tbody', null,
          loading ? React.createElement('tr', null, React.createElement('td', { colSpan: 6, className: 'p-6 text-center text-neutral-500' }, 'Loading…')) :
          stock.length === 0 ? React.createElement('tr', null, React.createElement('td', { colSpan: 6, className: 'p-6 text-center text-neutral-600' }, 'No tracked variants yet. Add one above.')) :
          stock.map(function(row) {
            var low = row.quantity <= (row.low_threshold || 1);
            return React.createElement('tr', { key: row.slug + '|' + (row.variant || ''), className: 'border-b border-white/5', 'data-testid': 'stock-row-' + row.slug },
              React.createElement('td', { className: 'p-3 text-white' }, row.product_name || row.slug, React.createElement('div', { className: 'text-neutral-500 text-xs' }, row.slug)),
              React.createElement('td', { className: 'p-3 text-neutral-300' }, row.variant || '—'),
              React.createElement('td', { className: 'p-3 font-semibold ' + (low ? 'text-red-400' : 'text-white') }, row.quantity),
              React.createElement('td', { className: 'p-3 text-neutral-400' }, row.low_threshold || 1),
              React.createElement('td', { className: 'p-3 text-neutral-500 text-xs' },
                row.low_stock_alerted_at ? new Date(row.low_stock_alerted_at).toLocaleString() : '—'
              ),
              React.createElement('td', { className: 'p-3 text-right' },
                React.createElement('button', {
                  onClick: function() { removeRow(row); },
                  'data-testid': 'stock-remove-' + row.slug,
                  className: 'text-neutral-500 hover:text-red-400'
                }, React.createElement(Trash2, { className: 'w-4 h-4' }))
              )
            );
          })
        )
      )
    ),
    // ====== Free shipping campaign (manual toggle) ======
    React.createElement(FreeShippingCard, {}),
    // ====== Flash discounts ======
    // Per-product, time-limited sales. Server enforces the sale price at
    // checkout (see `_verify_server_totals`) so customers can't bypass it.
    React.createElement(FlashDiscountsCard, { products: products })
  );
}


function FollowupDripCard() {  // Small one-click card to drain the follow-up email backlog. Calls the
  // admin /run-followup-drip endpoint which sends up to 10 emails per click
  // (Gmail SMTP rate-limit safe). Shows how many are still pending so the
  // admin knows whether to click again.
  var [pending, setPending] = useState(null);
  var [busy, setBusy] = useState(false);
  var [lastSent, setLastSent] = useState(null);
  var [msg, setMsg] = useState(null);

  async function refresh() {
    try {
      // Cheap stat — re-run drip endpoint with zero work doesn't exist, so
      // just call it and read remaining_backlog. Send=0 if nothing to do.
      var r = await fetch(BACKEND + '/api/admin/orders?status=delivered_or_completed_no_email', { headers: authHeaders() });
      // Endpoint above doesn't exist; we fall through to letting the click
      // surface the count.
    } catch (e) {}
  }

  async function sendBatch() {
    setBusy(true); setMsg(null);
    try {
      var r = await fetch(BACKEND + '/api/admin/run-followup-drip', { method: 'POST', headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setLastSent(d.sent_this_run);
      setPending(d.remaining_backlog);
      if (d.sent_this_run === 0 && d.remaining_backlog === 0) {
        setMsg({ type: 'success', text: 'All follow-ups are sent — nothing pending 🎉' });
      } else if (d.sent_this_run === 0) {
        setMsg({ type: 'error', text: 'Sent 0 (Gmail SMTP may be temporarily unavailable). ' + d.remaining_backlog + ' still pending.' });
      } else {
        setMsg({ type: 'success', text: 'Sent ' + d.sent_this_run + ' follow-up emails. ' + d.remaining_backlog + ' still pending — click again to keep draining.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setBusy(false);
  }

  return React.createElement('div', { 'data-testid': 'followup-drip-card', className: 'mb-4 bg-neutral-900 border border-white/10 rounded-xl p-4 flex items-center gap-4 flex-wrap' },
    React.createElement('div', { className: 'flex-1 min-w-[200px]' },
      React.createElement('div', { className: 'text-white text-sm font-semibold' }, '📨 Follow-up email backlog'),
      React.createElement('div', { className: 'text-neutral-500 text-xs mt-0.5' },
        pending == null
          ? 'Click to clear any "Deliver & email" yellow buttons in bulk (sends up to 10 per click — Gmail rate-safe).'
          : (pending + ' orders still need the post-delivery follow-up.')
      ),
      msg ? React.createElement('div', { className: 'mt-2 text-xs ' + (msg.type === 'error' ? 'text-red-400' : 'text-emerald-400') }, msg.text) : null
    ),
    React.createElement('button', {
      onClick: sendBatch, disabled: busy,
      'data-testid': 'run-followup-drip-btn',
      className: 'bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50'
    }, busy ? 'Sending\u2026' : 'Send pending follow-ups')
  );
}


function ReviewDripCard() {
  // One-click batch sender for review invitations (10 per click, Gmail-safe).
  // Eligible: digital-only orders once paid, physical orders once delivered or
  // completed — each order only once, skipping unsubscribed customers.
  var [pending, setPending] = useState(null);
  var [busy, setBusy] = useState(false);
  var [msg, setMsg] = useState(null);

  var loadPending = useCallback(async function() {
    try {
      var r = await fetch(BACKEND + '/api/admin/review-drip-status', { headers: authHeaders() });
      var d = await r.json();
      if (r.ok) setPending(d.remaining_backlog);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(function() { loadPending(); }, [loadPending]);

  async function sendBatch() {
    setBusy(true); setMsg(null);
    try {
      var r = await fetch(BACKEND + '/api/admin/run-review-drip', { method: 'POST', headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setPending(d.remaining_backlog);
      if (d.sent_this_run === 0 && d.remaining_backlog === 0) {
        setMsg({ type: 'success', text: 'Every eligible customer has already been invited 🎉' });
      } else if (d.sent_this_run === 0) {
        setMsg({ type: 'error', text: 'Sent 0 (SMTP may be unavailable). ' + d.remaining_backlog + ' still pending.' });
      } else {
        setMsg({ type: 'success', text: 'Sent ' + d.sent_this_run + ' review invitations'
          + (d.skipped_optout ? ' (' + d.skipped_optout + ' skipped — unsubscribed)' : '')
          + '. ' + d.remaining_backlog + ' still pending — click again to keep draining.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setBusy(false);
  }

  return React.createElement('div', { 'data-testid': 'review-drip-card', className: 'mb-4 bg-neutral-900 border border-white/10 rounded-xl p-4 flex items-center gap-4 flex-wrap' },
    React.createElement('div', { className: 'flex-1 min-w-[200px]' },
      React.createElement('div', { className: 'text-white text-sm font-semibold' }, '⭐ Review invitations'),
      React.createElement('div', { className: 'text-neutral-500 text-xs mt-0.5' },
        pending == null
          ? 'Invite customers to review what they bought (sends up to 10 per click, in the customer\u2019s language).'
          : (pending + ' customers have not been invited yet (digital: once paid · physical: once delivered/completed).')
      ),
      msg ? React.createElement('div', { className: 'mt-2 text-xs ' + (msg.type === 'error' ? 'text-red-400' : 'text-emerald-400') }, msg.text) : null
    ),
    React.createElement('button', {
      onClick: sendBatch, disabled: busy,
      'data-testid': 'run-review-drip-btn',
      className: 'bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50'
    }, busy ? 'Sending\u2026' : 'Send review invitations')
  );
}


function AdminSearchBar(props) {
  // One box over orders, customers and products. Orders open straight away;
  // customers and products filter the table below.
  var onOpenOrder = props.onOpenOrder;
  var onFilter = props.onFilter;
  var [q, setQ] = useState('');
  var [res, setRes] = useState(null);
  var [busy, setBusy] = useState(false);
  var [open, setOpen] = useState(false);

  useEffect(function() {
    var term = q.trim();
    if (term.length < 2) { setRes(null); return; }
    var cancelled = false;
    setBusy(true);
    var timer = setTimeout(function() {
      fetch(BACKEND + '/api/admin/search?q=' + encodeURIComponent(term), { headers: authHeaders() })
        .then(function(r) { return r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)); })
        .then(function(d) { if (!cancelled) { setRes(d); setOpen(true); setBusy(false); } })
        .catch(function() { if (!cancelled) { setRes(null); setBusy(false); } });
    }, 300);
    return function() { cancelled = true; clearTimeout(timer); setBusy(false); };
  }, [q]);

  async function openOrder(orderNumber) {
    setOpen(false);
    try {
      var r = await fetch(BACKEND + '/api/admin/orders/' + encodeURIComponent(orderNumber), { headers: authHeaders() });
      var d = await r.json();
      if (r.ok) onOpenOrder(d.order || d);
    } catch (e) { console.error(e); }
  }

  function pickFilter(kind, value, label) {
    setOpen(false);
    setQ('');
    onFilter({ kind: kind, value: value, label: label });
  }

  async function pickProduct(slug, name) {
    setOpen(false);
    setQ('');
    onFilter({ kind: 'product', value: slug, label: name, numbers: null });
    try {
      var r = await fetch(BACKEND + '/api/admin/product-buyers?slug=' + encodeURIComponent(slug), { headers: authHeaders() });
      var d = await r.json();
      if (r.ok) onFilter({ kind: 'product', value: slug, label: name, numbers: d.order_numbers || [], units: d.units });
    } catch (e) { console.error(e); }
  }

  var groups = [];
  if (res) {
    if (res.customers.length) {
      groups.push(React.createElement('div', { key: 'c', className: 'py-1' },
        React.createElement('div', { className: 'px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-500' }, 'Customers'),
        res.customers.map(function(c) {
          return React.createElement('button', {
            key: c.email, onClick: function() { pickFilter('customer', c.email, c.name || c.email); },
            'data-testid': 'search-customer-' + c.email,
            className: 'w-full text-left px-3 py-2 hover:bg-white/5 flex items-center justify-between gap-3'
          },
            React.createElement('span', { className: 'min-w-0' },
              React.createElement('span', { className: 'text-white text-sm block truncate' }, c.name || c.email),
              React.createElement('span', { className: 'text-neutral-500 text-xs block truncate' }, c.email)
            ),
            React.createElement('span', { className: 'text-neutral-400 text-xs whitespace-nowrap' },
              c.orders + (c.orders === 1 ? ' order' : ' orders') + ' · $' + c.spent.toFixed(2))
          );
        })
      ));
    }
    if (res.orders.length) {
      groups.push(React.createElement('div', { key: 'o', className: 'py-1 border-t border-white/5' },
        React.createElement('div', { className: 'px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-500' }, 'Orders'),
        res.orders.map(function(o) {
          return React.createElement('button', {
            key: o.order_number, onClick: function() { openOrder(o.order_number); },
            'data-testid': 'search-order-' + o.order_number,
            className: 'w-full text-left px-3 py-2 hover:bg-white/5 flex items-center justify-between gap-3'
          },
            React.createElement('span', { className: 'min-w-0' },
              React.createElement('span', { className: 'text-white text-sm block truncate' },
                o.order_number + ' · ' + (o.customer_name || o.customer_email)),
              React.createElement('span', { className: 'text-neutral-500 text-xs block truncate' }, o.items_preview)
            ),
            React.createElement('span', { className: 'text-[#d4af37] text-xs whitespace-nowrap' }, '$' + Number(o.total || 0).toFixed(2))
          );
        })
      ));
    }
    if (res.products.length) {
      groups.push(React.createElement('div', { key: 'p', className: 'py-1 border-t border-white/5' },
        React.createElement('div', { className: 'px-3 py-1 text-[10px] uppercase tracking-wider text-neutral-500' }, 'Products — show who bought it'),
        res.products.map(function(p) {
          return React.createElement('button', {
            key: p.slug, onClick: function() { pickProduct(p.slug, p.name); },
            'data-testid': 'search-product-' + p.slug,
            className: 'w-full text-left px-3 py-2 hover:bg-white/5 flex items-center justify-between gap-3'
          },
            React.createElement('span', { className: 'text-white text-sm truncate' }, p.name),
            React.createElement('span', { className: 'text-neutral-500 text-xs whitespace-nowrap' }, p.slug)
          );
        })
      ));
    }
    if (!groups.length) {
      groups.push(React.createElement('div', { key: 'n', className: 'px-3 py-4 text-neutral-500 text-sm' },
        'Nothing matches “' + res.query + '”'));
    }
  }

  return React.createElement('div', { className: 'relative mb-4' },
    React.createElement('div', { className: 'flex items-center gap-2 bg-neutral-900 border border-white/10 rounded-xl px-3' },
      React.createElement(Search, { className: 'w-4 h-4 text-neutral-500 shrink-0' }),
      React.createElement('input', {
        value: q,
        onChange: function(e) { setQ(e.target.value); },
        onFocus: function() { if (res) setOpen(true); },
        onKeyDown: function(e) { if (e.key === 'Escape') setOpen(false); },
        'data-testid': 'admin-search-input',
        placeholder: 'Search orders, customers, products — name, email, order number…',
        className: 'flex-1 bg-transparent text-white text-sm py-3 outline-none placeholder:text-neutral-600'
      }),
      busy ? React.createElement(RefreshCw, { className: 'w-4 h-4 text-neutral-500 animate-spin shrink-0' }) : null,
      q ? React.createElement('button', {
        onClick: function() { setQ(''); setRes(null); setOpen(false); },
        'data-testid': 'admin-search-clear',
        className: 'text-neutral-500 hover:text-white text-xs px-1'
      }, '✕') : null
    ),
    open && res ? React.createElement('div', {
      'data-testid': 'admin-search-results',
      className: 'absolute z-30 left-0 right-0 mt-1 bg-neutral-950 border border-white/10 rounded-xl shadow-2xl max-h-96 overflow-y-auto'
    }, groups) : null
  );
}


function ReviewRelinkCard() {
  // One-off repair: review emails sent before the slug fix linked to a blank
  // page. Finds exactly those recipients and emails them again.
  var [status, setStatus] = useState(null);
  var [busy, setBusy] = useState(false);
  var [msg, setMsg] = useState(null);

  var load = useCallback(async function() {
    try {
      var r = await fetch(BACKEND + '/api/admin/review-relink-status', { headers: authHeaders() });
      var d = await r.json();
      if (r.ok) setStatus(d);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(function() { load(); }, [load]);

  async function run(testMode) {
    if (!testMode && !window.confirm('Re-send the review email to ' + (status ? status.audience : '?')
      + ' real customers whose link was broken?')) return;
    setBusy(true); setMsg(null);
    try {
      var url = BACKEND + '/api/admin/run-review-relink?limit=25'
        + (testMode ? '&test_email=' + encodeURIComponent(localStorage.getItem('admin_email') || 'trumpet95@gmail.com') : '');
      var r = await fetch(url, { method: 'POST', headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      if (d.skipped === 'preview_env') {
        setMsg({ type: 'error', text: 'Blocked: this is the preview site. Run it from jimendez.com/admin.' });
      } else if (testMode) {
        setMsg({ type: 'success', text: 'Sent ' + d.sent + ' sample email(s) to you only — nothing went to customers.' });
      } else {
        setMsg({ type: 'success', text: 'Re-sent to ' + d.sent + ' customers'
          + (d.skipped_optout ? ' (' + d.skipped_optout + ' unsubscribed)' : '')
          + (d.failed ? ', ' + d.failed + ' failed' : '')
          + '. ' + d.remaining + ' left — click again to continue.' });
        load();
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    }
    setBusy(false);
  }

  if (status && status.audience === 0 && !msg) return null;

  return React.createElement('div', { 'data-testid': 'review-relink-card', className: 'mb-4 bg-neutral-900 border border-amber-500/30 rounded-xl p-4 flex items-center gap-4 flex-wrap' },
    React.createElement('div', { className: 'flex-1 min-w-[220px]' },
      React.createElement('div', { className: 'text-white text-sm font-semibold' }, '🔗 Broken review links — repair send'),
      React.createElement('div', { className: 'text-neutral-500 text-xs mt-0.5' },
        status == null
          ? 'Checking which review emails had a dead product link\u2026'
          : status.audience + ' customers got a review email whose link opened a blank page. Re-sends in their language, 25 per click, each customer only once.'
      ),
      msg ? React.createElement('div', { className: 'mt-2 text-xs ' + (msg.type === 'error' ? 'text-red-400' : 'text-emerald-400') }, msg.text) : null
    ),
    React.createElement('button', {
      onClick: function() { run(true); }, disabled: busy,
      'data-testid': 'review-relink-test-btn',
      className: 'border border-white/20 text-white text-sm px-3 py-2 rounded-lg disabled:opacity-50'
    }, 'Send me a test'),
    React.createElement('button', {
      onClick: function() { run(false); }, disabled: busy || !status || !status.audience,
      'data-testid': 'review-relink-send-btn',
      className: 'bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50'
    }, busy ? 'Sending\u2026' : 'Re-send to customers')
  );
}


function FreeShippingCard() {
  var [status, setStatus] = useState({ active: false, mode: 'subtotal', min_usd: null, max_usd: 50 });
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [msg, setMsg] = useState(null);
  var [mode, setMode] = useState('subtotal');
  var [minUsd, setMinUsd] = useState('');
  var [maxUsd, setMaxUsd] = useState('50');

  async function load() {
    setLoading(true);
    try {
      var r = await fetch(BACKEND + '/api/admin/free-shipping', { headers: authHeaders() });
      var d = await r.json();
      setStatus(d);
      setMode(d.mode || 'subtotal');
      setMinUsd(d.min_usd != null ? String(d.min_usd) : '');
      setMaxUsd(d.max_usd != null ? String(d.max_usd) : '');
    } catch (e) { setMsg({ type: 'error', text: 'Could not load free-shipping campaign' }); }
    setLoading(false);
  }
  useEffect(function() { load(); }, []);

  function parseBound(v) {
    if (v === '' || v == null) return null;
    var n = Number(v);
    return isFinite(n) && n > 0 ? n : null;
  }

  async function save(nextActive) {
    setSaving(true); setMsg(null);
    try {
      var body = {
        active: nextActive,
        mode: mode,
        min_usd: parseBound(minUsd),
        max_usd: parseBound(maxUsd),
      };
      var resp = await fetch(BACKEND + '/api/admin/free-shipping', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify(body),
      });
      var d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || 'Failed');
      setStatus(d);
      var ruleText = describeRule(d);
      setMsg({ type: 'success', text: nextActive
        ? 'FREE SHIPPING is LIVE — ' + ruleText
        : 'FREE SHIPPING turned off. Rule saved: ' + ruleText });
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    setSaving(false);
  }

  function describeRule(s) {
    var lo = s.min_usd, hi = s.max_usd;
    var scope = s.mode === 'per_item' ? 'each item' : 'order subtotal';
    if (lo && hi) return scope + ' between $' + lo + ' and $' + hi;
    if (lo && !hi) return scope + ' at $' + lo + ' or above';
    if (!lo && hi) return scope + ' up to $' + hi;
    return scope + ' — no bounds set (every order qualifies)';
  }

  var isActive = !!status.active;
  var statusDot = React.createElement('span', {
    className: 'inline-block w-2 h-2 rounded-full ' + (isActive ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.8)]' : 'bg-neutral-600')
  });

  var modeBtnCls = function(v) {
    return 'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ' +
      (mode === v ? 'bg-emerald-500 text-black' : 'bg-black/40 text-neutral-400 hover:text-white border border-white/10');
  };

  var inputCls = 'w-20 bg-transparent text-white text-sm focus:outline-none';

  return React.createElement('div', { 'data-testid': 'admin-free-shipping', className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6 mt-6' },
    React.createElement('div', { className: 'flex items-center gap-2 mb-1' },
      React.createElement('span', null, '\ud83d\ude9a'),
      React.createElement('h2', { className: 'text-white text-lg font-semibold' }, 'Free shipping campaign'),
      React.createElement('span', { className: 'ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ' + (isActive ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-neutral-800 text-neutral-500 border border-neutral-700') },
        statusDot, ' ', isActive ? 'ACTIVE' : 'OFF'
      )
    ),
    React.createElement('p', { className: 'text-neutral-500 text-xs mb-4' },
      'A big "FREE SHIPPING" banner shows sitewide when active. Configure a min/max range and pick whether it applies to each item\u2019s price or the order subtotal. Leave a bound blank for no limit on that side.'
    ),

    // Mode toggle
    React.createElement('div', { className: 'mb-3' },
      React.createElement('div', { className: 'text-neutral-400 text-xs uppercase tracking-wider mb-1' }, 'Apply the range to'),
      React.createElement('div', { className: 'flex gap-2' },
        React.createElement('button', {
          type: 'button', 'data-testid': 'free-shipping-mode-subtotal',
          onClick: function() { setMode('subtotal'); },
          className: modeBtnCls('subtotal')
        }, 'Order subtotal'),
        React.createElement('button', {
          type: 'button', 'data-testid': 'free-shipping-mode-per-item',
          onClick: function() { setMode('per_item'); },
          className: modeBtnCls('per_item')
        }, 'Each item price')
      )
    ),

    // Min / Max
    React.createElement('div', { className: 'flex flex-wrap items-center gap-3 mb-3' },
      React.createElement('label', { className: 'text-neutral-400 text-xs uppercase tracking-wider' }, 'Min'),
      React.createElement('div', { className: 'flex items-center gap-1 bg-black/50 border border-white/10 rounded-lg px-3 py-1.5' },
        React.createElement('span', { className: 'text-neutral-500' }, '$'),
        React.createElement('input', {
          type: 'number', min: 0, step: 1, value: minUsd,
          placeholder: 'none',
          onChange: function(e) { setMinUsd(e.target.value); },
          'data-testid': 'free-shipping-min',
          className: inputCls, disabled: saving,
        })
      ),
      React.createElement('label', { className: 'text-neutral-400 text-xs uppercase tracking-wider ml-2' }, 'Max'),
      React.createElement('div', { className: 'flex items-center gap-1 bg-black/50 border border-white/10 rounded-lg px-3 py-1.5' },
        React.createElement('span', { className: 'text-neutral-500' }, '$'),
        React.createElement('input', {
          type: 'number', min: 0, step: 1, value: maxUsd,
          placeholder: 'none',
          onChange: function(e) { setMaxUsd(e.target.value); },
          'data-testid': 'free-shipping-max',
          className: inputCls, disabled: saving,
        })
      )
    ),

    // Live rule preview
    React.createElement('div', { className: 'text-emerald-300/80 text-xs mb-3' },
      'Rule: ', describeRule({ mode: mode, min_usd: parseBound(minUsd), max_usd: parseBound(maxUsd) })
    ),

    React.createElement('div', { className: 'flex flex-wrap items-center gap-2' },
      React.createElement('button', {
        onClick: function() { save(!isActive); },
        disabled: saving || loading,
        'data-testid': 'free-shipping-toggle',
        className: (isActive
          ? 'bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40'
          : 'bg-emerald-500 hover:bg-emerald-400 text-black') +
          ' font-semibold px-5 py-2 rounded-xl text-sm transition-all disabled:opacity-50'
      }, saving ? 'Saving\u2026' : (isActive ? 'Turn OFF free shipping' : 'Turn ON free shipping')),
      isActive ? React.createElement('button', {
        onClick: function() { save(true); },
        disabled: saving,
        'data-testid': 'free-shipping-save-rule',
        className: 'bg-white/5 hover:bg-white/10 text-white border border-white/10 font-semibold px-4 py-2 rounded-xl text-sm transition-all disabled:opacity-50'
      }, 'Save rule changes') : null
    ),
    msg ? React.createElement('div', {
      className: 'mt-3 text-xs px-3 py-2 rounded-lg ' + (msg.type === 'error'
        ? 'bg-red-500/10 text-red-300 border border-red-500/30'
        : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30')
    }, msg.text) : null
  );
}


function FlashDiscountsCard(props) {  var products = props.products || [];
  var [discounts, setDiscounts] = useState([]);
  var [loading, setLoading] = useState(true);
  var [msg, setMsg] = useState(null);
  var [slug, setSlug] = useState('');
  var [amountOff, setAmountOff] = useState('');
  var [percentOff, setPercentOff] = useState('');
  var [hours, setHours] = useState(24);
  var [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      var r = await fetch(BACKEND + '/api/admin/flash-discounts', { headers: authHeaders() });
      var d = await r.json();
      setDiscounts(d.discounts || []);
    } catch (e) { setMsg({ type: 'error', text: 'Could not load flash discounts' }); }
    setLoading(false);
  }
  useEffect(function() { load(); }, []);

  async function startSale() {
    if (!slug) { setMsg({ type: 'error', text: 'Pick a product' }); return; }
    var amt = parseFloat(amountOff) || 0;
    var pct = parseFloat(percentOff) || 0;
    if (amt <= 0 && pct <= 0) {
      setMsg({ type: 'error', text: 'Enter either $ off or % off (at least one must be > 0).' });
      return;
    }
    setSaving(true); setMsg(null);
    try {
      var resp = await fetch(BACKEND + '/api/admin/flash-discounts', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({
          slug: slug,
          amount_off: amt > 0 ? amt : null,
          percent_off: pct > 0 ? pct : null,
          expires_in_hours: hours,
        }),
      });
      var d = await resp.json();
      if (!resp.ok) throw new Error(d.detail || 'Failed');
      setMsg({ type: 'success', text: 'Sale started for ' + slug + ' (expires ' + new Date(d.expires_at).toLocaleString() + ')' });
      setAmountOff(''); setPercentOff(''); setSlug('');
      load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    setSaving(false);
  }

  async function clearSale(s) {
    if (!window.confirm('End the flash sale on ' + s + ' now?')) return;
    await fetch(BACKEND + '/api/admin/flash-discounts/' + encodeURIComponent(s), { method: 'DELETE', headers: authHeaders() });
    load();
  }

  function timeUntil(iso) {
    if (!iso) return '\u2014';
    var ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expired';
    var h = Math.floor(ms / 3600000);
    var m = Math.floor((ms % 3600000) / 60000);
    if (h >= 24) return Math.floor(h / 24) + 'd ' + (h % 24) + 'h';
    return h + 'h ' + m + 'm';
  }

  var presets = [
    { label: '24h', value: 24 },
    { label: '48h', value: 48 },
    { label: '7 days', value: 168 },
  ];

  return React.createElement('div', { 'data-testid': 'admin-flash-discounts', className: 'bg-neutral-900 border border-white/10 rounded-2xl p-6 mt-6' },
    React.createElement('div', { className: 'flex items-center gap-2 mb-1' },
      React.createElement('span', { className: 'text-[#d4af37]' }, '\u26a1'),
      React.createElement('h2', { className: 'text-white text-lg font-semibold' }, 'Flash discounts')
    ),
    React.createElement('p', { className: 'text-neutral-500 text-xs mb-4' },
      'Start a short-lived sale on any product. Customers see strikethrough pricing and a SALE badge. The server enforces the sale price at checkout \u2014 no bypass possible.'
    ),
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-6 gap-3' },
      React.createElement('select', {
        value: slug, onChange: function(e) { setSlug(e.target.value); },
        'data-testid': 'flash-product-select',
        className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2 md:col-span-2'
      },
        React.createElement('option', { value: '' }, '\u2014 product \u2014'),
        products.map(function(p) {
          return React.createElement('option', { key: p.slug, value: p.slug }, p.name + ' ($' + (p.originalPrice || p.price) + ')');
        })
      ),
      React.createElement('input', {
        value: amountOff, onChange: function(e) { setAmountOff(e.target.value); setPercentOff(''); },
        type: 'number', min: 0, step: '0.01', placeholder: '$ off',
        'data-testid': 'flash-amount-input',
        className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2'
      }),
      React.createElement('input', {
        value: percentOff, onChange: function(e) { setPercentOff(e.target.value); setAmountOff(''); },
        type: 'number', min: 0, max: 99, placeholder: '% off',
        'data-testid': 'flash-percent-input',
        className: 'bg-neutral-950 border border-white/10 text-white rounded-lg px-3 py-2'
      }),
      React.createElement('div', { className: 'flex gap-1' },
        presets.map(function(p) {
          return React.createElement('button', {
            key: p.value, type: 'button', onClick: function() { setHours(p.value); },
            'data-testid': 'flash-preset-' + p.label,
            className: 'flex-1 px-2 py-2 rounded-lg text-xs font-semibold ' + (hours === p.value ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700')
          }, p.label);
        })
      ),
      React.createElement('button', {
        onClick: startSale, disabled: saving,
        'data-testid': 'flash-start-btn',
        className: 'bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-50'
      }, saving ? 'Starting\u2026' : 'Start sale')
    ),
    msg ? React.createElement('div', { className: 'mt-3 text-sm ' + (msg.type === 'error' ? 'text-red-400' : 'text-emerald-400') }, msg.text) : null,
    React.createElement('div', { className: 'overflow-x-auto mt-6' },
      React.createElement('table', { className: 'w-full text-sm' },
        React.createElement('thead', null,
          React.createElement('tr', { className: 'text-left text-neutral-500 text-xs uppercase tracking-wider border-b border-white/10' },
            React.createElement('th', { className: 'p-2' }, 'Product'),
            React.createElement('th', { className: 'p-2' }, 'Discount'),
            React.createElement('th', { className: 'p-2' }, 'Expires in'),
            React.createElement('th', { className: 'p-2' }, '')
          )
        ),
        React.createElement('tbody', null,
          loading ? React.createElement('tr', null, React.createElement('td', { colSpan: 4, className: 'p-4 text-center text-neutral-500' }, 'Loading\u2026')) :
          discounts.length === 0 ? React.createElement('tr', null, React.createElement('td', { colSpan: 4, className: 'p-4 text-center text-neutral-600' }, 'No active flash sales.')) :
          discounts.map(function(d) {
            var prod = products.find(function(p) { return p.slug === d.slug; });
            var displayName = prod ? prod.name : d.slug;
            var discount = d.percent_off ? d.percent_off + '% off' : '$' + d.amount_off + ' off';
            return React.createElement('tr', { key: d.slug, className: 'border-b border-white/5', 'data-testid': 'flash-row-' + d.slug },
              React.createElement('td', { className: 'p-2 text-white' }, displayName, React.createElement('div', { className: 'text-neutral-500 text-xs' }, d.slug)),
              React.createElement('td', { className: 'p-2 text-[#d4af37] font-semibold' }, discount),
              React.createElement('td', { className: 'p-2 text-neutral-300 text-sm' }, timeUntil(d.expires_at)),
              React.createElement('td', { className: 'p-2 text-right' },
                React.createElement('button', {
                  onClick: function() { clearSale(d.slug); },
                  'data-testid': 'flash-clear-' + d.slug,
                  className: 'text-neutral-500 hover:text-red-400 text-xs'
                }, 'End sale')
              )
            );
          })
        )
      )
    )
  );
}
// Renders an SVG line chart comparing the most recent 3 years month-by-month.
// `series` is the `monthly_revenue` array from /api/admin/orders/analytics —
// each entry is { key: "YYYY-MM", revenue: 1234.56 }.
function YoYChart(props) {
  var series = props.series || [];
  // Group by year → 12-month array
  var byYear = {};
  for (var i = 0; i < series.length; i++) {
    var entry = series[i];
    var parts = entry.key.split('-');
    var y = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10) - 1;
    if (!byYear[y]) byYear[y] = new Array(12).fill(0);
    byYear[y][m] = entry.revenue;
  }
  var allYears = Object.keys(byYear).map(Number).sort();
  if (allYears.length === 0) return null;
  // Show only the most recent 3 years to keep the chart legible
  var years = allYears.slice(-3);

  // Find global max for shared y-axis scale
  var maxRevenue = 0;
  years.forEach(function(y) {
    byYear[y].forEach(function(v) { if (v > maxRevenue) maxRevenue = v; });
  });
  if (maxRevenue === 0) maxRevenue = 1;

  var width = 720;
  var height = 220;
  var padL = 50, padR = 16, padT = 16, padB = 28;
  var chartW = width - padL - padR;
  var chartH = height - padT - padB;
  // Color palette: most recent year in gold, prior in muted teal, oldest in dim grey
  var palette = ['#5f6471', '#3a7ca5', '#d4af37'];
  var months = ['J','F','M','A','M','J','J','A','S','O','N','D'];

  function pathFor(values) {
    var pts = values.map(function(v, i) {
      var x = padL + (chartW * i) / 11;
      var y = padT + chartH - (chartH * v) / maxRevenue;
      return (i === 0 ? 'M' : 'L') + x.toFixed(1) + ' ' + y.toFixed(1);
    });
    return pts.join(' ');
  }

  // Build totals per year for the legend
  var legendItems = years.map(function(y, idx) {
    var color = palette[palette.length - years.length + idx] || palette[0];
    var total = byYear[y].reduce(function(s, v) { return s + v; }, 0);
    return React.createElement('div', { key: y, 'data-testid': 'yoy-legend-' + y, className: 'flex items-center gap-2 text-sm' },
      React.createElement('span', { className: 'inline-block w-3 h-3 rounded-full', style: { background: color } }),
      React.createElement('span', { className: 'text-neutral-300 font-semibold' }, y),
      React.createElement('span', { className: 'text-neutral-500 text-xs' }, '$', total.toFixed(0))
    );
  });

  // Grid lines: 4 horizontal levels (0, 25%, 50%, 75%, 100%)
  var gridLines = [0, 0.25, 0.5, 0.75, 1].map(function(t, i) {
    var y = padT + chartH * (1 - t);
    return React.createElement('line', {
      key: i, x1: padL, x2: width - padR, y1: y, y2: y,
      stroke: '#1f1f1f', strokeWidth: 1
    });
  });
  // y-axis labels: show $ amounts at 0%, 50%, 100%
  var yLabels = [0, 0.5, 1].map(function(t, i) {
    var y = padT + chartH * (1 - t);
    return React.createElement('text', {
      key: i, x: padL - 8, y: y + 4, fill: '#666', fontSize: '10', textAnchor: 'end'
    }, '$' + Math.round(maxRevenue * t / 1000) + 'k');
  });
  // x-axis month labels
  var xLabels = months.map(function(m, i) {
    var x = padL + (chartW * i) / 11;
    return React.createElement('text', {
      key: i, x: x, y: height - 10, fill: '#666', fontSize: '10', textAnchor: 'middle'
    }, m);
  });
  // Lines, newest gets the gold color (last in palette)
  var lines = years.map(function(y, idx) {
    var color = palette[palette.length - years.length + idx] || palette[0];
    return React.createElement('path', {
      key: y, d: pathFor(byYear[y]),
      stroke: color, strokeWidth: idx === years.length - 1 ? 2.5 : 1.8,
      fill: 'none', strokeLinejoin: 'round', strokeLinecap: 'round',
      opacity: idx === years.length - 1 ? 1 : 0.8
    });
  });
  // Dots for the most-recent-year line
  var latestYear = years[years.length - 1];
  var dots = byYear[latestYear].map(function(v, i) {
    if (v === 0) return null;
    var x = padL + (chartW * i) / 11;
    var yy = padT + chartH - (chartH * v) / maxRevenue;
    return React.createElement('circle', {
      key: i, cx: x, cy: yy, r: 3, fill: '#d4af37'
    });
  });

  return React.createElement('div', { 'data-testid': 'yoy-chart', className: 'mb-6 bg-neutral-900 border border-white/10 rounded-xl p-4' },
    React.createElement('div', { className: 'flex items-center justify-between flex-wrap gap-3 mb-3' },
      React.createElement('h3', { className: 'text-neutral-400 text-xs uppercase tracking-wider' }, 'Revenue — year over year'),
      React.createElement('div', { className: 'flex gap-4 flex-wrap' }, legendItems)
    ),
    React.createElement('svg', { viewBox: '0 0 ' + width + ' ' + height, width: '100%', height: 'auto', preserveAspectRatio: 'xMidYMid meet' },
      gridLines, yLabels, xLabels, lines, dots
    )
  );
}


function AdminDashboardPage() {
  var navigate = useNavigate();
  var [orders, setOrders] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selected, setSelected] = useState(null);
  var [tab, setTab] = useState('orders');
  var email = localStorage.getItem('admin_email') || '';

  async function loadOrders() {
    setLoading(true);
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders', { headers: authHeaders() });
      if (resp.status === 401) { handleLogout(); return; }
      var data = await resp.json();
      setOrders(data.orders || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  // One-click backfill of romanized name/address on every past order. Calls the
  // admin endpoint, then re-loads the table. Shows the counts via toast/alert.
  var [backfilling, setBackfilling] = useState(false);
  // Wix CSV import (one-shot)
  var [importing, setImporting] = useState(false);
  var importInputRef = React.useRef(null);
  // Sales analytics summary (lifetime KPIs + top products)
  var [analytics, setAnalytics] = useState(null);

  useEffect(function() {
    fetch(BACKEND + '/api/admin/orders/analytics', { headers: authHeaders() })
      .then(function(r) { return r.ok ? r.json() : null; })
      .then(function(d) { if (d) setAnalytics(d); })
      .catch(function() {});
  }, [orders.length]);

  async function backfillRomanize() {
    if (backfilling) return;
    if (!window.confirm('Romanize Japanese / Mandarin / Cantonese / Korean / Cyrillic / etc. names and addresses on all past orders? This will use the Emergent LLM key. Safe to run multiple times — already-romanized orders are skipped.')) return;
    setBackfilling(true);
    try {
      var resp = await fetch(BACKEND + '/api/admin/orders/backfill-romanize', { method: 'POST', headers: authHeaders() });
      if (resp.status === 401) { handleLogout(); return; }
      var data = await resp.json();
      window.alert('Backfill complete:\n• Processed: ' + data.processed + '\n• Updated: ' + data.updated + '\n• Already Latin: ' + data.skipped_latin + '\n• Already romanized: ' + data.skipped_already_romanized + '\n• Errors: ' + data.errors);
      await loadOrders();
    } catch (e) { console.error(e); window.alert('Backfill failed: ' + (e && e.message ? e.message : e)); }
    finally { setBackfilling(false); }
  }

  // Wix CSV import handler — POSTs the selected file to the backend.
  async function importWixCsv(e) {
    var f = e && e.target && e.target.files && e.target.files[0];
    if (!f) return;
    if (importing) return;
    // Ask whether to UPDATE existing orders or just skip them. Updating is
    // the right choice after the parser has been improved (e.g. country codes).
    var doUpdate = window.confirm(
      'Import "' + f.name + '"?\n\n' +
      'OK = Import + also UPDATE existing orders (fixes country codes & addresses on re-import).\n' +
      'Cancel = Just skip already-imported orders.'
    );
    setImporting(true);
    try {
      var fd = new FormData();
      fd.append('file', f);
      var url = BACKEND + '/api/admin/orders/import-wix-csv' + (doUpdate ? '?update_existing=true' : '');
      var resp = await fetch(url, { method: 'POST', headers: authHeaders(), body: fd });
      if (resp.status === 401) { handleLogout(); return; }
      var data = await resp.json();
      window.alert('Wix import complete:\n• CSV rows: ' + data.rows_in_csv +
        '\n• Unique orders in file: ' + data.unique_orders +
        '\n• Imported (new): ' + data.imported +
        '\n• Updated (existing): ' + (data.updated || 0) +
        '\n• Skipped (already in DB): ' + data.skipped_existing +
        '\n• Auto-romanized: ' + data.romanized +
        '\n• Errors: ' + data.errors);
      await loadOrders();
    } catch (err) { window.alert('Import failed: ' + (err && err.message ? err.message : err)); }
    finally { setImporting(false); e.target.value = ''; }
  }


  // Reconcile Stripe — recovers any Apple Pay / Google Pay / card payments that
  // succeeded at Stripe but never landed in our orders collection (because the
  // customer's browser closed before the post-payment poll completed).
  var [reconciling, setReconciling] = useState(false);
  async function reconcileStripe() {
    if (reconciling) return;
    if (!window.confirm(
      'Reconcile Stripe payments?\n\n' +
      'This will scan the last 30 days of SUCCEEDED Stripe payments and create ' +
      'any missing orders in your admin.\n\n' +
      'I will run a DRY-RUN first (no changes) and show you the report.'
    )) return;
    setReconciling(true);
    try {
      var dr = await fetch(BACKEND + '/api/checkout/stripe/reconcile', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ days_back: 30, dry_run: true })
      });
      if (dr.status === 401) { handleLogout(); return; }
      var preview = await dr.json();
      var msg = 'STRIPE RECONCILE — DRY RUN\n\n' +
        '• Scanned succeeded payments: ' + preview.scanned + '\n' +
        '• Already in orders DB:       ' + preview.already_finalized + '\n' +
        '• Would create new orders:    ' + preview.newly_created + '\n' +
        '• Missing cart info (manual): ' + preview.no_txn_record + '\n\n';
      if (preview.newly_created === 0 && preview.no_txn_record === 0) {
        window.alert(msg + 'Nothing to do — every Stripe success is already in your admin.');
        return;
      }
      if (!window.confirm(msg + 'Proceed to actually create the ' + preview.newly_created + ' missing order(s)?')) return;
      var rr = await fetch(BACKEND + '/api/checkout/stripe/reconcile', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ days_back: 30, dry_run: false })
      });
      var report = await rr.json();
      var manualList = (report.details || []).filter(function(d) { return d.action === 'MANUAL — no cart info on file'; });
      var manualNote = manualList.length ? '\n\n⚠️ ' + manualList.length + ' payment(s) succeeded at Stripe but we have NO cart info on file — these need manual entry. See browser console.' : '';
      if (manualList.length) console.warn('Stripe payments with no cart info on file:', manualList);
      window.alert(
        'STRIPE RECONCILE — COMPLETE\n\n' +
        '• Newly created orders: ' + report.newly_created + '\n' +
        '• Already finalized:    ' + report.already_finalized + '\n' +
        '• Errors:               ' + report.errors + manualNote
      );
      await loadOrders();
    } catch (err) {
      window.alert('Reconcile failed: ' + (err && err.message ? err.message : err));
    } finally {
      setReconciling(false);
    }
  }

  useEffect(function() {
    if (!localStorage.getItem('admin_token')) { navigate('/admin/login'); return; }
    loadOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLogout() {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  }

  function handleUpdate() {
    loadOrders();
    if (selected) {
      // refresh single order
      fetch(BACKEND + '/api/admin/orders/' + selected.order_number, { headers: authHeaders() })
        .then(function(r) { return r.json(); })
        .then(function(o) { setSelected(o); });
    }
  }

  // Active country filter — click a country pill to show only orders from that country.
  // Click the same pill again (or "Clear" pseudo-button) to remove the filter.
  var [countryFilter, setCountryFilter] = useState(null);
  // Set from the search bar: narrows the table to one customer or one product.
  var [searchFilter, setSearchFilter] = useState(null);

  // Apply the country filter to the orders table only. KPIs, monthly cards
  // and country pill counts stay GLOBAL so they always reflect the full DB.
  var visibleOrders = countryFilter
    ? orders.filter(function(o) {
        var cc = (o.shipping_address && o.shipping_address.country_code_alpha2 || '').toUpperCase();
        if (countryFilter === '—') return !cc;
        return cc === countryFilter;
      })
    : orders;

  if (searchFilter) {
    visibleOrders = visibleOrders.filter(function(o) {
      if (searchFilter.kind === 'customer') {
        return (o.customer_email || '').toLowerCase() === searchFilter.value.toLowerCase();
      }
      // Product: the backend resolves legacy slugs, so trust its order list.
      if (searchFilter.numbers) {
        return searchFilter.numbers.indexOf(o.order_number) !== -1;
      }
      return (o.items || []).some(function(it) { return (it.slug || '') === searchFilter.value; });
    });
  }

  var rows = [];
  for (var i = 0; i < visibleOrders.length; i++) {
    rows.push(React.createElement(OrderRow, { key: visibleOrders[i].order_number, order: visibleOrders[i], onOpen: setSelected, onMarkDelivered: loadOrders }));
  }

  if (!rows.length && searchFilter && !loading) {
    rows.push(React.createElement('div', {
      key: 'empty', 'data-testid': 'orders-empty-state',
      className: 'bg-neutral-900 border border-white/10 rounded-xl p-6 text-center text-neutral-400 text-sm'
    }, searchFilter.kind === 'product'
      ? 'Nobody has ordered \u201C' + searchFilter.label + '\u201D yet.'
      : 'No orders from ' + searchFilter.label + ' in the current view.'));
  }

  // ----- Monthly sales summary (paid + shipped + delivered, excluding refunded/cancelled) -----
  // Each month bucket also tracks per-country counts so we can show a flag row
  // ON TOP of the month label and the total at the BOTTOM (per Isaias, May 2026).
  var monthBuckets = {};
  for (var mi = 0; mi < orders.length; mi++) {
    var mo = orders[mi];
    if (mo.status === 'refunded' || mo.status === 'cancelled') continue;
    var dt = new Date(mo.created_at);
    if (isNaN(dt.getTime())) continue;
    var key = dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0');
    if (!monthBuckets[key]) monthBuckets[key] = {
      total: 0, count: 0, countries: {},
      label: dt.toLocaleString('en-US', { month: 'short', year: 'numeric' })
    };
    monthBuckets[key].total += (mo.total || 0);
    monthBuckets[key].count += 1;
    var mcc = (mo.shipping_address && mo.shipping_address.country_code_alpha2 || '').toUpperCase();
    if (mcc) monthBuckets[key].countries[mcc] = (monthBuckets[key].countries[mcc] || 0) + 1;
  }
  var monthKeys = Object.keys(monthBuckets).sort().reverse();  // newest first
  var monthCards = monthKeys.slice(0, 24).map(function(k) {
    var b = monthBuckets[k];
    // Per-month country pills (sorted by count desc)
    var perMonthCountries = Object.keys(b.countries).map(function(cc) {
      return { code: cc, count: b.countries[cc] };
    }).sort(function(a, c) { return c.count - a.count; });
    var monthFlags = perMonthCountries.map(function(e) {
      return React.createElement('span', { key: e.code, className: 'inline-flex items-center gap-1 text-xs whitespace-nowrap' },
        React.createElement('span', { className: 'text-base leading-none' }, flagEmoji(e.code)),
        React.createElement('span', { className: 'text-neutral-300 font-semibold' }, e.count)
      );
    });
    return React.createElement('div', {
      key: k,
      'data-testid': 'monthly-sales-' + k,
      className: 'bg-neutral-900 border border-white/10 rounded-xl px-4 py-3 min-w-[180px] flex flex-col gap-2'
    },
      // TOP: country flags row
      React.createElement('div', { className: 'flex flex-wrap gap-2 min-h-[20px]' }, monthFlags.length ? monthFlags : React.createElement('span', { className: 'text-neutral-700 text-xs' }, '—')),
      // MIDDLE: month label + order count
      React.createElement('div', null,
        React.createElement('div', { className: 'text-neutral-500 text-xs uppercase tracking-wider' }, b.label),
        React.createElement('div', { className: 'text-neutral-400 text-xs mt-0.5' }, b.count, ' order', b.count === 1 ? '' : 's')
      ),
      // BOTTOM: total sales for the month
      React.createElement('div', { className: 'text-[#d4af37] text-xl font-bold border-t border-white/5 pt-2' }, '$', b.total.toFixed(2))
    );
  });

  // ----- Orders by country (active orders only; excludes refunded/cancelled) -----
  // ISO-3166-1-alpha-2 → flag emoji + display name. Only orders with a
  // shipping_address count (digital-only orders without an address are
  // bucketed as "—").
  var COUNTRY_NAMES = {
    US: 'United States', MX: 'Mexico', CA: 'Canada', JP: 'Japan', DE: 'Germany',
    FR: 'France', IT: 'Italy', ES: 'Spain', GB: 'United Kingdom', UK: 'United Kingdom',
    BR: 'Brazil', AR: 'Argentina', CL: 'Chile', CO: 'Colombia', PE: 'Peru',
    UA: 'Ukraine', PL: 'Poland', NL: 'Netherlands', BE: 'Belgium', SE: 'Sweden',
    NO: 'Norway', FI: 'Finland', DK: 'Denmark', AT: 'Austria', CH: 'Switzerland',
    AU: 'Australia', NZ: 'New Zealand', KR: 'South Korea', CN: 'China', TW: 'Taiwan',
    HK: 'Hong Kong', SG: 'Singapore', TH: 'Thailand', IN: 'India', ID: 'Indonesia',
    PH: 'Philippines', VN: 'Vietnam', MY: 'Malaysia', AE: 'UAE', SA: 'Saudi Arabia',
    IL: 'Israel', TR: 'Türkiye', RU: 'Russia', PT: 'Portugal', IE: 'Ireland',
    CZ: 'Czechia', HU: 'Hungary', RO: 'Romania', GR: 'Greece', ZA: 'South Africa',
    EG: 'Egypt', NG: 'Nigeria', KE: 'Kenya'
  };
  function flagEmoji(cc) {
    if (!cc || cc.length !== 2) return '🌐';
    var A = 0x1F1E6, base = 'A'.charCodeAt(0);
    return String.fromCodePoint(A + (cc.charCodeAt(0) - base)) + String.fromCodePoint(A + (cc.charCodeAt(1) - base));
  }
  var countryBuckets = {};
  for (var ci = 0; ci < orders.length; ci++) {
    var co = orders[ci];
    if (co.status === 'refunded' || co.status === 'cancelled') continue;
    var cc = (co.shipping_address && co.shipping_address.country_code_alpha2 || '').toUpperCase();
    if (!cc) cc = '—';
    if (!countryBuckets[cc]) countryBuckets[cc] = 0;
    countryBuckets[cc] += 1;
  }
  var countryEntries = Object.keys(countryBuckets).map(function(k) {
    return { code: k, count: countryBuckets[k] };
  }).sort(function(a, b) { return b.count - a.count; });
  var countryPills = countryEntries.map(function(entry) {
    var label = entry.code === '—' ? 'No address (digital)' : (COUNTRY_NAMES[entry.code] || entry.code);
    var active = countryFilter === entry.code;
    return React.createElement('button', {
      type: 'button',
      key: entry.code,
      'data-testid': 'orders-by-country-' + entry.code,
      onClick: function() { setCountryFilter(active ? null : entry.code); },
      title: active ? 'Click to clear filter' : 'Show only orders from ' + label,
      className: 'flex items-center gap-2 rounded-full pl-3 pr-1 py-1 whitespace-nowrap transition-colors cursor-pointer ' +
        (active
          ? 'bg-[#d4af37]/20 border border-[#d4af37] '
          : 'bg-neutral-900 border border-white/10 hover:border-[#d4af37]/40 ')
    },
      entry.code !== '—' ? React.createElement('span', { className: 'text-lg leading-none' }, flagEmoji(entry.code)) : React.createElement('span', { className: 'text-neutral-500 text-lg' }, '🌐'),
      React.createElement('span', { className: 'text-sm ' + (active ? 'text-white font-semibold' : 'text-neutral-300') }, label),
      React.createElement('span', { className: 'bg-[#d4af37] text-black text-xs font-bold rounded-full min-w-[1.5rem] px-2 py-0.5 text-center' }, entry.count)
    );
  });
  // Prepend a "Clear filter" pill when a filter is active
  if (countryFilter) {
    countryPills.unshift(React.createElement('button', {
      type: 'button',
      key: '__clear__',
      'data-testid': 'orders-by-country-clear',
      onClick: function() { setCountryFilter(null); },
      className: 'flex items-center gap-2 bg-neutral-800 border border-white/10 hover:border-red-500/40 rounded-full px-3 py-1 whitespace-nowrap cursor-pointer transition-colors'
    },
      React.createElement('span', { className: 'text-red-400 text-sm' }, '✕ Clear filter')
    ));
  }

  return React.createElement('div', { className: 'min-h-screen bg-black', 'data-testid': 'admin-dashboard' },
    React.createElement('header', { className: 'bg-neutral-950 border-b border-white/10 px-6 py-4 flex items-center justify-between' },
      React.createElement(Link, { to: '/', className: 'text-[#d4af37] font-bold' }, 'JiMendez.com Admin'),
      React.createElement('div', { className: 'flex items-center gap-3' },
        React.createElement('span', { className: 'text-neutral-500 text-sm hidden sm:inline' }, email),
        React.createElement('button', { onClick: handleLogout, 'data-testid': 'admin-logout-btn', className: 'flex items-center gap-1 text-neutral-400 hover:text-white text-sm' },
          React.createElement(LogOut, { className: 'w-4 h-4' }), 'Logout'
        )
      )
    ),
    React.createElement('div', { className: 'max-w-7xl mx-auto px-6 py-8' },
      React.createElement('div', { className: 'flex items-center gap-3 mb-6' },
        React.createElement('button', { onClick: function() { setTab('orders'); }, 'data-testid': 'tab-orders', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'orders' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Package, { className: 'w-4 h-4 inline mr-1' }), 'Orders ', React.createElement('span', { className: 'text-xs opacity-70' }, '(' + orders.length + ')')
        ),
        React.createElement('button', { onClick: function() { setTab('images'); }, 'data-testid': 'tab-images', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'images' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(ImageIcon, { className: 'w-4 h-4 inline mr-1' }), 'Product Images'
        ),
        React.createElement('button', { onClick: function() { setTab('catalog'); }, 'data-testid': 'tab-catalog', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'catalog' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Boxes, { className: 'w-4 h-4 inline mr-1' }), 'Catalog'
        ),
        React.createElement('button', { onClick: function() { setTab('stock'); }, 'data-testid': 'tab-stock', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'stock' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Boxes, { className: 'w-4 h-4 inline mr-1' }), 'Stock'
        ),
        React.createElement('button', { onClick: function() { setTab('descriptions'); }, 'data-testid': 'tab-descriptions', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'descriptions' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(FileText, { className: 'w-4 h-4 inline mr-1' }), 'Copy & Translations'
        ),
        React.createElement('button', { onClick: function() { setTab('abandoned'); }, 'data-testid': 'tab-abandoned', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'abandoned' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(ShoppingCart, { className: 'w-4 h-4 inline mr-1' }), 'Abandoned carts'
        ),
        React.createElement('button', { onClick: function() { setTab('promotions'); }, 'data-testid': 'tab-promotions', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'promotions' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Mail, { className: 'w-4 h-4 inline mr-1' }), 'Promotions'
        ),
        React.createElement('button', { onClick: function() { setTab('export'); }, 'data-testid': 'tab-export', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'export' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Download, { className: 'w-4 h-4 inline mr-1' }), 'Export'
        ),
        React.createElement('button', { onClick: function() { setTab('subscribers'); }, 'data-testid': 'tab-subscribers', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'subscribers' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Mail, { className: 'w-4 h-4 inline mr-1' }), 'Subscribers'
        ),
        React.createElement('button', { onClick: function() { setTab('neworder'); }, 'data-testid': 'tab-neworder', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'neworder' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Plus, { className: 'w-4 h-4 inline mr-1' }), 'Take an order'
        ),
        React.createElement('button', { onClick: function() { setTab('presales'); }, 'data-testid': 'tab-presales', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'presales' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Clock, { className: 'w-4 h-4 inline mr-1' }), 'Pre-orders'
        ),
        React.createElement('button', { onClick: function() { setTab('sources'); }, 'data-testid': 'tab-sources', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'sources' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(TrendingUp, { className: 'w-4 h-4 inline mr-1' }), 'Sources'
        ),
        React.createElement('button', { onClick: function() { setTab('reviews'); }, 'data-testid': 'tab-reviews', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'reviews' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Star, { className: 'w-4 h-4 inline mr-1' }), 'Reviews'
        ),
        React.createElement('button', { onClick: function() { setTab('files'); }, 'data-testid': 'tab-files', className: 'px-4 py-2 rounded-lg font-semibold ' + (tab === 'files' ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400') },
          React.createElement(Download, { className: 'w-4 h-4 inline mr-1' }), 'Digital Files'
        ),
        React.createElement('button', { onClick: loadOrders, className: 'ml-auto text-neutral-400 hover:text-white p-2' }, React.createElement(RefreshCw, { className: 'w-4 h-4' + (loading ? ' animate-spin' : '') }))
      ),
      tab === 'orders' ?
        React.createElement('div', null,
          React.createElement(AdminSearchBar, { onOpenOrder: setSelected, onFilter: setSearchFilter }),
          searchFilter ? React.createElement('div', {
            'data-testid': 'admin-search-active-filter',
            className: 'mb-4 flex items-center gap-2 text-sm text-neutral-300'
          },
            React.createElement('span', { className: 'bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] rounded-full px-3 py-1' },
              (searchFilter.kind === 'customer' ? 'Customer: ' : 'Bought: ') + searchFilter.label),
            React.createElement('span', { className: 'text-neutral-500' }, visibleOrders.length + ' of ' + orders.length + ' orders'),
            React.createElement('button', {
              onClick: function() { setSearchFilter(null); },
              'data-testid': 'admin-search-filter-clear',
              className: 'text-neutral-400 hover:text-white underline'
            }, 'Clear')
          ) : null,
          // ----- "Send pending follow-ups" backlog drainer -----
          React.createElement(FollowupDripCard, null),
          React.createElement(ReviewDripCard, null),
          // ----- Lifetime KPI tiles + Top products (Wix-style analytics) -----
          analytics ? React.createElement('div', { 'data-testid': 'analytics-summary', className: 'mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3' },
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-3' },
              React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Lifetime revenue'),
              React.createElement('div', { className: 'text-[#d4af37] text-xl font-bold mt-1' }, '$', (analytics.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
            ),
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-3' },
              React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Total orders'),
              React.createElement('div', { className: 'text-white text-xl font-bold mt-1' }, analytics.total_orders)
            ),
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-3' },
              React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Avg order value'),
              React.createElement('div', { className: 'text-white text-xl font-bold mt-1' }, '$', (analytics.average_order_value || 0).toFixed(2))
            ),
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-3' },
              React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Unique customers'),
              React.createElement('div', { className: 'text-white text-xl font-bold mt-1' }, analytics.unique_customers)
            ),
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-3' },
              React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Repeat rate'),
              React.createElement('div', { className: 'text-white text-xl font-bold mt-1' }, Math.round((analytics.repeat_rate || 0) * 100), '%')
            ),
            React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-3' },
              React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Countries reached'),
              React.createElement('div', { className: 'text-white text-xl font-bold mt-1' }, analytics.countries_reached)
            )
          ) : null,
          analytics && analytics.top_products && analytics.top_products.length ? React.createElement('div', { 'data-testid': 'top-products', className: 'mb-6 bg-neutral-900 border border-white/10 rounded-xl p-4' },
            React.createElement('h3', { className: 'text-neutral-400 text-xs uppercase tracking-wider mb-3' }, 'Top sellers (lifetime)'),
            React.createElement('ol', { className: 'space-y-1.5 list-decimal list-inside' },
              analytics.top_products.map(function(p, i) {
                return React.createElement('li', { key: i, className: 'text-sm flex items-baseline justify-between gap-2' },
                  React.createElement('span', { className: 'text-white' }, p.name),
                  React.createElement('span', { className: 'text-[#d4af37] font-semibold whitespace-nowrap' }, p.quantity, ' units')
                );
              })
            )
          ) : null,
          // Year-over-year revenue chart (latest 3 years)
          analytics && analytics.monthly_revenue && analytics.monthly_revenue.length ? React.createElement(YoYChart, { series: analytics.monthly_revenue }) : null,
          // Monthly sales summary
          monthCards.length > 0 ? React.createElement('div', { 'data-testid': 'monthly-sales-summary', className: 'mb-6' },
            React.createElement('div', { className: 'flex items-center justify-between mb-3 flex-wrap gap-2' },
              React.createElement('h2', { className: 'text-neutral-400 text-sm uppercase tracking-wider' }, 'Total sales by month'),
              React.createElement('div', { className: 'flex gap-2' },
                React.createElement('input', { type: 'file', accept: '.csv', ref: importInputRef, onChange: importWixCsv, style: { display: 'none' }, 'data-testid': 'admin-wix-import-input' }),
                React.createElement('button', {
                  onClick: function() { if (importInputRef.current) importInputRef.current.click(); },
                  disabled: importing,
                  'data-testid': 'admin-import-wix-csv-btn',
                  className: 'text-xs bg-neutral-900 hover:bg-neutral-800 border border-[#d4af37]/30 text-[#d4af37] px-3 py-1.5 rounded-lg disabled:opacity-50',
                  title: 'Import historical orders from a Wix CSV export'
                }, importing ? 'Importing…' : '📥 Import Wix CSV'),
                React.createElement('button', {
                  onClick: reconcileStripe,
                  disabled: reconciling,
                  'data-testid': 'admin-reconcile-stripe-btn',
                  className: 'text-xs bg-neutral-900 hover:bg-neutral-800 border border-[#d4af37]/30 text-[#d4af37] px-3 py-1.5 rounded-lg disabled:opacity-50',
                  title: 'Find Apple Pay / Google Pay / card payments that succeeded at Stripe but are missing from your admin orders list'
                }, reconciling ? 'Reconciling…' : '🔁 Reconcile Stripe (last 30 days)'),
                React.createElement('button', {
                  onClick: async function() {
                    if (reconciling) return;
                    setReconciling(true);
                    try {
                      var r = await fetch(BACKEND + '/api/checkout/stripe/verify-orders', {
                        method: 'POST',
                        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
                        body: JSON.stringify({ days_back: 30, dry_run: true })
                      });
                      if (r.status === 401) { handleLogout(); return; }
                      var d = await r.json();
                      var lines = (d.issues || []).slice(0, 15).map(function(i) {
                        return '• ' + i.order_number + ' (' + (i.customer || '—') + '): ' + i.issue;
                      });
                      console.log('Stripe verification full report:', d);
                      window.alert(
                        'STRIPE VERIFICATION — LAST 30 DAYS\n\n' +
                        '• Checked: ' + d.checked + '\n' +
                        '• ✅ OK (money captured): ' + d.ok + '\n' +
                        '• ❌ Not succeeded:        ' + d.not_succeeded + '\n' +
                        '• ❌ Not captured:         ' + d.not_captured + '\n' +
                        '• ❌ Amount mismatch:      ' + d.amount_mismatch + '\n' +
                        '• ⚠️ Errors:              ' + d.errors +
                        (lines.length ? '\n\nFirst issues:\n' + lines.join('\n') : '')
                      );
                    } catch (err) {
                      window.alert('Verify failed: ' + (err && err.message ? err.message : err));
                    } finally {
                      setReconciling(false);
                    }
                  },
                  disabled: reconciling,
                  'data-testid': 'admin-verify-stripe-btn',
                  className: 'text-xs bg-neutral-900 hover:bg-neutral-800 border border-[#d4af37]/30 text-[#d4af37] px-3 py-1.5 rounded-lg disabled:opacity-50',
                  title: 'For each order with a Stripe intent: ask Stripe if the money was actually captured'
                }, reconciling ? '…' : '✅ Verify Stripe money'),
                React.createElement('button', {
                  onClick: async function() {
                    var raw = window.prompt(
                      'Recover a stranded Stripe order.\n\n' +
                      'Paste EITHER a Checkout Session id (cs_...) OR a Payment Intent id (pi_...) from your Stripe dashboard.\n\n' +
                      'Use this when a customer paid on Stripe but never returned to /checkout so the order was never created.'
                    );
                    if (!raw) return;
                    var id = raw.trim();
                    if (!(id.startsWith('cs_') || id.startsWith('pi_'))) {
                      window.alert('id must start with "cs_" (Checkout Session) or "pi_" (Payment Intent).');
                      return;
                    }
                    try {
                      var r = await fetch(BACKEND + '/api/admin/orders/recover-from-stripe', {
                        method: 'POST',
                        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
                        body: JSON.stringify({ stripe_id: id })
                      });
                      if (r.status === 401) { handleLogout(); return; }
                      var d = await r.json();
                      if (!r.ok) {
                        window.alert('Recovery failed: ' + (d.detail || JSON.stringify(d)));
                        return;
                      }
                      var msg = d.recovered
                        ? '✅ Order ' + d.order_number + ' recovered for ' + (d.customer_email || '—') + ' ($' + (d.total || 0) + ')'
                        : 'ℹ️ Already had order ' + d.order_number + ' for ' + (d.customer_email || '—');
                      if (d.needs_admin_fill) msg += '\n\n⚠️ No cart info on file — open the order and fill in shipping manually.';
                      window.alert(msg);
                      await loadOrders();
                    } catch (err) {
                      window.alert('Recovery failed: ' + (err && err.message ? err.message : err));
                    }
                  },
                  'data-testid': 'admin-recover-stripe-btn',
                  className: 'text-xs bg-neutral-900 hover:bg-neutral-800 border border-[#d4af37]/30 text-[#d4af37] px-3 py-1.5 rounded-lg',
                  title: 'Manually finalize a single stranded Stripe payment by pasting its cs_ or pi_ id'
                }, '🛟 Recover Stripe order'),
                React.createElement('button', {
                  onClick: backfillRomanize,
                  disabled: backfilling,
                  'data-testid': 'admin-backfill-romanize-btn',
                  className: 'text-xs bg-neutral-900 hover:bg-neutral-800 border border-[#d4af37]/30 text-[#d4af37] px-3 py-1.5 rounded-lg disabled:opacity-50',
                  title: 'Romanize Japanese / Cyrillic / etc. names + addresses on existing past orders'
                }, backfilling ? 'Romanizing…' : '🌐 Backfill romanized addresses')
              )
            ),
            React.createElement('div', { className: 'flex gap-3 overflow-x-auto pb-2' }, monthCards)
          ) : null,
          // Orders by country
          countryPills.length > 0 ? React.createElement('div', { 'data-testid': 'orders-by-country-summary', className: 'mb-6' },
            React.createElement('h2', { className: 'text-neutral-400 text-sm uppercase tracking-wider mb-3' }, 'Orders by country'),
            React.createElement('div', { className: 'flex flex-wrap gap-2' }, countryPills)
          ) : null,
          React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-2xl overflow-x-auto' },
            React.createElement('table', { className: 'w-full min-w-[900px]' },
              React.createElement('thead', { className: 'bg-neutral-950' },
                React.createElement('tr', { className: 'text-left text-neutral-500 text-xs uppercase tracking-wider' },
                  React.createElement('th', { className: 'p-3' }, 'Order #'),
                  React.createElement('th', { className: 'p-3' }, 'Customer'),
                  React.createElement('th', { className: 'p-3' }, 'Location'),
                  React.createElement('th', { className: 'p-3' }, 'Date'),
                  React.createElement('th', { className: 'p-3' }, 'Items ordered'),
                  React.createElement('th', { className: 'p-3' }, 'Payment'),
                  React.createElement('th', { className: 'p-3' }, 'Total'),
                  React.createElement('th', { className: 'p-3' }, 'Status'),
                  React.createElement('th', { className: 'p-3 text-right' }, 'Quick action')
                )
              ),
              React.createElement('tbody', null,
                orders.length === 0 && !loading ?
                  React.createElement('tr', null, React.createElement('td', { colSpan: 9, className: 'p-8 text-center text-neutral-600' }, countryFilter ? 'No orders match the current filter.' : 'No orders yet')) :
                  rows
              )
            )
          )
        )
      : tab === 'catalog' ? React.createElement(CatalogPanel, null)
      : tab === 'neworder' ? React.createElement(ManualOrderPanel, null)
      : tab === 'presales' ? React.createElement(PresalePanel, null)
      : tab === 'reviews' ? React.createElement('div', null,
          React.createElement(ReviewRelinkCard, null),
          React.createElement(ReviewsPanel, null)
        )
      : tab === 'sources' ? React.createElement(SourcesPanel, null)
      : tab === 'stock' ? React.createElement(StockPanel, null)
      : tab === 'descriptions' ? React.createElement(DescriptionsPanel, null)
      : tab === 'abandoned' ? React.createElement(AbandonedCartsPanel, null)
      : tab === 'promotions' ? React.createElement(PromotionsPanel, null)
      : tab === 'export' ? React.createElement(ExportPanel, null)
      : tab === 'subscribers' ? React.createElement(SubscribersPanel, null)
      : tab === 'files' ? React.createElement(DropboxFilesPanel, null)
      : React.createElement(ImagesPanel, null)
    ),
    selected ? React.createElement(OrderDetailModal, { order: selected, onClose: function() { setSelected(null); }, onUpdate: handleUpdate }) : null
  );
}

// ---------------------------------------------------------------------------
// AbandonedCartsPanel — lists in-progress carts that customers left without
// completing checkout, so the admin can review what they were buying and
// optionally fire the 24h recovery email NOW instead of waiting.
// ---------------------------------------------------------------------------
function AbandonedCartsPanel() {
  var [carts, setCarts] = useState([]);
  var [total, setTotal] = useState(0);
  var [loading, setLoading] = useState(false);
  var [filterMode, setFilterMode] = useState('open'); // 'open' | 'recovered' | 'all'
  var [busy, setBusy] = useState({});
  var [msg, setMsg] = useState('');

  async function load() {
    setLoading(true);
    try {
      var q = '?limit=200';
      if (filterMode === 'open') q += '&converted=false&recovery_sent=false';
      if (filterMode === 'recovered') q += '&converted=false&recovery_sent=true';
      var r = await fetch(BACKEND + '/api/admin/abandoned-carts' + q, { headers: authHeaders() });
      var d = await r.json();
      setCarts(d.carts || []);
      setTotal(d.total || 0);
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setLoading(false); }
  }

  useEffect(function() { load(); /* eslint-disable-next-line */ }, [filterMode]);

  async function handleSendRecovery(email) {
    if (!window.confirm('Send the recovery email to ' + email + ' now?')) return;
    setBusy(function(b) { var n = Object.assign({}, b); n[email] = true; return n; });
    setMsg('');
    try {
      var r = await fetch(BACKEND + '/api/admin/abandoned-carts/' + encodeURIComponent(email) + '/send-recovery', {
        method: 'POST', headers: authHeaders()
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setMsg('Recovery email sent to ' + email + ' ✅');
      await load();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(function(b) { var n = Object.assign({}, b); delete n[email]; return n; }); }
  }

  async function handleDelete(email) {
    if (!window.confirm('Delete the abandoned cart for ' + email + '? They won\'t receive a recovery email.')) return;
    try {
      var r = await fetch(BACKEND + '/api/admin/abandoned-carts/' + encodeURIComponent(email), {
        method: 'DELETE', headers: authHeaders()
      });
      if (!r.ok) throw new Error('Failed');
      setMsg('Deleted ' + email);
      await load();
    } catch (e) { setMsg('Error: ' + e.message); }
  }

  function ageStr(iso) {
    if (!iso) return '—';
    var ms = Date.now() - new Date(iso).getTime();
    var mins = Math.floor(ms / 60000);
    if (mins < 60) return mins + 'm ago';
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    return Math.floor(hrs / 24) + 'd ago';
  }

  return React.createElement('div', null,
    React.createElement('div', { className: 'flex items-center gap-3 mb-4 flex-wrap' },
      React.createElement('div', { className: 'flex gap-2' },
        ['open', 'recovered', 'all'].map(function(m) {
          return React.createElement('button', {
            key: m, onClick: function() { setFilterMode(m); },
            'data-testid': 'abandoned-filter-' + m,
            className: 'px-3 py-1.5 rounded-lg text-sm font-medium ' + (filterMode === m ? 'bg-[#d4af37] text-black' : 'bg-neutral-900 text-neutral-400 hover:text-white')
          }, m === 'open' ? 'Open (no recovery sent)' : m === 'recovered' ? 'Recovery sent' : 'All');
        })
      ),
      React.createElement('span', { className: 'text-sm text-neutral-500' }, total + ' total · ' + carts.length + ' shown'),
      React.createElement('button', { onClick: load, className: 'ml-auto text-neutral-400 hover:text-white p-2', 'data-testid': 'abandoned-refresh-btn' },
        React.createElement(RefreshCw, { className: 'w-4 h-4' + (loading ? ' animate-spin' : '') })
      )
    ),
    msg ? React.createElement('div', { className: 'mb-3 px-3 py-2 rounded-lg bg-neutral-900 border border-white/10 text-sm text-neutral-300' }, msg) : null,
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl overflow-hidden' },
      React.createElement('table', { className: 'w-full text-sm', 'data-testid': 'abandoned-carts-table' },
        React.createElement('thead', { className: 'bg-neutral-800 text-neutral-400 text-xs uppercase tracking-wider' },
          React.createElement('tr', null,
            React.createElement('th', { className: 'text-left p-3' }, 'Customer'),
            React.createElement('th', { className: 'text-left p-3' }, 'Items'),
            React.createElement('th', { className: 'text-right p-3' }, 'Subtotal'),
            React.createElement('th', { className: 'text-left p-3' }, 'Lang'),
            React.createElement('th', { className: 'text-left p-3' }, 'Updated'),
            React.createElement('th', { className: 'text-left p-3' }, 'Recovery'),
            React.createElement('th', { className: 'text-right p-3' }, 'Actions')
          )
        ),
        React.createElement('tbody', null,
          carts.length === 0
            ? React.createElement('tr', null, React.createElement('td', { colSpan: 7, className: 'p-8 text-center text-neutral-600' }, loading ? 'Loading...' : 'No abandoned carts in this view'))
            : carts.map(function(c) {
                var lineCount = (c.items || []).length;
                var sampleNames = (c.items || []).slice(0, 3).map(function(i) { return (i.name || i.slug) + (i.quantity > 1 ? ' ×' + i.quantity : ''); }).join(', ');
                if (lineCount > 3) sampleNames += ' …+' + (lineCount - 3);
                return React.createElement('tr', { key: c.email, className: 'border-t border-white/5', 'data-testid': 'abandoned-row-' + c.email },
                  React.createElement('td', { className: 'p-3 font-mono text-xs' }, c.email),
                  React.createElement('td', { className: 'p-3 text-neutral-300 max-w-md truncate', title: sampleNames }, sampleNames || '—'),
                  React.createElement('td', { className: 'p-3 text-right text-white font-semibold' }, '$' + Number(c.subtotal || 0).toFixed(2)),
                  React.createElement('td', { className: 'p-3 text-neutral-500' }, (c.language || 'en').toUpperCase()),
                  React.createElement('td', { className: 'p-3 text-neutral-500' }, ageStr(c.updated_at)),
                  React.createElement('td', { className: 'p-3' }, c.recovery_sent
                    ? React.createElement('span', { className: 'text-emerald-400 text-xs' }, '✓ sent ' + ageStr(c.recovery_sent_at))
                    : c.converted
                      ? React.createElement('span', { className: 'text-blue-400 text-xs' }, '✓ converted')
                      : React.createElement('span', { className: 'text-amber-400 text-xs' }, 'pending')
                  ),
                  React.createElement('td', { className: 'p-3 text-right' },
                    React.createElement('div', { className: 'flex gap-2 justify-end' },
                      c.resume_url ? React.createElement('a', {
                        href: c.resume_url, target: '_blank', rel: 'noopener noreferrer',
                        className: 'text-xs px-2 py-1 rounded-md bg-neutral-800 hover:bg-neutral-700 text-neutral-300',
                        'data-testid': 'abandoned-view-' + c.email
                      }, '↗ View cart') : null,
                      c.recovery_sent ? null : React.createElement('button', {
                        onClick: function() { handleSendRecovery(c.email); },
                        disabled: !!busy[c.email],
                        'data-testid': 'abandoned-send-recovery-' + c.email,
                        className: 'text-xs px-2 py-1 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50'
                      }, busy[c.email] ? 'Sending…' : '✉️ Send recovery'),
                      React.createElement('button', {
                        onClick: function() { handleDelete(c.email); },
                        'data-testid': 'abandoned-delete-' + c.email,
                        className: 'text-xs px-2 py-1 rounded-md bg-neutral-800 hover:bg-red-700 text-neutral-400 hover:text-white'
                      }, '🗑')
                    )
                  )
                );
              })
        )
      )
    )
  );
}

export default AdminDashboardPage;

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// SampleInviteCard — one-off invite for past customers who never bought an
// indexed book: free half of Real Book Vol. 1, in the key they actually read.
// ---------------------------------------------------------------------------
function SampleInviteCard() {
  var [data, setData] = useState(null);
  var [busy, setBusy] = useState('');
  var [msg, setMsg] = useState(null);
  var [showList, setShowList] = useState(false);

  async function load() {
    try {
      var r = await fetch(BACKEND + '/api/admin/sample-invites/status', { headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Could not load the audience');
      setData(d);
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
  }
  useEffect(function() { load(); }, []);

  async function run(mode) {
    if (mode === 'send' && !window.confirm('Email the next ' + (data ? data.batch_size : 25) +
        ' past customer(s) their free sample links? This cannot be undone.')) return;
    setBusy(mode); setMsg(null);
    try {
      var qs = mode === 'test' ? '?test_to=' + encodeURIComponent(localStorage.getItem('admin_email') || 'trumpet95@gmail.com')
                               : '?limit=' + (data ? data.batch_size : 25);
      var r = await fetch(BACKEND + '/api/admin/sample-invites/send' + qs, { method: 'POST', headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Send failed');
      if (d.test_to) setMsg({ type: 'success', text: 'Test copies (' + (d.variants_sent || []).join(', ') + ') sent to ' + d.test_to });
      else if (d.skipped === 'preview_env') setMsg({ type: 'error', text: 'Skipped — real sends only run on the live site (' + d.remaining + ' waiting there).' });
      else setMsg({ type: 'success', text: 'Sent ' + d.sent + (d.failed ? ', ' + d.failed + ' failed' : '') + ' — ' + d.remaining + ' still to go.' });
      await load();
    } catch (e) { setMsg({ type: 'error', text: e.message }); }
    finally { setBusy(''); }
  }

  var keyLabels = { bb: 'B\u266d', eb: 'E\u266d', c: 'C', bass: 'Bass clef' };
  function prettyKeys(combo) {
    return combo.split('+').map(function(k) { return keyLabels[k] || k; }).join(' + ');
  }
  var combos = data ? Object.keys(data.by_keys || {}) : [];
  var preview = (data && data.preview) || [];

  return React.createElement('div', { className: 'bg-neutral-900 border border-[#d4af37]/30 rounded-xl p-5', 'data-testid': 'sample-invite-card' },
    React.createElement('h3', { className: 'font-semibold text-white mb-1 flex items-center gap-2' },
      React.createElement(Send, { className: 'w-5 h-5 text-[#d4af37]' }), 'Free sample invitation'),
    React.createElement('p', { className: 'text-neutral-500 text-sm mb-4' },
      'Past customers who never bought an indexed book. Each one gets the sample in the key they actually read \u2014 a trumpet NoteTurner gets B\u266d, a sax one gets E\u266d + B\u266d, a trombone one gets bass clef + C. Everyone else gets all four and picks. Sent in their own language, once per customer.'),
    data ? React.createElement('div', null,
      React.createElement('div', { className: 'flex flex-wrap items-center gap-3 mb-4' },
        React.createElement('span', { className: 'text-white text-2xl font-bold', 'data-testid': 'sample-invite-remaining' }, data.remaining),
        React.createElement('span', { className: 'text-neutral-400 text-sm' }, 'waiting'),
        data.already_sent ? React.createElement('span', { className: 'text-neutral-600 text-xs' }, '\u00b7 ' + data.already_sent + ' already sent') : null,
        React.createElement('button', { onClick: function() { setShowList(!showList); }, 'data-testid': 'sample-invite-toggle-list',
          className: 'ml-auto text-xs px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300' },
          showList ? '\u25be Hide who gets it' : '\u25b8 Who gets it')
      ),
      React.createElement('div', { className: 'flex flex-wrap gap-2 mb-4' },
        combos.map(function(combo) {
          return React.createElement('span', { key: combo, className: 'text-xs text-neutral-300 bg-neutral-800 border border-white/10 rounded-full px-3 py-1.5' },
            prettyKeys(combo) + ' \u00b7 ' + data.by_keys[combo]);
        })
      ),
      showList ? React.createElement('div', { className: 'max-h-56 overflow-y-auto rounded-lg bg-neutral-950 border border-white/5 p-3 text-xs font-mono mb-4' },
        preview.map(function(r) {
          return React.createElement('div', { key: r.email, className: 'py-1 flex justify-between gap-3' },
            React.createElement('span', { className: 'text-neutral-200 truncate' }, r.email),
            React.createElement('span', { className: 'text-neutral-500 shrink-0' }, prettyKeys(r.keys.join('+')) + ' \u00b7 ' + r.language));
        })
      ) : null,
      React.createElement('div', { className: 'flex flex-wrap gap-2' },
        React.createElement('button', { onClick: function() { run('test'); }, disabled: !!busy, 'data-testid': 'sample-invite-test',
          className: 'text-sm px-4 py-2 rounded-full border border-white/15 text-neutral-200 hover:border-white/40 disabled:opacity-40' },
          busy === 'test' ? 'Sending\u2026' : 'Send 3 test copies to me'),
        React.createElement('button', { onClick: function() { run('send'); }, disabled: !!busy || !data.remaining, 'data-testid': 'sample-invite-send',
          className: 'text-sm px-5 py-2 rounded-full bg-[#d4af37] text-black font-semibold hover:bg-white disabled:opacity-40' },
          busy === 'send' ? 'Sending\u2026' : 'Send next ' + data.batch_size)
      )
    ) : React.createElement('p', { className: 'text-neutral-500 text-sm' }, 'Loading\u2026'),
    msg ? React.createElement('div', { 'data-testid': 'sample-invite-msg',
      className: 'mt-4 px-4 py-2 rounded-lg text-sm ' + (msg.type === 'success'
        ? 'bg-green-500/10 border border-green-500/30 text-green-300'
        : 'bg-red-500/10 border border-red-500/30 text-red-300') },
      (msg.type === 'success' ? '\u2705 ' : '\u26a0\ufe0f ') + msg.text) : null
  );
}

// ---------------------------------------------------------------------------
// PromotionsPanel — segment customers by past purchases and send a BCC'd
// broadcast (new product launch, holiday discount, etc.). Lists past
// broadcasts at the bottom for history.
// ---------------------------------------------------------------------------
function PromotionsPanel() {
  var [segments, setSegments] = useState([]);
  var [segment, setSegment] = useState('trumpet');
  var [customers, setCustomers] = useState([]);
  var [showCustomers, setShowCustomers] = useState(false);
  var [subject, setSubject] = useState('');
  var [greeting, setGreeting] = useState('Hello');
  var [bodyText, setBodyText] = useState('');
  var [productSlug, setProductSlug] = useState('');
  var [footerNote, setFooterNote] = useState('');
  var [history, setHistory] = useState([]);
  var [busy, setBusy] = useState(false);
  var [msg, setMsg] = useState('');

  async function loadSegments() {
    try {
      var r = await fetch(BACKEND + '/api/admin/customer-segments', { headers: authHeaders() });
      var d = await r.json();
      setSegments(d.segments || []);
    } catch (e) { setMsg('Error: ' + e.message); }
  }
  async function loadCustomers(seg) {
    try {
      var r = await fetch(BACKEND + '/api/admin/customer-segments/' + encodeURIComponent(seg) + '/customers?limit=500', { headers: authHeaders() });
      var d = await r.json();
      setCustomers(d.customers || []);
    } catch (e) { setMsg('Error: ' + e.message); }
  }
  async function loadHistory() {
    try {
      var r = await fetch(BACKEND + '/api/admin/broadcasts?limit=20', { headers: authHeaders() });
      var d = await r.json();
      setHistory(d.broadcasts || []);
    } catch (e) {}
  }
  useEffect(function() { loadSegments(); loadHistory(); }, []);
  useEffect(function() { if (segment) loadCustomers(segment); }, [segment]);

  // Convert simple newline-separated paragraphs into <p> wrapped HTML
  function paragraphify(text) {
    return text.split(/\n\n+/).map(function(p) {
      return '<p>' + p.trim().replace(/\n/g, '<br>') + '</p>';
    }).join('');
  }

  async function handleSend(testOnly) {
    if (!subject.trim() || !bodyText.trim()) { setMsg('Subject and body are required'); return; }
    var segMeta = segments.find(function(s) { return s.segment === segment; });
    var count = segMeta ? segMeta.customer_count : 0;
    var confirmMsg = testOnly
      ? 'Send a TEST copy to your admin inbox only (no customers will receive this)?'
      : 'Send to ' + count + ' ' + segment + ' customer(s) via BCC. This cannot be undone.\\n\\nProceed?';
    if (!window.confirm(confirmMsg)) return;
    setBusy(true); setMsg('');
    try {
      var resp = await fetch(BACKEND + '/api/admin/broadcasts/send', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({
          segment: segment, subject: subject, greeting: greeting,
          body_html: paragraphify(bodyText),
          product_slug: productSlug.trim() || null,
          footer_note: footerNote.trim() || null,
          test_only: !!testOnly,
        }),
      });
      var data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Send failed');
      setMsg('✅ Sent ' + data.sent + ' of ' + data.recipients_resolved + ' (in ' + data.batches + ' batch' + (data.batches === 1 ? '' : 'es') + ')' + (data.failed > 0 ? ' — ' + data.failed + ' failed' : ''));
      await loadHistory();
    } catch (e) { setMsg('Error: ' + e.message); }
    finally { setBusy(false); }
  }

  var currentSeg = segments.find(function(s) { return s.segment === segment; }) || { customer_count: 0, label: segment };

  return React.createElement('div', { className: 'space-y-6' },
    React.createElement(SampleInviteCard, null),
    // Segment picker + customer preview
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-5' },
      React.createElement('h3', { className: 'font-semibold text-white mb-4 flex items-center gap-2' }, React.createElement(Mail, { className: 'w-5 h-5 text-[#d4af37]' }), 'Customer segment'),
      React.createElement('div', { className: 'flex flex-wrap items-center gap-3 mb-3' },
        React.createElement('select', {
          value: segment, onChange: function(e) { setSegment(e.target.value); },
          'data-testid': 'promo-segment-select',
          className: 'bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm flex-1 min-w-[200px]'
        },
          segments.map(function(s) {
            return React.createElement('option', { key: s.segment, value: s.segment }, s.label + ' — ' + s.customer_count + ' customer' + (s.customer_count === 1 ? '' : 's'));
          })
        ),
        React.createElement('button', { onClick: function() { setShowCustomers(!showCustomers); }, className: 'text-xs px-3 py-2 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-300', 'data-testid': 'promo-toggle-customers-btn' },
          showCustomers ? '▾ Hide list' : '▸ Show ' + currentSeg.customer_count + ' customer' + (currentSeg.customer_count === 1 ? '' : 's')
        )
      ),
      showCustomers ? React.createElement('div', { className: 'max-h-48 overflow-y-auto rounded-lg bg-neutral-950 border border-white/5 p-3 text-xs font-mono' },
        customers.length === 0 ? React.createElement('span', { className: 'text-neutral-500' }, 'No customers in this segment.')
          : customers.map(function(c) {
              return React.createElement('div', { key: c.email, className: 'py-1 text-neutral-400 flex justify-between' },
                React.createElement('span', { className: 'text-neutral-200' }, c.email),
                React.createElement('span', null, (c.name || '') + ' · ' + c.order_count + ' order(s)')
              );
            })
      ) : null
    ),
    // Compose
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-5 space-y-3' },
      React.createElement('h3', { className: 'font-semibold text-white mb-2' }, 'Compose your message'),
      React.createElement('div', null,
        React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-1' }, 'Subject'),
        React.createElement('input', { value: subject, onChange: function(e) { setSubject(e.target.value); }, placeholder: '🎺 Father\u2019s Day — 25% off any trumpet accessory', className: 'w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm', 'data-testid': 'promo-subject' })
      ),
      React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-3' },
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-1' }, 'Greeting'),
          React.createElement('input', { value: greeting, onChange: function(e) { setGreeting(e.target.value); }, placeholder: 'Hello', className: 'w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm', 'data-testid': 'promo-greeting' })
        ),
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-1' }, 'Product to feature (optional slug)'),
          React.createElement('input', { value: productSlug, onChange: function(e) { setProductSlug(e.target.value); }, placeholder: 'noteturner', className: 'w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm', 'data-testid': 'promo-product' })
        )
      ),
      React.createElement('div', null,
        React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-1' }, 'Body (plain paragraphs separated by blank lines)'),
        React.createElement('textarea', { value: bodyText, onChange: function(e) { setBodyText(e.target.value); }, rows: 7, placeholder: 'A short personal opener — why you\u2019re writing, what\u2019s new, the benefit.\n\nThen a second paragraph with the discount code and dates, or the new-product story.', className: 'w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono', 'data-testid': 'promo-body' })
      ),
      React.createElement('div', null,
        React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-1' }, 'Footer note (optional fine-print)'),
        React.createElement('input', { value: footerNote, onChange: function(e) { setFooterNote(e.target.value); }, placeholder: 'Code DADS25 auto-applies at checkout. One use per customer.', className: 'w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm', 'data-testid': 'promo-footer' })
      ),
      msg ? React.createElement('div', { className: 'px-3 py-2 rounded-lg bg-neutral-800 border border-white/10 text-sm text-neutral-200', 'data-testid': 'promo-status' }, msg) : null,
      React.createElement('div', { className: 'flex gap-3 pt-2' },
        React.createElement('button', { onClick: function() { handleSend(true); }, disabled: busy, 'data-testid': 'promo-send-test-btn', className: 'flex items-center gap-2 bg-neutral-800 hover:bg-neutral-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50 text-sm' },
          React.createElement(Eye, { className: 'w-4 h-4' }), 'Send test to admin only'
        ),
        React.createElement('button', { onClick: function() { handleSend(false); }, disabled: busy || currentSeg.customer_count === 0, 'data-testid': 'promo-send-broadcast-btn', className: 'flex items-center gap-2 bg-[#d4af37] hover:bg-[#e8c44a] text-black font-bold px-5 py-2 rounded-lg disabled:opacity-50 text-sm' },
          React.createElement(Send, { className: 'w-4 h-4' }), busy ? 'Sending…' : 'Send to ' + currentSeg.customer_count + ' customer' + (currentSeg.customer_count === 1 ? '' : 's')
        )
      )
    ),
    // History
    React.createElement('div', { className: 'bg-neutral-900 border border-white/10 rounded-xl p-5' },
      React.createElement('h3', { className: 'font-semibold text-white mb-3' }, 'Recent broadcasts'),
      history.length === 0 ? React.createElement('p', { className: 'text-neutral-500 text-sm' }, 'No broadcasts sent yet.')
        : React.createElement('div', { className: 'space-y-2' },
            history.map(function(b, i) {
              return React.createElement('div', { key: i, className: 'flex items-center gap-3 text-sm border-t border-white/5 pt-2' },
                React.createElement('span', { className: 'text-xs text-neutral-500 w-32' }, new Date(b.created_at).toLocaleString()),
                React.createElement('span', { className: 'px-2 py-0.5 rounded bg-neutral-800 text-xs text-neutral-300' }, b.segment),
                React.createElement('span', { className: 'flex-1 text-white truncate' }, b.subject),
                React.createElement('span', { className: 'text-xs text-emerald-400' }, b.sent_count + '/' + b.total_recipients + (b.test_only ? ' (test)' : ''))
              );
            })
          )
    )
  );
}



// ---------------------------------------------------------------------------
// ExportPanel — download filtered customer emails or full sales as CSV.
// Filters:
//   • Type      — emails (unique customer list) or sales (one row per order)
//   • Segment   — Trumpet / Sax / Digital / Physical / All
//   • Language  — customer preferred language, or All
// ---------------------------------------------------------------------------
// Select field for ExportPanel — kept OUT of the parent to avoid React's
// "component re-created on every render" warning.
function _ExportSelect(props) {
  return React.createElement('select', {
    value: props.value,
    onChange: function(e) { props.onChange(e.target.value); },
    'data-testid': 'export-select-' + props.name,
    className: 'w-full px-4 py-2.5 rounded-lg bg-neutral-950 border border-neutral-700 text-white focus:border-[#d4af37] focus:outline-none'
  }, props.options.map(function(opt) {
    return React.createElement('option', { key: opt.value, value: opt.value }, opt.label);
  }));
}

function ExportPanel() {
  var API = process.env.REACT_APP_BACKEND_URL;
  var [kind, setKind] = useState('emails');
  var [segment, setSegment] = useState('all');
  var [language, setLanguage] = useState('all');
  var [busy, setBusy] = useState(false);
  var [lastCount, setLastCount] = useState(null);
  var [err, setErr] = useState('');

  async function downloadCsv() {
    setBusy(true); setErr(''); setLastCount(null);
    try {
      var url = API + '/api/admin/export.csv?kind=' + encodeURIComponent(kind)
        + '&segment=' + encodeURIComponent(segment)
        + '&language=' + encodeURIComponent(language);
      var res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) {
        var msg = 'HTTP ' + res.status;
        try { var j = await res.json(); if (j && j.detail) msg = j.detail; } catch (parseErr) { void parseErr; }
        throw new Error(msg);
      }
      var count = res.headers.get('X-Row-Count');
      if (count) setLastCount(parseInt(count, 10));
      var blob = await res.blob();
      var link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'jimendez-' + kind + '-' + segment + '-' + language + '.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  return React.createElement('div', { 'data-testid': 'export-panel', className: 'max-w-3xl' },
    React.createElement('div', { className: 'bg-neutral-900 border border-neutral-800 rounded-xl p-8' },
      React.createElement('h2', { className: 'text-2xl font-bold text-white mb-2' }, 'Export customer data'),
      React.createElement('p', { className: 'text-neutral-400 text-sm mb-8' },
        'Download a CSV of customer emails or full sales rows. Filter by segment (instrument or physical/digital) and preferred language.'),

      React.createElement('div', { className: 'grid md:grid-cols-3 gap-5 mb-8' },
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-2' }, 'Export type'),
          React.createElement(_ExportSelect, { name: 'kind', value: kind, onChange: setKind, options: [
            { value: 'emails', label: 'Emails (unique customer list)' },
            { value: 'sales',  label: 'Sales (one row per order)' }
          ]})
        ),
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-2' }, 'Segment'),
          React.createElement(_ExportSelect, { name: 'segment', value: segment, onChange: setSegment, options: [
            { value: 'all',       label: 'All customers' },
            { value: 'trumpet',   label: '🎺 Trumpet players' },
            { value: 'saxophone', label: '🎷 Saxophone players' },
            { value: 'digital',   label: '💾 Digital files (any order with digital)' },
            { value: 'physical',  label: '📦 Physical products (any order with physical)' }
          ]})
        ),
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-2' }, 'Language'),
          React.createElement(_ExportSelect, { name: 'language', value: language, onChange: setLanguage, options: [
            { value: 'all', label: 'All languages' },
            { value: 'en',  label: 'English' },
            { value: 'es',  label: 'Español' },
            { value: 'ja',  label: '日本語' },
            { value: 'de',  label: 'Deutsch' },
            { value: 'fr',  label: 'Français' },
            { value: 'it',  label: 'Italiano' },
            { value: 'uk',  label: 'Українська' }
          ]})
        )
      ),

      React.createElement('button', {
        onClick: downloadCsv,
        disabled: busy,
        'data-testid': 'export-download-btn',
        className: 'inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#d4af37] hover:bg-[#e8c458] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition'
      },
        busy ? React.createElement(Loader2, { className: 'w-5 h-5 animate-spin' }) : React.createElement(Download, { className: 'w-5 h-5' }),
        busy ? 'Preparing CSV…' : 'Download CSV'
      ),

      lastCount !== null ? React.createElement('div', { 'data-testid': 'export-result-count', className: 'mt-6 text-sm text-emerald-400' },
        '✓ Exported ' + lastCount + ' matching ' + (kind === 'emails' ? 'customer' : 'order') + (lastCount === 1 ? '' : 's') + '.'
      ) : null,

      err ? React.createElement('div', { 'data-testid': 'export-error', className: 'mt-6 text-sm text-red-400' }, 'Error: ' + err) : null,

      React.createElement('div', { className: 'mt-8 pt-6 border-t border-neutral-800 text-xs text-neutral-500 leading-relaxed' },
        React.createElement('div', { className: 'mb-1' }, React.createElement('strong', null, 'Segments explained:')),
        React.createElement('div', null, '• Trumpet / Saxophone — derived from what each customer bought (uses the same rules as the Promotions tab).'),
        React.createElement('div', null, '• Digital — any order that contained at least one digital file.'),
        React.createElement('div', null, '• Physical — any order that contained at least one physical product.'),
        React.createElement('div', { className: 'mt-2' }, 'Cancelled and refunded orders are automatically excluded.')
      )
    )
  );
}

export { ExportPanel };

function SubscribersPanel() {
  var API = process.env.REACT_APP_BACKEND_URL;
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState('');
  var [status, setStatus] = useState('active');
  var [language, setLanguage] = useState('all');

  var load = useCallback(async function() {
    setLoading(true); setErr('');
    try {
      var res = await fetch(API + '/api/newsletter/subscribers', { headers: authHeaders() });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      setData(await res.json());
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(function() { load(); }, [load]);

  async function downloadCsv() {
    try {
      var url = API + '/api/newsletter/subscribers.csv?status=' + encodeURIComponent(status)
        + '&language=' + encodeURIComponent(language);
      var res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var blob = await res.blob();
      var link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'jimendez-newsletter-' + status + '-' + language + '.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (e) {
      setErr(e.message || String(e));
    }
  }

  async function removeSubscriber(email) {
    if (!window.confirm('Delete ' + email + ' from the newsletter list?')) return;
    try {
      var res = await fetch(API + '/api/newsletter/subscribers/' + encodeURIComponent(email), {
        method: 'DELETE', headers: authHeaders()
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      load();
    } catch (e) {
      setErr(e.message || String(e));
    }
  }

  var rows = (data && data.subscribers) || [];

  return React.createElement('div', { 'data-testid': 'subscribers-panel', className: 'max-w-5xl' },
    React.createElement('div', { className: 'bg-neutral-900 border border-neutral-800 rounded-xl p-8' },
      React.createElement('div', { className: 'flex items-start justify-between gap-4 mb-6' },
        React.createElement('div', null,
          React.createElement('h2', { className: 'text-2xl font-bold text-white mb-1' }, 'Newsletter subscribers'),
          React.createElement('p', { className: 'text-neutral-400 text-sm' }, 'Collected from the site signup popup. Each new subscriber gets an instant welcome email in their language.')
        ),
        React.createElement('button', { onClick: load, className: 'text-neutral-400 hover:text-white p-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' + (loading ? ' animate-spin' : '') })
        )
      ),

      React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6' },
        React.createElement('div', { 'data-testid': 'subscribers-active-count', className: 'bg-neutral-950 border border-white/10 rounded-xl p-3' },
          React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Active subscribers'),
          React.createElement('div', { className: 'text-[#d4af37] text-xl font-bold mt-1' }, data ? data.active : '—')
        ),
        React.createElement('div', { className: 'bg-neutral-950 border border-white/10 rounded-xl p-3' },
          React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider' }, 'Total ever'),
          React.createElement('div', { className: 'text-white text-xl font-bold mt-1' }, data ? data.total : '—')
        ),
        React.createElement('div', { className: 'bg-neutral-950 border border-white/10 rounded-xl p-3 col-span-2' },
          React.createElement('div', { className: 'text-neutral-500 text-[10px] uppercase tracking-wider mb-1' }, 'By language'),
          React.createElement('div', { className: 'text-white text-sm font-semibold' },
            data && data.by_language && Object.keys(data.by_language).length
              ? Object.keys(data.by_language).map(function(k) { return k.toUpperCase() + ' ' + data.by_language[k]; }).join(' · ')
              : '—'
          )
        )
      ),

      React.createElement('div', { className: 'flex flex-wrap items-end gap-3 mb-6' },
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-2' }, 'Status'),
          React.createElement(_ExportSelect, { name: 'sub-status', value: status, onChange: setStatus, options: [
            { value: 'active', label: 'Active only' },
            { value: 'unsubscribed', label: 'Unsubscribed only' },
            { value: 'all', label: 'All' }
          ]})
        ),
        React.createElement('div', null,
          React.createElement('label', { className: 'block text-xs uppercase tracking-wider text-neutral-500 mb-2' }, 'Language'),
          React.createElement(_ExportSelect, { name: 'sub-language', value: language, onChange: setLanguage, options: [
            { value: 'all', label: 'All languages' },
            { value: 'en', label: 'English' },
            { value: 'es', label: 'Español' },
            { value: 'ja', label: '日本語' },
            { value: 'de', label: 'Deutsch' },
            { value: 'fr', label: 'Français' },
            { value: 'it', label: 'Italiano' },
            { value: 'uk', label: 'Українська' }
          ]})
        ),
        React.createElement('button', {
          onClick: downloadCsv,
          'data-testid': 'subscribers-export-btn',
          className: 'inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#d4af37] hover:bg-[#e8c458] text-black font-semibold transition'
        },
          React.createElement(Download, { className: 'w-5 h-5' }), 'Download CSV'
        )
      ),

      err ? React.createElement('div', { 'data-testid': 'subscribers-error', className: 'mb-4 text-sm text-red-400' }, 'Error: ' + err) : null,

      rows.length === 0 && !loading
        ? React.createElement('p', { 'data-testid': 'subscribers-empty', className: 'text-neutral-500 text-sm' }, 'No subscribers yet.')
        : React.createElement('div', { className: 'overflow-x-auto' },
          React.createElement('table', { className: 'w-full text-sm' },
            React.createElement('thead', null,
              React.createElement('tr', { className: 'text-neutral-500 text-xs uppercase tracking-wider text-left' },
                React.createElement('th', { className: 'py-2 pr-4' }, 'Email'),
                React.createElement('th', { className: 'py-2 pr-4' }, 'Lang'),
                React.createElement('th', { className: 'py-2 pr-4' }, 'Source'),
                React.createElement('th', { className: 'py-2 pr-4' }, 'Joined'),
                React.createElement('th', { className: 'py-2 pr-4' }, 'Welcome'),
                React.createElement('th', { className: 'py-2' }, '')
              )
            ),
            React.createElement('tbody', null,
              rows.map(function(s) {
                return React.createElement('tr', { key: s.email, className: 'border-t border-white/5' },
                  React.createElement('td', { className: 'py-2 pr-4 text-white' }, s.email,
                    s.unsubscribed ? React.createElement('span', { className: 'ml-2 text-[10px] uppercase text-red-400' }, 'unsubscribed') : null
                  ),
                  React.createElement('td', { className: 'py-2 pr-4 text-neutral-400' }, (s.language || 'en').toUpperCase()),
                  React.createElement('td', { className: 'py-2 pr-4 text-neutral-500' }, s.source || '—'),
                  React.createElement('td', { className: 'py-2 pr-4 text-neutral-500' }, (s.created_at || '').slice(0, 10)),
                  React.createElement('td', { className: 'py-2 pr-4' }, s.welcome_email_sent ? '✓' : '—'),
                  React.createElement('td', { className: 'py-2 text-right' },
                    React.createElement('button', {
                      onClick: function() { removeSubscriber(s.email); },
                      'data-testid': 'subscriber-delete-' + s.email,
                      className: 'text-neutral-600 hover:text-red-400 text-xs'
                    }, 'Delete')
                  )
                );
              })
            )
          )
        )
    )
  );
}

export { SubscribersPanel };

var FORMAT_ORDER = ['forscore', 'mobilesheets', 'pdf'];
var KEY_ORDER = ['c', 'bb', 'eb', 'bass', 'none'];
var FORMAT_LABEL = { forscore: 'forScore (.4sc)', mobilesheets: 'MobileSheets (.msf)', pdf: 'PDF / other' };
var KEY_LABEL = { c: 'Key of C', bb: 'Bb', eb: 'Eb', bass: 'Bass clef', none: 'No key' };

function digitalFormatOf(p) {
  // The linked file's extension is the truth; fall back to the product name.
  var path = (p.dropbox_path || '').toLowerCase();
  if (/\.4sc(\.zip)?$/.test(path)) return 'forscore';
  if (/\.msf(\.zip)?$/.test(path)) return 'mobilesheets';
  if (/\.pdf$/.test(path)) return 'pdf';
  var blob = ((p.name || '') + ' ' + (p.slug || '') + ' ' + (p.platform || '')).toLowerCase();
  if (blob.indexOf('mobilesheet') !== -1) return 'mobilesheets';
  if (blob.indexOf('forscore') !== -1 || blob.indexOf('4sc') !== -1) return 'forscore';
  return 'pdf';
}

function digitalKeyOf(p) {
  var override = (p.bookKey || '').toLowerCase();
  if (override) return override;
  var blob = ((p.name || '') + ' ' + (p.slug || '')).toLowerCase().replace(/[^a-z0-9]+/g, ' ');
  if (blob.indexOf('bass') !== -1) return 'bass';
  if (/\bbb\b|\bb flat\b/.test(blob)) return 'bb';
  if (/\beb\b|\be flat\b/.test(blob)) return 'eb';
  if (/\bin c\b|\bkey of c\b|\bc\b/.test(blob)) return 'c';
  // Catalog rule: no key in the title means the book is in C — except the
  // Rafael Méndez / Clifford Brown / Wynton Marsalis collections.
  if (/mendez|m ndez|clifford|wynton|marsalis/.test(blob)) return 'none';
  return 'c';
}

// Free sample slots: the two generic ones on the shop, then the per-key
// samples NoteTurner buyers are emailed (their instrument decides the key).
var SAMPLE_SLOTS = [
  ['forscore', 'forScore — generic sample (.4sc)'],
  ['mobilesheets', 'MobileSheets — generic sample (.msf)'],
  ['forscore:bb', 'forScore — B\u266d (trumpet, tenor sax, clarinet)'],
  ['mobilesheets:bb', 'MobileSheets — B\u266d (trumpet, tenor sax, clarinet)'],
  ['forscore:eb', 'forScore — E\u266d (alto sax)'],
  ['mobilesheets:eb', 'MobileSheets — E\u266d (alto sax)'],
  ['forscore:c', 'forScore — C / concert pitch'],
  ['mobilesheets:c', 'MobileSheets — C / concert pitch'],
  ['forscore:bass', 'forScore — Bass clef (trombone)'],
  ['mobilesheets:bass', 'MobileSheets — Bass clef (trombone)']
];

function DropboxFilesPanel() {  // Maps each digital product to a private Dropbox file. Credentials live only
  // in backend env vars — this UI never sees a token, just paths.
  var API = process.env.REACT_APP_BACKEND_URL;
  var [status, setStatus] = useState(null);
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [err, setErr] = useState('');
  var [savingSlug, setSavingSlug] = useState('');
  var [drafts, setDrafts] = useState({});
  var [code, setCode] = useState('');
  var [appKey, setAppKey] = useState('');
  var [appSecret, setAppSecret] = useState('');
  var [credMsg, setCredMsg] = useState('');
  var [scanPath, setScanPath] = useState('');
  var [scan, setScan] = useState(null);
  var [scanning, setScanning] = useState(false);
  var [picked, setPicked] = useState({});
  var [applyMsg, setApplyMsg] = useState('');
  var [browser, setBrowser] = useState(null); // { slug, path, entries, loading }
  var [filter, setFilter] = useState({ view: 'all', query: '', format: 'all', key: 'all' });
  var [editing, setEditing] = useState({});
  var [backfill, setBackfill] = useState({ loading: false, msg: '', data: null });
  var [samples, setSamples] = useState({});
  var [sampleDrafts, setSampleDrafts] = useState({});
  var [sampleBusy, setSampleBusy] = useState('');
  var [visBusy, setVisBusy] = useState('');

  async function showInShop(slug) {
    setVisBusy(slug); setErr('');
    try {
      var r = await fetch(API + '/api/admin/product-visibility/' + encodeURIComponent(slug), {
        method: 'PUT',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ hidden: false }),
      });
      // Never swallow this: a silent 401 made the toggle look like it did nothing.
      if (!r.ok) {
        var d = await r.json().catch(function() { return {}; });
        throw new Error(r.status === 401
          ? 'Your admin session expired — log out and log in again, then try once more.'
          : (d.detail || 'Could not change visibility (HTTP ' + r.status + ')'));
      }
      await load();
    } catch (e) {
      setErr(e.message || String(e));
    } finally { setVisBusy(''); }
  }

  var load = useCallback(async function() {
    setLoading(true); setErr('');
    try {
      var s = await fetch(API + '/api/admin/dropbox/status', { headers: authHeaders() });
      setStatus(await s.json());
      var r = await fetch(API + '/api/admin/dropbox/product-files', { headers: authHeaders() });
      if (!r.ok) throw new Error('HTTP ' + r.status);
      setData(await r.json());
      var sm = await fetch(API + '/api/admin/dropbox/samples', { headers: authHeaders() });
      if (sm.ok) setSamples(((await sm.json()) || {}).samples || {});
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(function() { load(); }, [load]);

  async function savePath(slug, path) {
    setSavingSlug(slug); setErr('');
    try {
      var r = await fetch(API + '/api/admin/dropbox/product-files/' + encodeURIComponent(slug), {
        method: 'PUT',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ dropbox_path: path || null }),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      await load();
      setBrowser(null);
      setEditing(function(prev) { var n = Object.assign({}, prev); delete n[slug]; return n; });
      setDrafts(function(prev) { var n = Object.assign({}, prev); delete n[slug]; return n; });
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setSavingSlug('');
    }
  }

  async function saveCredentials() {
    setCredMsg(''); setErr('');
    try {
      var r = await fetch(API + '/api/admin/dropbox/credentials', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ app_key: appKey.trim(), app_secret: appSecret.trim() }),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setAppKey(''); setAppSecret('');
      setCredMsg('Saved. Now click "Open Dropbox consent page" below.');
      await load();
    } catch (e) {
      setErr(e.message || String(e));
    }
  }

  async function exchangeCode() {
    setErr(''); setCredMsg('');
    try {
      var r = await fetch(API + '/api/admin/dropbox/exchange-code', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ code: code.trim() }),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setCode('');
      setCredMsg(d.connected === false
        ? ('Saved, but the connection test failed: ' + (d.error || 'unknown'))
        : ('Connected as ' + (d.account_name || d.email || 'your Dropbox') + ' 🎉'));
      await load();
    } catch (e) {
      setErr(e.message || String(e));
    }
  }

  async function runScan() {
    setScanning(true); setErr(''); setScan(null); setApplyMsg('');
    try {
      var r = await fetch(API + '/api/admin/dropbox/auto-match?path=' + encodeURIComponent(scanPath || ''), { headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setScan(d);
      var pre = {};
      (d.suggestions || []).forEach(function(s) {
        if (s.dropbox_path && s.confidence >= 70) pre[s.slug] = true;
      });
      setPicked(pre);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setScanning(false);
    }
  }

  async function applyPicked() {
    var mappings = (scan.suggestions || [])
      .filter(function(s) { return picked[s.slug] && s.dropbox_path; })
      .map(function(s) { return { slug: s.slug, dropbox_path: s.dropbox_path }; });
    if (mappings.length === 0) { setApplyMsg('Nothing selected.'); return; }
    setApplyMsg('Linking ' + mappings.length + ' files…');
    try {
      var r = await fetch(API + '/api/admin/dropbox/auto-match/apply', {
        method: 'POST',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ mappings: mappings }),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setApplyMsg('Linked ' + d.applied + ' products 🎉');
      await load();
    } catch (e) {
      setApplyMsg('Error: ' + e.message);
    }
  }

  async function backfillTokens(dryRun) {
    setBackfill({ loading: true, msg: dryRun ? 'Checking past orders…' : 'Creating download links…', data: null });
    try {
      var r = await fetch(API + '/api/admin/orders/backfill-download-tokens?dry_run=' + (dryRun ? 'true' : 'false'), {
        method: 'POST', headers: authHeaders(),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      var msg = dryRun
        ? (d.orders_needing_tokens + ' past order(s) are missing download links (' + d.tokens_missing + ' file(s)). Scanned ' + d.orders_scanned + ' paid orders.')
        : ('Done ✅ Created ' + d.tokens_created + ' download link(s) across ' + d.orders_needing_tokens + ' order(s). Customers can now self-serve at /my-downloads.');
      setBackfill({ loading: false, msg: msg, data: d });
    } catch (e) {
      setBackfill({ loading: false, msg: 'Error: ' + e.message, data: null });
    }
  }

  async function loadSamples() {
    try {
      var r = await fetch(API + '/api/admin/dropbox/samples', { headers: authHeaders() });
      var d = await r.json();
      if (r.ok) setSamples(d.samples || {});
    } catch (e) { /* non-fatal */ }
  }

  async function saveSample(platform, path) {
    setSampleBusy(platform);
    try {
      var r = await fetch(API + '/api/admin/dropbox/product-files/' + encodeURIComponent('sample:' + platform), {
        method: 'PUT',
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
        body: JSON.stringify({ dropbox_path: path || null }),
      });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      await loadSamples();
      setBrowser(null);
      setSampleDrafts(function(prev) { var n = Object.assign({}, prev); delete n[platform]; return n; });
    } catch (e) { setErr(e.message); }
    finally { setSampleBusy(''); }
  }

  async function openBrowser(slug, path) {    setBrowser({ slug: slug, path: path || '', entries: [], loading: true });
    try {
      var r = await fetch(API + '/api/admin/dropbox/browse?path=' + encodeURIComponent(path || ''), { headers: authHeaders() });
      var d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      setBrowser({ slug: slug, path: d.path || '', entries: d.entries || [], loading: false });
    } catch (e) {
      setBrowser({ slug: slug, path: path || '', entries: [], loading: false, error: e.message });
    }
  }

  var allRows = (data && data.products) || [];
  var connected = status && status.configured && status.connected !== false;

  var q = filter.query.trim().toLowerCase();
  var withMeta = allRows.map(function(p) {
    return Object.assign({}, p, { _format: digitalFormatOf(p), _key: digitalKeyOf(p) });
  });

  function countBy(field, value) {
    return withMeta.filter(function(p) { return p[field] === value; }).length;
  }

  var rows = withMeta
    .filter(function(p) {
      if (filter.view === 'linked' && !p.dropbox_path) return false;
      if (filter.view === 'unlinked' && p.dropbox_path) return false;
      if (filter.format !== 'all' && p._format !== filter.format) return false;
      if (filter.key !== 'all' && p._key !== filter.key) return false;
      if (!q) return true;
      return ((p.name || '') + ' ' + (p.slug || '') + ' ' + (p.dropbox_path || '')).toLowerCase().indexOf(q) !== -1;
    })
    .slice()
    .sort(function(a, b) {
      var fa = FORMAT_ORDER.indexOf(a._format), fb = FORMAT_ORDER.indexOf(b._format);
      if (fa !== fb) return fa - fb;
      var ka = KEY_ORDER.indexOf(a._key), kb = KEY_ORDER.indexOf(b._key);
      if (ka !== kb) return ka - kb;
      return (a.name || '').toLowerCase() < (b.name || '').toLowerCase() ? -1 : 1;
    });

  function chip(field, value, label, testid) {
    return React.createElement('button', {
      key: field + value,
      onClick: function() {
        setFilter(function(prev) { var n = Object.assign({}, prev); n[field] = value; return n; });
      },
      'data-testid': testid,
      className: 'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ' +
        (filter[field] === value ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-300 hover:text-white')
    }, label);
  }

  function filterChip(view, label) {
    return chip('view', view, label, 'dropbox-filter-' + view);
  }

  return React.createElement('div', { 'data-testid': 'dropbox-files-panel', className: 'max-w-5xl space-y-4' },
    // Connection card
    React.createElement('div', { className: 'bg-neutral-900 border border-neutral-800 rounded-xl p-6' },
      React.createElement('div', { className: 'flex items-start justify-between gap-4 mb-3' },
        React.createElement('div', null,
          React.createElement('h2', { className: 'text-xl font-bold text-white mb-1' }, 'Dropbox connection'),
          React.createElement('p', { className: 'text-neutral-400 text-sm' },
            connected
              ? ('Connected'
                 + ((status && (status.account_name || status.email)) ? ' as ' + (status.account_name || status.email) : ' to your app folder Dropbox/Apps/JiMendez')
                 + '. Customer downloads use fresh temporary links — no public share links are ever created.')
              : 'Two quick steps below and your files are connected. Credentials are stored on the server only — they never reach a browser.'
          )
        ),
        React.createElement('span', {
          'data-testid': 'dropbox-status-badge',
          className: 'px-3 py-1 rounded-full text-xs font-semibold ' + (connected ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300')
        }, connected ? 'Connected' : 'Not connected')
      ),
      !connected ? React.createElement('div', { className: 'mt-4 space-y-3' },
        React.createElement('div', { className: 'text-xs text-neutral-500' },
          'Server env vars: ',
          ['DROPBOX_APP_KEY', 'DROPBOX_APP_SECRET', 'DROPBOX_REFRESH_TOKEN'].map(function(v) {
            var present = status && (
              (v === 'DROPBOX_APP_KEY' && status.has_app_key) ||
              (v === 'DROPBOX_APP_SECRET' && status.has_app_secret) ||
              (v === 'DROPBOX_REFRESH_TOKEN' && status.has_refresh_token)
            );
            return React.createElement('span', { key: v, className: present ? 'text-emerald-400 mr-3' : 'text-red-400 mr-3' },
              (present ? '✓ ' : '✗ ') + v);
          })
        ),
        // Step 1 — app key/secret, saved straight into backend/.env.
        (!(status && status.has_app_key && status.has_app_secret)) ? React.createElement('div', { className: 'bg-neutral-950 border border-white/10 rounded-lg p-4 space-y-3' },
          React.createElement('p', { className: 'text-neutral-300 text-sm font-semibold' }, 'Step 1 — paste your Dropbox app key & secret'),
          React.createElement('p', { className: 'text-neutral-500 text-xs' },
            'From dropbox.com/developers/apps → your app → Settings. Enable the permissions files.content.read and files.metadata.read first. These are stored on the server only.'),
          React.createElement('div', { className: 'flex flex-wrap gap-2' },
            React.createElement('input', {
              value: appKey, onChange: function(e) { setAppKey(e.target.value); },
              placeholder: 'App key', 'data-testid': 'dropbox-appkey-input',
              className: 'flex-1 min-w-[180px] bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm'
            }),
            React.createElement('input', {
              value: appSecret, onChange: function(e) { setAppSecret(e.target.value); },
              type: 'password', placeholder: 'App secret', 'data-testid': 'dropbox-appsecret-input',
              className: 'flex-1 min-w-[180px] bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm'
            }),
            React.createElement('button', {
              onClick: saveCredentials, 'data-testid': 'dropbox-save-credentials-btn',
              className: 'bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg text-sm'
            }, 'Save')
          )
        ) : null,
        // Step 2 — one-time consent code → refresh token (auto-saved).
        (status && status.has_app_key && status.has_app_secret) ? React.createElement('div', { className: 'bg-neutral-950 border border-white/10 rounded-lg p-4 space-y-3' },
          React.createElement('p', { className: 'text-neutral-300 text-sm font-semibold' }, 'Step 2 — authorize your Dropbox account'),
          React.createElement('a', {
            href: status.authorize_url, target: '_blank', rel: 'noopener noreferrer',
            'data-testid': 'dropbox-authorize-link',
            className: 'inline-block bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg text-sm'
          }, 'Open Dropbox consent page →'),
          React.createElement('p', { className: 'text-neutral-500 text-xs' }, 'Approve access, copy the one-time code Dropbox shows, paste it here. I save the long-lived token on the server for you.'),
          React.createElement('div', { className: 'flex gap-2' },
            React.createElement('input', {
              value: code, onChange: function(e) { setCode(e.target.value); },
              placeholder: 'Paste the authorization code',
              'data-testid': 'dropbox-code-input',
              className: 'flex-1 bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-white text-sm'
            }),
            React.createElement('button', {
              onClick: exchangeCode, 'data-testid': 'dropbox-exchange-btn',
              className: 'bg-white text-black font-semibold px-4 py-2 rounded-lg text-sm'
            }, 'Connect')
          )
        ) : null,
        credMsg ? React.createElement('p', { 'data-testid': 'dropbox-cred-msg', className: 'text-emerald-400 text-xs' }, credMsg) : null
      ) : null,
      React.createElement('button', { onClick: load, className: 'text-neutral-400 hover:text-white text-xs mt-4' }, '↻ Re-check connection')
    ),

    // Auto-match card
    connected ? React.createElement('div', { className: 'bg-neutral-900 border border-[#d4af37]/30 rounded-xl p-6', 'data-testid': 'dropbox-automatch-card' },
      React.createElement('h2', { className: 'text-xl font-bold text-white mb-1' }, '⚡ Auto-link all files'),
      React.createElement('p', { className: 'text-neutral-400 text-sm mb-4' },
        'I scan a Dropbox folder (including sub-folders) and match each file to the right product by name, volume and key. Extensions are a hard rule: .4sc files only ever go to forScore products, .msf files only to MobileSheets products. Nothing is saved until you press Apply.'),
      React.createElement('div', { className: 'flex flex-wrap gap-2 mb-4' },
        React.createElement('input', {
          value: scanPath, onChange: function(e) { setScanPath(e.target.value); },
          placeholder: 'Folder to scan — leave empty for everything',
          'data-testid': 'dropbox-scan-path-input',
          className: 'flex-1 min-w-[240px] bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-white text-xs font-mono'
        }),
        React.createElement('button', {
          onClick: runScan, disabled: scanning,
          'data-testid': 'dropbox-scan-btn',
          className: 'bg-[#d4af37] text-black font-semibold px-4 py-2 rounded-lg text-sm disabled:opacity-50'
        }, scanning ? 'Scanning…' : 'Scan & suggest matches')
      ),
      scan ? React.createElement('div', null,
        React.createElement('div', { className: 'flex flex-wrap items-center gap-4 mb-3 text-sm' },
          React.createElement('span', { className: 'text-neutral-400' }, scan.files_scanned + ' files scanned'),
          React.createElement('span', { className: 'text-emerald-400' }, scan.matched + ' matched'),
          React.createElement('span', { className: 'text-amber-400' }, scan.unmatched + ' need attention'),
          React.createElement('button', {
            onClick: applyPicked, 'data-testid': 'dropbox-apply-btn',
            className: 'ml-auto bg-white text-black font-semibold px-4 py-2 rounded-lg text-sm'
          }, 'Apply selected')
        ),
        applyMsg ? React.createElement('p', { 'data-testid': 'dropbox-apply-msg', className: 'text-xs mb-3 ' + (applyMsg.indexOf('Error') === 0 ? 'text-red-400' : 'text-emerald-400') }, applyMsg) : null,
        React.createElement('div', { className: 'space-y-1 max-h-[420px] overflow-y-auto' },
          (scan.suggestions || []).map(function(s) {
            return React.createElement('label', {
              key: s.slug,
              className: 'flex items-center gap-3 px-3 py-2 rounded-lg ' + (s.dropbox_path ? 'hover:bg-white/5 cursor-pointer' : 'opacity-60')
            },
              React.createElement('input', {
                type: 'checkbox',
                checked: !!picked[s.slug],
                disabled: !s.dropbox_path,
                onChange: function(e) {
                  var v = e.target.checked;
                  setPicked(function(prev) { var n = Object.assign({}, prev); n[s.slug] = v; return n; });
                },
                'data-testid': 'dropbox-suggestion-' + s.slug,
                className: 'w-4 h-4 accent-[#d4af37]'
              }),
              React.createElement('span', {
                className: 'text-xs font-bold w-10 flex-shrink-0 ' + (s.confidence >= 85 ? 'text-emerald-400' : s.confidence >= 70 ? 'text-[#d4af37]' : s.confidence > 0 ? 'text-amber-400' : 'text-neutral-600')
              }, s.confidence ? s.confidence + '%' : '—'),
              React.createElement('span', { className: 'text-white text-sm truncate flex-1' }, s.name),
              React.createElement('span', { className: 'text-neutral-500 text-xs font-mono truncate flex-1 hidden sm:block' },
                s.dropbox_path || 'no match found')
            );
          })
        )
      ) : null
    ) : null,

    // Free sample files shown on the shop / forScore / MobileSheets pages.
    React.createElement('div', { className: 'bg-neutral-900 border border-neutral-800 rounded-xl p-6' },
      React.createElement('h2', { className: 'text-xl font-bold text-white mb-1' }, '🎁 Free sample files'),
      React.createElement('p', { className: 'text-neutral-400 text-sm mb-4' },
        'These are the free "try a sample" downloads. The first two show on the shop and the forScore / MobileSheets pages. The per-key ones are emailed to NoteTurner buyers with their order confirmation — their instrument picks the key (trumpet \u2192 B\u266d, alto sax \u2192 E\u266d, trombone \u2192 bass clef + C, guitar \u2192 C). Leave a slot empty and that button quietly falls back to the generic sample.'),
      React.createElement('div', { className: 'space-y-3' },
        SAMPLE_SLOTS.map(function(pair) {
          var platform = pair[0];
          var current = samples[platform] || {};
          var draft = sampleDrafts[platform] !== undefined ? sampleDrafts[platform] : (current.dropbox_path || '');
          return React.createElement('div', {
            key: platform,
            'data-testid': 'sample-row-' + platform,
            className: 'border rounded-lg p-3 ' + (current.dropbox_path ? 'border-emerald-500/20 bg-emerald-500/[0.03]' : 'border-white/5')
          },
            React.createElement('div', { className: 'flex items-center justify-between gap-3 mb-2' },
              React.createElement('div', { className: 'text-white text-sm font-semibold' }, pair[1]),
              React.createElement('span', {
                className: 'text-xs px-2 py-1 rounded-full ' + (current.dropbox_path ? 'bg-emerald-500/15 text-emerald-300' : 'bg-neutral-800 text-neutral-500')
              }, current.dropbox_path ? 'Live' : 'Not set')
            ),
            current.dropbox_path ? React.createElement('div', { 'data-testid': 'sample-filename-' + platform, className: 'text-emerald-200 text-xs font-mono break-all mb-2' },
              (current.is_folder ? '📁 ' : '📄 ') + current.dropbox_path) : null,
            React.createElement('div', { className: 'flex flex-wrap gap-2' },
              React.createElement('input', {
                value: draft,
                onChange: function(e) {
                  var v = e.target.value;
                  setSampleDrafts(function(prev) { var n = Object.assign({}, prev); n[platform] = v; return n; });
                },
                placeholder: '/Samples/Test sample Real Book Vol 1 in Bb.4sc',
                'data-testid': 'sample-path-input-' + platform,
                className: 'flex-1 min-w-[220px] bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-white text-xs font-mono'
              }),
              React.createElement('button', {
                onClick: function() { openBrowser('sample:' + platform, ''); },
                disabled: !connected,
                'data-testid': 'sample-browse-btn-' + platform,
                className: 'px-3 py-2 rounded-lg bg-neutral-800 text-neutral-200 text-xs font-semibold disabled:opacity-40'
              }, 'Browse…'),
              React.createElement('button', {
                onClick: function() { saveSample(platform, draft); },
                disabled: sampleBusy === platform,
                'data-testid': 'sample-save-btn-' + platform,
                className: 'px-3 py-2 rounded-lg bg-[#d4af37] text-black text-xs font-semibold disabled:opacity-50'
              }, sampleBusy === platform ? 'Saving…' : 'Save'),
              current.dropbox_path ? React.createElement('a', {
                href: API + '/api/download/sample/' + platform.replace(':', '/'),
                target: '_blank', rel: 'noopener noreferrer',
                'data-testid': 'sample-test-link-' + platform,
                className: 'px-3 py-2 rounded-lg bg-neutral-800 text-neutral-200 text-xs font-semibold'
              }, 'Test download') : null,
              current.dropbox_path ? React.createElement('button', {
                onClick: function() { saveSample(platform, ''); },
                'data-testid': 'sample-clear-btn-' + platform,
                className: 'px-3 py-2 rounded-lg bg-red-500/15 text-red-300 text-xs font-semibold'
              }, 'Remove') : null
            )
          );
        })
      )
    ),

    // Past-order backfill: gives old digital orders the download links they
    // never got, so customers can self-serve at /my-downloads.
    React.createElement('div', { className: 'bg-neutral-900 border border-neutral-800 rounded-xl p-6' },
      React.createElement('h2', { className: 'text-xl font-bold text-white mb-1' }, 'Past orders — download links'),
      React.createElement('p', { className: 'text-neutral-400 text-sm mb-4' },
        'Older digital orders (and any order you edited by hand) may have no download link on file, which means /my-downloads finds nothing for that customer. Check and fix them here. No emails are sent.'),
      React.createElement('div', { className: 'flex flex-wrap gap-2' },
        React.createElement('button', {
          onClick: function() { backfillTokens(true); }, disabled: backfill.loading,
          'data-testid': 'backfill-check-btn',
          className: 'px-4 py-2 rounded-lg bg-neutral-800 text-neutral-100 text-sm font-semibold disabled:opacity-50'
        }, 'Check past orders'),
        React.createElement('button', {
          onClick: function() {
            if (window.confirm('Create the missing download links for all past digital orders? No emails are sent.')) backfillTokens(false);
          },
          disabled: backfill.loading,
          'data-testid': 'backfill-apply-btn',
          className: 'px-4 py-2 rounded-lg bg-[#d4af37] text-black text-sm font-semibold disabled:opacity-50'
        }, 'Create missing links')
      ),
      backfill.msg ? React.createElement('p', { 'data-testid': 'backfill-msg', className: 'text-sm text-neutral-300 mt-3' }, backfill.msg) : null,
      backfill.data && backfill.data.details && backfill.data.details.length ? React.createElement('div', {
        className: 'mt-3 max-h-48 overflow-y-auto text-xs text-neutral-500 space-y-1'
      }, backfill.data.details.map(function(d) {
        return React.createElement('div', { key: d.order_number },
          React.createElement('span', { className: 'text-neutral-300 font-mono' }, d.order_number),
          ' · ', d.email || 'no email', ' · ', (d.files || []).join(', '));
      })) : null
    ),

    // Mapping table
    React.createElement('div', { className: 'bg-neutral-900 border border-neutral-800 rounded-xl p-6' },
      React.createElement('div', { className: 'flex items-center justify-between mb-4' },
        React.createElement('div', null,
          React.createElement('h2', { className: 'text-xl font-bold text-white mb-1' }, 'Product → file mapping'),
          React.createElement('p', { className: 'text-neutral-400 text-sm' },
            data ? (data.mapped + ' of ' + data.total + ' digital products have a Dropbox file') : 'Loading…')
        ),
        React.createElement('button', { onClick: load, className: 'text-neutral-400 hover:text-white p-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' + (loading ? ' animate-spin' : '') })
        )
      ),
      React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-4' },
        filterChip('all', 'All' + (data ? ' (' + data.total + ')' : '')),
        filterChip('linked', 'Linked' + (data ? ' (' + data.mapped + ')' : '')),
        filterChip('unlinked', 'Not linked' + (data ? ' (' + (data.total - data.mapped) + ')' : '')),
        React.createElement('input', {
          value: filter.query,
          onChange: function(e) { var v = e.target.value; setFilter(function(prev) { var n = Object.assign({}, prev); n.query = v; return n; }); },
          placeholder: 'Search product or file…',
          'data-testid': 'dropbox-search-input',
          className: 'ml-auto w-full sm:w-64 bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-white text-xs'
        })
      ),
      React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-2' },
        React.createElement('span', { className: 'text-neutral-500 text-[11px] uppercase tracking-wider w-16' }, 'Format'),
        chip('format', 'all', 'All', 'dropbox-format-all'),
        FORMAT_ORDER.map(function(f) {
          return chip('format', f, FORMAT_LABEL[f] + ' (' + countBy('_format', f) + ')', 'dropbox-format-' + f);
        })
      ),
      React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-4' },
        React.createElement('span', { className: 'text-neutral-500 text-[11px] uppercase tracking-wider w-16' }, 'Key'),
        chip('key', 'all', 'All', 'dropbox-key-all'),
        KEY_ORDER.map(function(k) {
          return chip('key', k, KEY_LABEL[k] + ' (' + countBy('_key', k) + ')', 'dropbox-key-' + k);
        })
      ),
      err ? React.createElement('div', { 'data-testid': 'dropbox-error', className: 'mb-4 text-sm text-red-400' }, 'Error: ' + err) : null,
      React.createElement('div', { className: 'space-y-2 max-h-[600px] overflow-y-auto' },
        rows.length === 0 ? React.createElement('p', { className: 'text-neutral-500 text-sm py-6 text-center' }, 'No products match this filter.') : null,
        rows.map(function(p, idx) {
          var draft = drafts[p.slug] !== undefined ? drafts[p.slug] : (p.dropbox_path || '');
          var isEditing = !p.dropbox_path || editing[p.slug];
          var fileName = p.filename || (p.dropbox_path ? p.dropbox_path.split('/').pop() : '');
          var folder = p.dropbox_path ? p.dropbox_path.split('/').slice(0, -1).join('/') : '';
          var prevRow = rows[idx - 1];
          var newGroup = !prevRow || prevRow._format !== p._format || prevRow._key !== p._key;
          var header = newGroup ? React.createElement('div', {
            'data-testid': 'dropbox-group-' + p._format + '-' + p._key,
            className: 'flex items-center gap-2 pt-4 pb-1 sticky top-0 bg-neutral-900 z-10'
          },
            React.createElement('span', { className: 'text-[#d4af37] text-xs font-bold uppercase tracking-wider' }, FORMAT_LABEL[p._format]),
            React.createElement('span', { className: 'text-neutral-600 text-xs' }, '·'),
            React.createElement('span', { className: 'text-neutral-300 text-xs font-semibold' }, KEY_LABEL[p._key])
          ) : null;
          var row = React.createElement('div', {
            'data-testid': 'dropbox-row-' + p.slug,
            className: 'border rounded-lg p-3 ' + (p.dropbox_path ? 'border-emerald-500/20 bg-emerald-500/[0.03]' : 'border-white/5')
          },
            React.createElement('div', { className: 'flex items-start justify-between gap-3 mb-2' },
              React.createElement('div', { className: 'min-w-0' },
                React.createElement('div', { className: 'text-white text-sm font-semibold truncate' }, p.name),
                React.createElement('div', { className: 'text-neutral-600 text-xs' }, p.slug, p.sold_count ? ' · ' + p.sold_count + ' sold' : '')
              ),
              React.createElement('span', {
                'data-testid': 'dropbox-status-' + p.slug,
                className: 'flex-shrink-0 text-xs px-2 py-1 rounded-full ' + (p.dropbox_path ? 'bg-emerald-500/15 text-emerald-300' : 'bg-neutral-800 text-neutral-500')
              }, p.dropbox_path ? 'Linked' : 'No file'),
              p.hidden ? React.createElement('button', {
                onClick: function() { showInShop(p.slug); },
                disabled: !p.dropbox_path || visBusy === p.slug,
                title: p.dropbox_path ? 'Make this product visible in the shop' : 'Link a file first',
                'data-testid': 'dropbox-show-in-shop-' + p.slug,
                className: 'flex-shrink-0 text-xs px-2 py-1 rounded-full font-semibold disabled:opacity-40 ' + (p.dropbox_path ? 'bg-[#d4af37] text-black' : 'bg-neutral-800 text-neutral-500')
              }, visBusy === p.slug ? '…' : 'Hidden — show in shop') : null
            ),
            p.dropbox_path ? React.createElement('div', { className: 'flex flex-wrap items-center gap-2 mb-2' },
              React.createElement('span', { className: 'text-emerald-300 text-xs' }, p.is_folder ? '📁' : '📄'),
              React.createElement('div', { className: 'min-w-0 flex-1' },
                React.createElement('div', { 'data-testid': 'dropbox-filename-' + p.slug, className: 'text-emerald-200 text-xs font-mono break-all' },
                  fileName, p.is_folder ? React.createElement('span', { className: 'ml-2 text-[10px] uppercase tracking-wider text-[#d4af37]' }, 'whole folder → .zip') : null),
                folder ? React.createElement('div', { className: 'text-neutral-600 text-[11px] font-mono break-all' }, folder) : null
              ),
              !isEditing ? React.createElement('button', {
                onClick: function() { setEditing(function(prev) { var n = Object.assign({}, prev); n[p.slug] = true; return n; }); },
                'data-testid': 'dropbox-change-btn-' + p.slug,
                className: 'px-3 py-1.5 rounded-lg bg-neutral-800 text-neutral-200 text-xs font-semibold'
              }, 'Change') : null,
              !isEditing ? React.createElement('button', {
                onClick: function() {
                  setDrafts(function(prev) { var n = Object.assign({}, prev); n[p.slug] = ''; return n; });
                  savePath(p.slug, '');
                },
                disabled: savingSlug === p.slug,
                'data-testid': 'dropbox-unlink-btn-' + p.slug,
                className: 'px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 text-xs font-semibold disabled:opacity-50'
              }, savingSlug === p.slug ? '…' : 'Unlink') : null
            ) : null,
            isEditing ? React.createElement('div', { className: 'flex flex-wrap gap-2' },
              React.createElement('input', {
                value: draft,
                onChange: function(e) {
                  var v = e.target.value;
                  setDrafts(function(prev) { var n = Object.assign({}, prev); n[p.slug] = v; return n; });
                },
                placeholder: '/Real Books/Real Book Vol 1 in Bb.4sc',
                'data-testid': 'dropbox-path-input-' + p.slug,
                className: 'flex-1 min-w-[220px] bg-neutral-950 border border-white/10 rounded-lg px-3 py-2 text-white text-xs font-mono'
              }),
              React.createElement('button', {
                onClick: function() { openBrowser(p.slug, ''); },
                disabled: !connected,
                'data-testid': 'dropbox-browse-btn-' + p.slug,
                className: 'px-3 py-2 rounded-lg bg-neutral-800 text-neutral-200 text-xs font-semibold disabled:opacity-40'
              }, 'Browse…'),
              React.createElement('button', {
                onClick: function() { savePath(p.slug, draft); },
                disabled: savingSlug === p.slug,
                'data-testid': 'dropbox-save-btn-' + p.slug,
                className: 'px-3 py-2 rounded-lg bg-[#d4af37] text-black text-xs font-semibold disabled:opacity-50'
              }, savingSlug === p.slug ? 'Saving…' : 'Save'),
              p.dropbox_path ? React.createElement('button', {
                onClick: function() {
                  setEditing(function(prev) { var n = Object.assign({}, prev); delete n[p.slug]; return n; });
                  setDrafts(function(prev) { var n = Object.assign({}, prev); delete n[p.slug]; return n; });
                },
                'data-testid': 'dropbox-cancel-btn-' + p.slug,
                className: 'px-3 py-2 rounded-lg text-neutral-400 text-xs font-semibold hover:text-white'
              }, 'Cancel') : null
            ) : null
          );
          return React.createElement(React.Fragment, { key: p.slug }, header, row);
        })
      )
    ),

    // File browser modal
    browser ? React.createElement('div', {
      className: 'fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4',
      onClick: function() { setBrowser(null); }
    },
      React.createElement('div', {
        onClick: function(e) { e.stopPropagation(); },
        'data-testid': 'dropbox-browser-modal',
        className: 'bg-neutral-950 border border-white/10 rounded-2xl w-full max-w-lg max-h-[70vh] overflow-hidden flex flex-col'
      },
        React.createElement('div', { className: 'p-4 border-b border-white/10 flex items-center justify-between' },
          React.createElement('div', null,
            React.createElement('div', { className: 'text-white text-sm font-semibold' }, 'Pick a file for ' + browser.slug),
            React.createElement('div', { className: 'text-neutral-500 text-xs font-mono' }, browser.path || '/'),
            React.createElement('div', { className: 'text-neutral-500 text-[11px] mt-1' }, 'Tip: for stamp collections use "Link this whole folder" — it is zipped automatically on download.')
          ),
          React.createElement('button', { onClick: function() { setBrowser(null); }, className: 'text-neutral-500 hover:text-white' }, '✕')
        ),
        browser.path ? React.createElement('div', { className: 'px-4 py-3 border-b border-white/10' },
          React.createElement('button', {
            onClick: function() { savePath(browser.slug, browser.path); },
            'data-testid': 'dropbox-use-folder-btn',
            className: 'w-full px-3 py-2 rounded-lg bg-[#d4af37] text-black text-xs font-semibold'
          }, '📁 Link this whole folder (' + browser.path.split('/').pop() + ')')
        ) : null,
        React.createElement('div', { className: 'p-2 overflow-y-auto' },
          browser.loading ? React.createElement('p', { className: 'text-neutral-500 text-sm p-4' }, 'Loading…')
            : browser.error ? React.createElement('p', { className: 'text-red-400 text-sm p-4' }, browser.error)
            : React.createElement('div', null,
              browser.path ? React.createElement('button', {
                onClick: function() { openBrowser(browser.slug, browser.path.split('/').slice(0, -1).join('/')); },
                className: 'w-full text-left px-3 py-2 text-neutral-400 hover:bg-white/5 rounded text-sm'
              }, '⬆ up one folder') : null,
              browser.entries.length === 0 ? React.createElement('p', { className: 'text-neutral-500 text-sm p-4' }, 'Empty folder') : null,
              browser.entries.map(function(e) {
                return React.createElement('button', {
                  key: e.path,
                  onClick: function() {
                    if (e.type === 'folder') openBrowser(browser.slug, e.path);
                    else savePath(browser.slug, e.path);
                  },
                  'data-testid': 'dropbox-entry-' + e.name,
                  className: 'w-full text-left px-3 py-2 hover:bg-white/5 rounded text-sm flex items-center gap-2 ' + (e.type === 'folder' ? 'text-white' : 'text-[#d4af37]')
                },
                  React.createElement('span', null, e.type === 'folder' ? '📁' : '📄'),
                  React.createElement('span', { className: 'truncate' }, e.name)
                );
              })
            )
        )
      )
    ) : null
  );
}

export { DropboxFilesPanel };
